import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as PrinterBridge from './utils/printerBridge';
import { PlateConfig, PlateType, FlagType, InitialDimensions, Dimensions, User, BulkItem, BulkColor, SavedTemplate, PrinterProfile, PrintSettings, TextEffect, PlateSize, PlateDimensions, RNPSRecord, LicenseInfo, LipTextItem, OKI_PAPER_SIZES, mmToDecipoints } from './types';
import { PlateView } from './components/PlateView';
import { exportPlateToPDF, generatePlatePDFBlob, capturePlateForPDF, buildMultiPagePDF, registerCustomFont } from './utils/pdfExport';
import { toPng } from 'html-to-image';
import { FontSelect } from './components/FontSelect';
import { LicenseManager, DEFAULT_SCRIPT_URL } from './utils/licenseManager';
import { Logo } from './components/Logo';
import { AndroidPrinterSelect } from './components/AndroidPrinterSelect';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthService, AuthSession } from './services/authService';
import { LoginScreen } from './components/LoginScreen';
import { RegistrationScreen } from './components/RegistrationScreen';
import { TrialActivationScreen } from './components/TrialActivationScreen';
import { OfflineVerificationModal as OfflineVerificationModalEnhanced } from './components/OfflineVerificationModal';
import { UserSettings } from './services/authService';
import { isDemoMode, getDemoLicense } from './utils/demoMode';
import { 
    Download, Settings2, Type, Flag, Loader2, Move, Maximize, KeyRound,
  AlignEndHorizontal, AlignJustify, Printer, EyeOff, Eye, LogOut, Lock, 
  PoundSterling, Info, X, Check, Key, Wifi, Save, FolderOpen, 
  Plus, Minus, Trash2, Upload, FileText, List, RotateCw, AlertTriangle, Settings, CheckCircle, CheckSquare, Square,
  Image as ImageIcon, ZoomIn, ZoomOut, RefreshCw, Zap, Moon, Sun, Unlock, Layers, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Wand2,
  Shield, Smartphone, Monitor, ShieldCheck, AlignLeft, AlignCenter, AlignRight, Crop, Edit2, Code, QrCode, Timer, Diamond, AlertOctagon, Search,
  Usb, Bluetooth as BluetoothIcon, Camera, Folder, History, Activity
} from 'lucide-react';

import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, remove } from "firebase/database";
import JSZip from 'jszip';
import QRCode from 'qrcode';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

// App version for version enforcement
// This will be replaced at build time with the actual version from package.json
// eslint-disable-next-line @typescript-eslint/naming-convention
const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

// --- FIREBASE CONFIG (For Phone Uploads) ---
const firebaseConfig = {
  apiKey: "AIzaSyApx9_wVN_94SRkfCgzBgCQjpmGcCbOitM",
  authDomain: "plateiq-pipe.firebaseapp.com",
  databaseURL: "https://plateiq-pipe-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "plateiq-pipe",
  storageBucket: "plateiq-pipe.firebasestorage.app",
  messagingSenderId: "591797854540",
  appId: "1:591797854540:web:54c164f069a9318cfeefe9"
};

let database: any = null;
try {
    const app = initializeApp(firebaseConfig);
    database = getDatabase(app);
} catch(e) { console.error("Firebase init failed", e); }

const SHOPIFY_UPLOAD_URL = "https://www.meenalite.com/pages/upload-test"; 

const SYSTEM_FONTS = [
  { name: 'Charles Wright Radial (Default)', value: 'Charles Wright Radial', isPro: false },
  { name: 'Charles Wright', value: 'Charles Wright', isPro: false },
  { name: 'Charles Moto', value: 'Charles Moto', isPro: false },
  { name: 'Charles Moto Radial', value: 'Charles Moto Radial', isPro: false },
  
  // PRO FONTS
  { name: 'Euro (VehicleJNL)', value: 'Euro', isPro: true },
  { name: 'Mandatory', value: 'Mandatory', isPro: true },
  { name: 'Metro Radial', value: 'Metro Radial', isPro: true },
  { name: 'Orbitron', value: 'Orbitron', isPro: true },
  { name: 'Arial', value: 'Arial', isPro: true },
  { name: 'DIN Next', value: 'DIN Next', isPro: true },
  { name: 'Retro', value: 'Retro', isPro: true },
  { name: 'Retro Skinny', value: 'RetroSkinny', isPro: true }
];

// BASIC Tier Allowed Fonts
const BASIC_ALLOWED_FONTS = ['Charles Wright', 'Charles Wright Radial', 'Charles Moto', 'Charles Moto Radial'];

const FONT_DEFAULTS: Record<string, { vOffset?: number, fontSize?: number }> = {
  'Charles Wright': { vOffset: -5 },
  'Charles Moto': { vOffset: 2.5 },
  'Euro': { vOffset: -2 },
  'Mandatory': { vOffset: -9 },
  'Metro Radial': { vOffset: -3.5, fontSize: 80 },
  'Orbitron': { vOffset: -3.5, fontSize: 80 },
  'Arial': { vOffset: 3.5 },
  'Retro': { vOffset: -5.5 },
  'RetroSkinny': { vOffset: -3.5 },
  'Charles Moto Radial': { vOffset: 2 }
};

const Toast = ({ message, onClose }: { message: string, onClose: () => void }) => (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] bg-slate-800 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4 animate-in fade-in slide-in-from-top-4 border border-slate-700">
        <span className="text-sm font-semibold">{message}</span>
        <button onClick={onClose}><X className="w-4 h-4 opacity-50 hover:opacity-100"/></button>
    </div>
);

const ProBadge = () => (
    <span className="text-blue-400 ml-1.5 align-middle" title="Professional Feature">
        <Diamond className="w-3 h-3 inline fill-blue-400/20" />
    </span>
);

const PrintingOverlay = ({ progress }: { progress: number }) => (
    <div className="fixed bottom-6 right-6 z-[9999] w-80 bg-white border border-slate-200 shadow-2xl rounded-xl p-4 flex flex-col gap-3 animate-in slide-in-from-bottom-10 fade-in print-hidden">
        <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div>
                <h4 className="font-bold text-sm text-slate-800">Printing...</h4>
                <p className="text-xs text-slate-500">Sending data to printer</p>
            </div>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-300 ease-out" style={{ width: `${progress}%` }}></div>
        </div>
    </div>
);

// Compact print queue indicator - will be defined inside App component

const ProFeatureModal = ({ errors, onClose }: { errors: string[], onClose: () => void }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/50 p-4 print-hidden">
        <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-6 text-white relative animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-center w-12 h-12 bg-red-900/30 rounded-full mx-auto mb-4 border border-red-500/50">
                <Lock className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2">License Restriction</h3>
            <p className="text-slate-400 text-center text-sm mb-6">
                The following features require a Professional License:
            </p>
            
            <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800 mb-6">
                <ul className="space-y-2">
                    {errors.map((err, i) => (
                        <li key={i} className="text-sm font-semibold text-red-400 flex items-center gap-2">
                            <AlertOctagon className="w-4 h-4" /> {err}
                        </li>
                    ))}
                </ul>
            </div>

            <a 
                href="https://regiq.co.uk" 
                target="_blank" 
                rel="noreferrer"
                className="block w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg text-center mb-3 transition-colors"
            >
                Upgrade at RegIQ.co.uk
            </a>
            
            <button 
                onClick={onClose}
                className="block w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-lg transition-colors"
            >
                Go Back & Adjust
            </button>
        </div>
    </div>
);

const SignOutModal = ({ onClose, onConfirm }: { onClose: () => void, onConfirm: () => void }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/50 p-4 print-hidden">
        <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-6 text-white relative animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold mb-2">Deactivate License?</h3>
            <p className="text-slate-400 text-sm mb-6">
                This will sign you out and you will need to re-verify your license key to access the app again.
            </p>
            <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-lg transition-colors">Cancel</button>
                <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-lg transition-colors">Deactivate</button>
            </div>
        </div>
    </div>
);

const LicenseExpiryReminderModal = ({ onClose, daysRemaining }: { onClose: () => void, daysRemaining: number }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/50 p-4 print-hidden">
        <div className="bg-slate-900 border border-red-500/50 w-full max-w-md rounded-2xl shadow-2xl p-6 text-white relative animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-center w-12 h-12 bg-red-900/30 rounded-full mx-auto mb-4 border border-red-500/50">
                <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2">License Expiring Soon</h3>
            <p className="text-slate-400 text-center text-sm mb-4">
                Your license will expire in <span className="font-bold text-red-400">{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</span>.
            </p>
            <p className="text-slate-500 text-center text-xs mb-6">
                Please renew your license to continue using all features.
            </p>
            <button 
                onClick={onClose}
                className="block w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-lg transition-colors"
            >
                I Understand
            </button>
        </div>
    </div>
);

const OfflineVerificationModal = ({ onClose, qrCode }: { onClose: () => void, qrCode: string }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm bg-black/50 p-4 print-hidden">
        <div className="bg-slate-900 border border-yellow-500/50 w-full max-w-md rounded-2xl shadow-2xl p-6 text-white relative animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-900/30 rounded-full mx-auto mb-4 border border-yellow-500/50">
                <Wifi className="w-6 h-6 text-yellow-500" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2">Internet Connection Required</h3>
            <p className="text-slate-400 text-center text-sm mb-4">
                Your device has been offline for 60+ days. Please connect to the internet or scan the QR code with your phone to verify your license.
            </p>
            {qrCode && (
                <div className="bg-white p-4 rounded-xl inline-block mx-auto shadow-lg mb-4">
                    <img src={qrCode} alt="Verification QR" className="w-48 h-48" />
                </div>
            )}
            <p className="text-slate-500 text-center text-xs mb-6">
                Scan this QR code with your phone to verify your license is still active.
            </p>
            <button 
                onClick={onClose}
                className="block w-full bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-3 rounded-lg transition-colors"
            >
                I Understand
            </button>
        </div>
    </div>
);

// Numeric input with local string state so users can backspace/clear freely on mobile.
// React controlled inputs with value={number} reset the field when it reaches empty.
const NumericInput: React.FC<{
  value: number | undefined;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  className?: string;
  placeholder?: string;
}> = ({ value, onChange, min, max, className, placeholder }) => {
  const [text, setText] = React.useState(value != null && !isNaN(value) ? String(value) : '');
  const focused = React.useRef(false);

  React.useEffect(() => {
    if (!focused.current) {
      setText(value != null && !isNaN(value) ? String(value) : '');
    }
  }, [value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={className}
      placeholder={placeholder}
      value={text}
      onFocus={() => { focused.current = true; }}
      onChange={e => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        setText(raw);
        if (raw !== '') {
          let n = parseInt(raw, 10);
          if (min != null) n = Math.max(min, n);
          if (max != null) n = Math.min(max, n);
          onChange(n);
        }
      }}
      onBlur={() => {
        focused.current = false;
        const n = parseInt(text, 10);
        if (isNaN(n) || text === '') {
          setText(value != null && !isNaN(value) ? String(value) : '');
        } else {
          let clamped = n;
          if (min != null) clamped = Math.max(min, clamped);
          if (max != null) clamped = Math.min(max, clamped);
          setText(String(clamped));
          onChange(clamped);
        }
      }}
    />
  );
};

const App: React.FC = () => {
  // --- STATE ---
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLicenseLoaded, setIsLicenseLoaded] = useState(false);
  const [debugMachineId, setDebugMachineId] = useState<string>(''); 
    const [trialFeatureTier, setTrialFeatureTier] = useState<'PROFESSIONAL' | 'BASIC'>('PROFESSIONAL');
  
  const [darkMode, setDarkMode] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<'main' | 'style' | 'footer'>('main');
  const [showTextEffects, setShowTextEffects] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showMobileRegistrationInput, setShowMobileRegistrationInput] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileCompact, setMobileCompact] = useState(false); // True when screen is short (keyboard open)
  const [mobileTypingMode, setMobileTypingMode] = useState<'tap' | 'classic'>(() => {
    try { return (localStorage.getItem('mobileTypingMode') as 'tap' | 'classic') || 'tap'; } catch { return 'tap'; }
  });
  const [hideOriginalPreview, setHideOriginalPreview] = useState(true); // Default to true - hide original and use floating preview
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showBadgeSettingsModal, setShowBadgeSettingsModal] = useState(false);
  const [showBorderSettingsModal, setShowBorderSettingsModal] = useState(false);
  const [showBadgeSettingsInline, setShowBadgeSettingsInline] = useState(true);

  // Pro Feature Modal State
  const [showProModal, setShowProModal] = useState(false);
  const [proModalErrors, setProModalErrors] = useState<string[]>([]);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showLicenseExpiryReminder, setShowLicenseExpiryReminder] = useState(false);
  const [printCount, setPrintCount] = useState(0);
  const [showOfflineVerificationModal, setShowOfflineVerificationModal] = useState(false);
  const [offlineVerificationQR, setOfflineVerificationQR] = useState('');

  // LICENSING
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseForm, setLicenseForm] = useState({ name: '', company: '', address: '', postcode: '', sid: '', key: '', email: '', phone: '' });
  const [licenseStep, setLicenseStep] = useState<'FORM' | 'QR'>('FORM');
  const [qrCodeData, setQrCodeData] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [isVerifyingLicense, setIsVerifyingLicense] = useState(false);
  const [isOnlineVerification, setIsOnlineVerification] = useState(false);
  const [hasTrialUsed, setHasTrialUsed] = useState(false);
  const [showUpgradeLicenseModal, setShowUpgradeLicenseModal] = useState(false);
  const [upgradeLicenseKey, setUpgradeLicenseKey] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [showLicenseExpiredModal, setShowLicenseExpiredModal] = useState(false);
  const [expiredSessionEmail, setExpiredSessionEmail] = useState('');
  
  // AUTHENTICATION STATE
  const [currentSession, setCurrentSession] = useState<AuthSession | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [showLoginScreen, setShowLoginScreen] = useState(false);
  const [showRegistrationScreen, setShowRegistrationScreen] = useState(false);
  const [showTrialActivation, setShowTrialActivation] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isNewInstall, setIsNewInstall] = useState(false);
  
  // OTP State
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  
  const [customLicenseUrl, setCustomLicenseUrl] = useState(DEFAULT_SCRIPT_URL);

  // PRINTING & PROFILES
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [availableSystemPrinters, setAvailableSystemPrinters] = useState<any[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  const [printProgress, setPrintProgress] = useState(0);
  
  // Print Queue System
  interface PrintJob {
    id: string;
    profileId: string;
    overridePlateType?: PlateType;
    status: 'queued' | 'processing' | 'completed' | 'failed' | 'paused';
    progress: number;
    message: string;
    registration: string; // Registration number for this job
    paused: boolean; // Whether this job is paused
    cachedPrintData?: { // Cached print data to avoid regenerating PDF/image
      type: 'image' | 'pdf';
      data: string; // base64 image or PDF
      pageWidth: number;
      pageHeight: number;
      cacheKey: string; // Unique key to identify this cached data
    };
  }
  const [printQueue, setPrintQueue] = useState<PrintJob[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [showQueueDetails, setShowQueueDetails] = useState(false);
  const [printHistory, setPrintHistory] = useState<Array<{registration: string, timestamp: number, plateType: PlateType}>>([]);
  const [showPrintHistory, setShowPrintHistory] = useState(false);
  
  // Compact print queue indicator component (defined inside App to access PrintJob type)
  const PrintQueueIndicator = () => {
      const queuedCount = printQueue.filter(job => job.status === 'queued').length;
      const allJobs = printQueue.length;
      
      if (queuedCount === 0 && !isProcessingQueue && allJobs === 0) return null;
      
      return (
          <div className="fixed top-4 right-4 z-[9998] print-hidden flex flex-col gap-2 items-end">

              <button
                  onClick={() => setShowQueueDetails(!showQueueDetails)}
                  className="bg-blue-600 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-bold hover:bg-blue-700 transition-colors animate-in slide-in-from-top-5 fade-in"
              >
                  {isProcessingQueue ? (
                      <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Printing...</span>
                      </>
                  ) : (
                      <>
                          <Printer className="w-4 h-4" />
                          <span>{queuedCount} in queue</span>
                      </>
                  )}
              </button>
              
              {showQueueDetails && printQueue.length > 0 && (
                  <div className="mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-3 min-w-[280px] max-h-[400px] overflow-y-auto">
                      <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-bold text-slate-500 dark:text-slate-400">Print Queue ({printQueue.length})</div>
                          {printQueue.some(job => job.status === 'queued' && !job.paused) && (
                              <button
                                  onClick={() => {
                                      setPrintQueue(prev => prev.map(j => 
                                          (j.status === 'queued' && !j.paused) 
                                              ? { ...j, paused: true, status: 'paused' as const }
                                              : j
                                      ));
                                  }}
                                  className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded transition-colors flex items-center gap-1"
                                  title="Pause All"
                              >
                                  <Timer className="w-3 h-3" />
                                  <span>Pause All</span>
                              </button>
                          )}
                      </div>
                      <div className="space-y-2">
                          {printQueue.map((job) => (
                              <div 
                                  key={job.id} 
                                  className={`text-xs p-2 rounded ${
                                      job.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' :
                                      job.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                                      job.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                                      job.paused ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' :
                                      'bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                                  }`}
                              >
                                  <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                          <div className="font-bold text-slate-900 dark:text-slate-100 font-mono">{job.registration}</div>
                                          <div className="text-slate-500 dark:text-slate-400 capitalize mt-0.5">{job.paused ? 'Paused' : job.status}</div>
                                          {job.status === 'processing' && (
                                              <div className="mt-1 w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${job.progress}%` }}></div>
                                              </div>
                                          )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                          {(job.status === 'queued' || job.status === 'paused') && (
                                              <button
                                                  onClick={() => {
                                                      setPrintQueue(prev => prev.map(j => 
                                                          j.id === job.id 
                                                              ? { ...j, paused: !j.paused, status: j.paused ? 'queued' : 'paused' as const }
                                                              : j
                                                      ));
                                                  }}
                                                  className={`p-1.5 rounded transition-colors ${
                                                      job.paused 
                                                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' 
                                                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                                                  }`}
                                                  title={job.paused ? 'Resume' : 'Pause'}
                                              >
                                                  {job.paused ? <RefreshCw className="w-3.5 h-3.5" /> : <Timer className="w-3.5 h-3.5" />}
                                              </button>
                                          )}
                                          {(job.status === 'queued' || job.status === 'paused' || job.status === 'failed') && (
                                              <button
                                                  onClick={() => {
                                                      setPrintQueue(prev => prev.filter(j => j.id !== job.id));
                                                  }}
                                                  className="p-1.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                                  title="Delete"
                                              >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>
              )}
          </div>
      );
  };
  const [printDropdownOpen, setPrintDropdownOpen] = useState(false); 
  const printDropdownRef = useRef<HTMLDivElement>(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printPreviewImage, setPrintPreviewImage] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  const [profiles, setProfiles] = useState<PrinterProfile[]>([]);
  const [printSettings, setPrintSettings] = useState<PrintSettings>({
      dualMode: false,
      quickPrintEnabled: false,
      singleProfileId: '',
      dualWhiteProfileId: '',
      dualYellowProfileId: ''
  });

  // PDF EXPORT SETTINGS
  const [pdfExportSettings, setPdfExportSettings] = useState({
      dpi: 300,
      offsetX: 0,
      offsetY: 0,
      lengthCalibration: 100,
      rotate: false,
      mirror: false,
      enableCropMarks: false,
      cropMarksOffset: 5,
      showCropLinesPreview: false,
      showButton: true,
      savePrintPdfs: true, // Default to true (save PDFs on mobile)
      printerXOffset: 0,
      printerYOffset: 0
  });
  const [showPdfSettingsExpanded, setShowPdfSettingsExpanded] = useState(false);

  // RNPS STATE
  const [showRNPSModal, setShowRNPSModal] = useState(false);
  const [rnpsRecord, setRnpsRecord] = useState<RNPSRecord>({
      registration: '', date: '', customerName: '', customerAddress: '', 
      idType: 'Driving Licence', idReference: '', 
      entitlementType: 'V5C (Logbook)', entitlementReference: '',
      idImage: null, entitlementImage: null
  });
  const [rnpsSessionID, setRnpsSessionID] = useState<string>('');
  const [rnpsQrCode, setRnpsQrCode] = useState<string | null>(null);
  const [rnpsDefaultPath, setRnpsDefaultPath] = useState<string>('');
  const [autoSaveRnps, setAutoSaveRnps] = useState<boolean>(false);
  
  const [printJobConfig, setPrintJobConfig] = useState<{
      profile: PrinterProfile;
      plateType: PlateType;
  } | null>(null);

  // Performance optimization: Fast path skips PDF generation
  const USE_FAST_PATH = false; // Disabled - image capture produces blank prints

  // Bulk Import Modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkItems, setBulkItems] = useState<BulkItem[]>([]);
  const [bulkCsvInput, setBulkCsvInput] = useState('');
  const [bulkPrintDelay, setBulkPrintDelay] = useState(2); // seconds between prints
  const [bulkExportProgress, setBulkExportProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkPrintProgress, setBulkPrintProgress] = useState<{ current: number; total: number } | null>(null);
  const [showBulkSettings, setShowBulkSettings] = useState(false);
  const [showBulkCsvInput, setShowBulkCsvInput] = useState(true);
  const [bulkExportDropdownOpen, setBulkExportDropdownOpen] = useState(false);
  const bulkExportCancelledRef = useRef(false);
  const bulkPrintCancelledRef = useRef(false);
  const bulkExportDropdownRef = useRef<HTMLDivElement>(null);

  // ANDROID / PRINTER SDK STATE
  const [bluetoothDevices, setBluetoothDevices] = useState<any[]>([]);
  const [isScanningBluetooth, setIsScanningBluetooth] = useState(false);
  const [isConnectingAndroid, setIsConnectingAndroid] = useState(false);
  const [androidConnectionStatus, setAndroidConnectionStatus] = useState<string>('');
  const [showAndroidPrinterSelect, setShowAndroidPrinterSelect] = useState(false);
  const [showPrinterProfileSelect, setShowPrinterProfileSelect] = useState(false);
  const [pendingPrintAction, setPendingPrintAction] = useState<'SINGLE' | 'DOUBLE' | 'WHITE' | 'YELLOW' | 'PAIR' | null>(null);

  // Templates
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  // Custom Sizes & Fonts
  const [dimensions, setDimensions] = useState<Record<string, Dimensions>>(InitialDimensions);
  const [customFonts, setCustomFonts] = useState<any[]>([]);
  const [showSizeModal, setShowSizeModal] = useState(false);
  
  const [newSizeName, setNewSizeName] = useState('');
  const [newSizeW, setNewSizeW] = useState(520);
  const [newSizeH, setNewSizeH] = useState(111);
  const [newSizePath, setNewSizePath] = useState('');
  const [newSizeViewBox, setNewSizeViewBox] = useState('');
  const [newSizeTransform, setNewSizeTransform] = useState('');
  const [newSizeRawSVG, setNewSizeRawSVG] = useState('');
  const [showShapeInputs, setShowShapeInputs] = useState(false);

  // Zoom / Preview
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const mainPreviewRef = useRef<HTMLDivElement>(null);
  const hiddenRegInputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [manualZoom, setManualZoom] = useState(1.0); 
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isSuperZoom, setIsSuperZoom] = useState(false);

  const lastTouchRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);
  const wasInteractingRef = useRef<boolean>(false); 

  const isRestoringTemplate = useRef(false);
  const hasLoadedFromStorage = useRef(false);

  const [config, setConfig] = useState<PlateConfig>({
    registration: 'YOUR REG',
      type: PlateType.REAR,
        flagLayout: false,
      showFlagGraphic: true, // Default to showing graphic if badge is enabled
      borderCornerRadius: 10,
    badge: {
        enabled: false,
        type: FlagType.UK,
        customSvgUrl: '',
        backgroundColor: 'BLUE',
        isVertical: false,
        showIdentifier: true,
        identifierText: 'UK',
        borderRadius: 6,
        graphicScale: 1.2,
        graphicVOffset: 0,
        identifierTextSize: 18
    },

    plateSize: PlateSize.REGULAR,
    isShowPlate: false,
    showPlateOverrides: { 
        removeBSAU: false, removeDealer: false, reduceSpace: false 
    },
    mainFontFamily: 'Charles Wright Radial',
    mainFontSize: 99, 
    mainTextLineHeight: 0.9,
    mainTextVerticalOffset: 0,
    mainTextHeightScale: 1.0,
    mainTextAlignment: 'center',
    enableKerning: false, // Default OFF
    letterSpacing: 0, 
    textColor: '#000000',
    forceUppercase: true,
    textEffect: TextEffect.NONE,
    globalOffsetX: 0,
    globalOffsetY: 0,
    border: true,
    borderThickness: 1, 
    borderMargin: 5, 
    borderColor: '#000000',
    isEV: false,
    dealerText: { text: 'YOUR COMPANY', fontFamily: 'Charles Wright Radial', fontSize: 5, color: '#000000', show: true, bold: true, italic: true },
    useDealerLogo: false,
    dealerLogo: '',
    dealerLogoHeight: 8,
    dealerPostcode: 'POSTCODE',
    dealerPostcodeConfig: { fontSize: 4, color: '#000000', fontFamily: 'Charles Wright Moto', bold: true, italic: false },
    dealerSeparator: 15,
    dealerLocation: 'ON_BORDER', 
    dealerVerticalOffset: 0,
    slogan: '',
    sloganConfig: { text: '', fontFamily: 'Arial', fontSize: 5, color: '#334155', show: true },
    sloganLocation: 'ON_BORDER',
    supplierInCorner: false,
    bsauText: { text: 'BS AU 145e', fontFamily: 'Charles Wright Moto', fontSize: 3, color: '#000000', show: true, xOffset: 0, yOffset: 0, bold: true },
    bsauLocation: 'ON_BORDER',

    lippedPlate: false,
    lipImage: '',
    lipHeight: 15,
    lipText: {
      enabled: false,
      text: '',
      fontFamily: 'Arial',
      fontSize: 4,
      color: '#000000',
      bold: false,
      italic: false,
      xOffset: 0,
      yOffset: 0
    },
    lipTexts: [],
    lipImages: []
  });

  const currentDims = dimensions[config.plateSize] || dimensions['REGULAR'];
  const lipHeight = config.lippedPlate ? config.lipHeight : 0;

  const [editingProfile, setEditingProfile] = useState<PrinterProfile | null>(null);
  const [godexRawCommand, setGodexRawCommand] = useState('^L\nE');
  const [godexRawSending, setGodexRawSending] = useState(false);
  const [godexRawResult, setGodexRawResult] = useState<string | null>(null);

  // --- PROFESSIONAL CHECKER ---
  const checkProFeatures = (currentConfig: PlateConfig): string[] => {
      // DEMO MODE: No violations, all features allowed
      if (demoMode) return [];
      
      const violations: string[] = [];
      const dims = dimensions[currentConfig.plateSize];

      if (!BASIC_ALLOWED_FONTS.includes(currentConfig.mainFontFamily)) violations.push("Pro Font Selected");
      if (currentConfig.mainFontSize !== 99 && currentConfig.mainFontSize !== 79) violations.push("Custom Text Height");
      if (currentConfig.letterSpacing !== 0) violations.push("Letter Spacing");
      if (currentConfig.enableKerning) violations.push("Kerning Enabled");
      if (currentConfig.textEffect !== TextEffect.NONE) violations.push("Text Effects Active");
      if (currentConfig.useDealerLogo) violations.push("Dealer Logo Identity");
      
      // Flags: Layout is allowed, but printing with graphic is not.
      if (currentConfig.badge.enabled && currentConfig.showFlagGraphic) violations.push("Side Badge Graphic");
      
      if (currentConfig.lippedPlate) violations.push("Lipped Plate Image");
      if (currentConfig.isShowPlate) violations.push("Show Plate Options");
      if (printSettings.dualMode) violations.push("Dual Printer Mode");
      if (profiles.length > 1) violations.push("Multiple Printer Profiles");
      if (printSettings.quickPrintEnabled) violations.push("Quick Print Active");
      if (dims?.isHex || dims?.pathData) violations.push("Custom Shape / Hex");

      return violations;
  };

  // DEMO MODE: Check if running in demo mode
  const demoMode = isDemoMode();

  // Track container height in demo mode for proper sizing
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!demoMode) return;

    const updateHeight = () => {
      const container = document.getElementById('regiq-demo-app');
      if (container) {
        setContainerHeight(container.offsetHeight);
      }
    };

    // Initial measurement after a small delay
    setTimeout(updateHeight, 100);

    // Listen for resize
    window.addEventListener('resize', updateHeight);

    // Also use ResizeObserver for container-specific changes
    const container = document.getElementById('regiq-demo-app');
    let observer: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateHeight);
      observer.observe(container);
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      if (observer) observer.disconnect();
    };
  }, [demoMode]);

  // In demo mode, use demo license and bypass all checks
  const isPro = demoMode
      ? true
      : (isLicenseLoaded && currentUser?.license
          ? currentUser.license.isTrial
              ? trialFeatureTier === 'PROFESSIONAL'
              : currentUser.license.tier?.toUpperCase() === 'PROFESSIONAL'
          : false);

  // SERVER FEATURE FLAGS
  const [hideProFeatures, setHideProFeatures] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ps_hide_pro_features') || 'false'); } catch { return false; }
  });
  // When true, hide pro features entirely instead of showing with errors
  // Demo mode always shows all features (watermarked)
  const shouldHidePro = demoMode ? false : (hideProFeatures && !isPro);

  // Keep trial override in sync with current license
  useEffect(() => {
      if (currentUser?.license?.isTrial) {
          setTrialFeatureTier('PROFESSIONAL');
      } else if (currentUser?.license?.tier) {
          setTrialFeatureTier(currentUser.license.tier);
      }
  }, [currentUser?.license?.tier, currentUser?.license?.isTrial]);

  // PERIODIC LICENSE VALIDATION - runs every 30 seconds while app is active
  useEffect(() => {
      if (!currentSession || demoMode) {
          console.log('[HEARTBEAT] Skipped -', !currentSession ? 'no session' : 'demo mode');
          return;
      }

      console.log('[HEARTBEAT] Effect mounted/updated', {
          email: currentSession.email,
          sessionId: currentSession.sessionId,
          hasSessionId: !!currentSession.sessionId,
          licenseNumber: currentSession.licenseNumber,
          deviceId: currentSession.deviceId
      });

      const validateLicense = async () => {
          try {
              console.log('[HEARTBEAT] Starting validation...', {
                  email: currentSession.email,
                  sessionId: currentSession.sessionId,
                  hasSessionId: !!currentSession.sessionId
              });
              const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/validateSession', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      email: currentSession.email,
                      licenseId: currentSession.licenseNumber,
                      deviceId: currentSession.deviceId,
                      sessionId: currentSession.sessionId, // Include sessionId for kill detection
                      appVersion: APP_VERSION // Include app version for version enforcement
                  })
              });

              const data = await response.json();
              console.log('[HEARTBEAT] Validation response:', data);
              
              if (!data.valid) {
                  console.error('[HEARTBEAT] License invalid:', data.reason);
                  // License deleted, revoked, expired, user banned, or version blocked
                  await AuthService.clearSession();
                  LicenseManager.clear();
                  setCurrentSession(null);
                  setCurrentUser(null);
                  setUserSettings(null);
                  
                  if (data.reason === 'expired' || data.reason === 'all_expired') {
                      setExpiredSessionEmail(currentSession.email);
                      setShowLicenseExpiredModal(true);
                  } else if (data.reason === 'version_blocked') {
                      // Show version update required screen
                      alert(`App update required!\n\nYour app version is no longer supported.\n\nMinimum required: ${data.minimumVersion}\nLatest available: ${data.latestVersion}\n\nPlease update to continue using this license.`);
                      setShowLoginScreen(true);
                  } else if (data.reason === 'session_killed') {
                      // Session was terminated by admin
                      alert('Your session has been terminated by an administrator. Please log in again.');
                      setShowLoginScreen(true);
                  } else {
                      setShowLoginScreen(true);
                  }
              } else {
                  console.log('[HEARTBEAT] License valid');
                  // Check for admin messages
                  if (data.activeMessages && data.activeMessages.length > 0) {
                      setAdminMessages(data.activeMessages);
                  } else {
                      setAdminMessages([]);
                  }
                  // hideProFeatures now controlled locally via settings toggle only
              }
          } catch (error) {
              console.warn('[HEARTBEAT] Validation failed (network error):', error);
              // Don't kick user out on network errors - allow grace period
          }
      };

      // Run immediately on mount
      validateLicense();

      // Then run every 30 seconds (for testing - change back to 5 minutes later)
      const interval = setInterval(validateLicense, 30 * 1000);

      return () => clearInterval(interval);
  }, [currentSession]);

  // TIME TAMPER STATE
  const [showTimeTamperError, setShowTimeTamperError] = useState(false);
  const [timeTamperMessage, setTimeTamperMessage] = useState('');

  // ADMIN MESSAGES STATE
  const [adminMessages, setAdminMessages] = useState<Array<{id: string, subject: string, htmlContent: string}>>([]);
  // (moved above)
  const [dismissedMessages, setDismissedMessages] = useState<string[]>(() => {
      try { return JSON.parse(localStorage.getItem('dismissedMessages') || '[]'); } catch { return []; }
  });

  // CHECK FOR TIME TAMPERING ON APP STARTUP
  useEffect(() => {
      const tamperCheck = LicenseManager.checkTimeTamper();
      if (tamperCheck.isTampered) {
          setTimeTamperMessage(tamperCheck.message || 'System clock error detected');
          setShowTimeTamperError(true);
      }
      
      // Update timestamp periodically while app is running
      const interval = setInterval(() => {
          LicenseManager.updateLastKnownTimestamp();
      }, 60000); // Every minute
      
      return () => clearInterval(interval);
  }, []);

  // CHECK FOR EXISTING SESSION ON APP STARTUP
  useEffect(() => {
      if (demoMode) {
          setIsCheckingSession(false);
          return; // Demo mode handles its own session setup
      }
      const checkExistingSession = async () => {
          try {
              // Check for time tampering first
              const tamperCheck = LicenseManager.checkTimeTamper();
              if (tamperCheck.isTampered) {
                  setTimeTamperMessage(tamperCheck.message || 'System clock error detected');
                  setShowTimeTamperError(true);
                  setIsCheckingSession(false);
                  return;
              }
              
              const session = await AuthService.getSession();
              const settings = await AuthService.getUserSettings();
              
              if (session) {
                  // Load app immediately with cached session for fast startup
                  setCurrentSession(session);
                  setUserSettings(settings);
                  setShowLoginScreen(false);
                  setShowRegistrationScreen(false);
                  setShowTrialActivation(false);
                  setUpUserFromSession(session, settings);
                  setIsLicenseLoaded(true);
                  setIsCheckingSession(false);
                  
                  // Validate session with server in background (non-blocking)
                  if (navigator.onLine) {
                      (async () => {
                          try {
                              const validateResponse = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/validateSession', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                      email: session.email,
                                      licenseId: session.licenseNumber,
                                      deviceId: session.deviceId,
                                      sessionId: session.sessionId // Include sessionId for kill detection
                                  })
                              });
                              
                              const validateData = await validateResponse.json();
                              console.log('Background session validation result:', validateData);
                              
                              if (!validateData.valid) {
                                  // License invalid on server - clear local session and redirect
                                  console.warn('Session invalid:', validateData.reason);
                                  await AuthService.clearSession();
                                  LicenseManager.clear();
                                  
                                  // Show appropriate screen based on reason
                                  if (validateData.reason === 'expired' || validateData.reason === 'all_expired') {
                                      setExpiredSessionEmail(session.email);
                                      setShowLicenseExpiredModal(true);
                                  } else if (validateData.reason === 'banned') {
                                      setShowLoginScreen(true);
                                  } else {
                                      setShowLoginScreen(true);
                                  }
                                  setCurrentSession(null);
                              } else {
                                  // Update local session with server data
                                  if (validateData.validUntil) {
                                      session.validUntil = validateData.validUntil;
                                      session.tier = validateData.tier;
                                      await AuthService.saveSession(session);
                                      
                                      // Also update local license storage
                                      const deviceId = await LicenseManager.getMachineId();
                                      LicenseManager.saveLicense({
                                          key: session.licenseNumber,
                                          hwId: deviceId,
                                          tier: validateData.tier,
                                          expiryDate: new Date(validateData.validUntil).getTime(),
                                          isTrial: validateData.isTrial
                                      });
                                      
                                      // Update current session state with latest data
                                      setCurrentSession({...session});
                                  }
                                  
                                  // Clear time tamper flag on successful online validation
                                  LicenseManager.clearTimeTamperFlag();
                              }
                          } catch (validateError) {
                              console.warn('Background validation failed, continuing with cached session:', validateError);
                          }
                      })();
                  }
                  return;
              } else {
                  // Check if user has registered before
                  const isRegistered = await AuthService.isUserRegistered();
                  if (isRegistered) {
                      // Registered user, show login screen
                      setShowLoginScreen(true);
                      setShowRegistrationScreen(false);
                      setShowTrialActivation(false);
                  } else {
                      // First install, show registration screen
                      setShowLoginScreen(false);
                      setShowRegistrationScreen(true);
                      setShowTrialActivation(false);
                      setIsNewInstall(true);
                  }
              }
          } catch (e) {
              console.error('Session check failed:', e);
              setShowLoginScreen(true);
          } finally {
              setIsCheckingSession(false);
          }
      };
      checkExistingSession();
  }, []);

  // HANDLE SUCCESSFUL REGISTRATION
  const handleRegistrationSuccess = async (session: AuthSession, settings: UserSettings, showTrialActivation: boolean) => {
      setCurrentSession(session);
      setUserSettings(settings);
      setShowRegistrationScreen(false);
      setShowLoginScreen(false);
      
      // Apply user's company name and postcode to plate config with preset styling
      if (settings.company || settings.postcode) {
          setConfig(prev => ({
              ...prev,
              // Company name: Charles Wright Radial, bold+italic, size 5, BLACK
              dealerText: {
                  ...prev.dealerText,
                  text: settings.company || prev.dealerText.text,
                  fontFamily: 'Charles Wright Radial',
                  fontSize: 5,
                  color: '#000000',
                  bold: true,
                  italic: true
              },
              // Postcode: Charles Wright Moto, bold, size 4, BLACK
              dealerPostcode: settings.postcode || prev.dealerPostcode,
              dealerPostcodeConfig: {
                  ...prev.dealerPostcodeConfig,
                  fontFamily: 'Charles Wright Moto',
                  fontSize: 4,
                  color: '#000000',
                  bold: true,
                  italic: false
              },
              // Location: ON_BORDER
              dealerLocation: 'ON_BORDER',
              // Horizontal offset (separator): 15
              dealerSeparator: 15,
              // BSAU: Charles Wright Moto, bold, size 3, BLACK
              bsauText: {
                  ...prev.bsauText,
                  fontFamily: 'Charles Wright Moto',
                  fontSize: 3,
                  color: '#000000',
                  bold: true
              },
              bsauLocation: 'ON_BORDER'
          }));
      }
      
      if (showTrialActivation) {
          setShowTrialActivation(true);
      } else {
          // License key provided, skip trial activation
          setUpUserFromSession(session, settings);
          setShowTrialActivation(false);
      }
  };

  // HANDLE TRIAL ACTIVATION
  const handleTrialActivated = async (session: AuthSession) => {
      setCurrentSession(session);
      setShowTrialActivation(false);
      
      // Load the license that was just saved by LicenseManager.activateTrial()
      const savedLicense = LicenseManager.getStoredLicense();
      if (savedLicense) {
          setCurrentUser({ 
              name: savedLicense.contactName, 
              status: 'ACTIVE', 
              license: savedLicense 
          });
          setIsLicenseLoaded(true);
      } else {
          // Fallback to session-based setup if no saved license
          setUpUserFromSession(session, userSettings);
      }
  };

  const handleTrialTierToggle = () => {
      setTrialFeatureTier(prev => prev === 'PROFESSIONAL' ? 'BASIC' : 'PROFESSIONAL');
  };

  const handleProUpgradeTrial = () => {
      // Use new registration system for trial upgrade
      setShowRegistrationScreen(true);
      setShowLicenseModal(false);
  };

  // HANDLE UPGRADE LICENSE KEY (for trial users to upgrade without logging out)
  const handleUpgradeLicense = async () => {
      if (!upgradeLicenseKey.trim()) {
          setUpgradeError('Please enter a license key');
          return;
      }

      // Validate we have an email
      const userEmail = currentSession?.email || userSettings?.email;
      if (!userEmail) {
          setUpgradeError('No user email found. Please log in again.');
          return;
      }

      setUpgradeLoading(true);
      setUpgradeError('');

      try {
          const deviceId = await LicenseManager.getMachineId();
          const platform = await LicenseManager.getPlatform();

          console.log('[License Upgrade] Attempting to activate license:', {
              licenseKey: upgradeLicenseKey.trim().substring(0, 4) + '...',
              email: userEmail,
              deviceId: deviceId.substring(0, 8) + '...',
              platform
          });

          // Call activateLicense endpoint to assign the license to this user
          const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/activateLicense', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  licenseKey: upgradeLicenseKey.trim(),
                  email: userEmail,
                  deviceId,
                  platform
              })
          });

          const data = await response.json();
          console.log('[License Upgrade] Response:', { ok: response.ok, data });

          if (!response.ok) {
              throw new Error(data.error || 'Failed to activate license');
          }

          // Validate response has required fields
          const expiryDateStr = data.validUntil || data.expiryDate;
          if (!expiryDateStr) {
              console.error('[License Upgrade] No expiry date in response:', data);
              throw new Error('Invalid server response: missing expiry date');
          }

          const expiryDate = new Date(expiryDateStr).getTime();
          if (isNaN(expiryDate)) {
              console.error('[License Upgrade] Invalid expiry date:', expiryDateStr);
              throw new Error('Invalid expiry date received from server');
          }

          // Normalize tier
          const rawTier = data.tier || 'PROFESSIONAL';
          const normalizedTier = rawTier.toLowerCase() === 'basic' || rawTier.toLowerCase() === 'essential'
              ? 'BASIC'
              : 'PROFESSIONAL';

          // Success! Update the local license
          const newLicense: LicenseInfo = {
              key: upgradeLicenseKey.trim(),
              hwId: deviceId,
              tier: normalizedTier as 'BASIC' | 'PROFESSIONAL',
              expiryDate,
              contactName: userSettings?.name || currentSession?.email || 'User',
              companyName: userSettings?.company || '',
              isTrial: false
          };

          console.log('[License Upgrade] Saving new license:', { ...newLicense, key: newLicense.key.substring(0, 4) + '...' });
          LicenseManager.saveLicense(newLicense);

          // Update current user
          setCurrentUser(prev => prev ? {
              ...prev,
              license: newLicense
          } : null);

          // Update session
          if (currentSession) {
              const updatedSession: AuthSession = {
                  ...currentSession,
                  tier: normalizedTier,
                  validUntil: expiryDateStr,
                  licenseNumber: upgradeLicenseKey.trim()
              };
              setCurrentSession(updatedSession);
              await AuthService.saveSession(updatedSession);
          }

          setUpgradeSuccess(true);
          setToastMessage('License upgraded successfully!');

          // Close modal after short delay
          setTimeout(() => {
              setShowUpgradeLicenseModal(false);
              setUpgradeLicenseKey('');
              setUpgradeSuccess(false);
          }, 2000);

      } catch (e: any) {
          console.error('[License Upgrade] Error:', e);
          setUpgradeError(e.message || 'Failed to upgrade license');
      } finally {
          setUpgradeLoading(false);
      }
  };

  // HANDLE SUCCESSFUL LOGIN
  const handleLoginSuccess = async (session: AuthSession) => {
      // Move to the app immediately - device lock is already enforced at login
      console.log('✅ Login successful, moving to app');
      setCurrentSession(session);
      setShowLoginScreen(false);
      setShowRegistrationScreen(false);
      setShowTrialActivation(false);

      // Load user settings asynchronously (non-blocking)
      (async () => {
          try {
              const settings = await AuthService.getUserSettings();
              setUserSettings(settings);
              setUpUserFromSession(session, settings);
          } catch (err) {
              console.warn('Failed to load user settings:', err);
              setUpUserFromSession(session, null);
          }
      })();
  };

  // SET UP USER FROM SESSION
  const setUpUserFromSession = (session: AuthSession, settings: UserSettings | null) => {
      const isTrialTier = session.tier?.toLowerCase?.() === 'trial';
    const rawTier = session.tier || 'BASIC';
      const normalizedTier = isTrialTier
          ? 'PROFESSIONAL'
          : (rawTier.toLowerCase() === 'professional' ? 'PROFESSIONAL' : 'BASIC');

      const license: LicenseInfo = {
          key: session.licenseNumber || '',
          hwId: session.deviceId,
          tier: normalizedTier as 'BASIC' | 'PROFESSIONAL',
          expiryDate: new Date(session.validUntil).getTime(),
          contactName: settings?.name || session.email,
          companyName: settings?.company || '',
          isTrial: isTrialTier
      };

      setCurrentUser({
          name: settings?.name || session.email,
          status: 'ACTIVE',
          license
      });

      // Persist license so Android/iOS don't fall back to legacy registration modal
      if (license.key && license.expiryDate) {
          LicenseManager.saveLicense(license);
      }
      // Always mark license as loaded - background validateSession handles invalidation
      setIsLicenseLoaded(true);
      setShowLicenseModal(false);
  };

  // HANDLE LOGOUT
  const handleLogout = async () => {
      await AuthService.logout();
      setCurrentSession(null);
      setUserSettings(null);
      setCurrentUser(null);
      setShowLoginScreen(true);
      setShowRegistrationScreen(false);
      setShowTrialActivation(false);
  };

  // SWITCH FROM REGISTRATION TO LOGIN
  const handleSwitchToLogin = () => {
      setShowRegistrationScreen(false);
      setShowLoginScreen(true);
  };

  // SWITCH FROM LOGIN TO REGISTRATION
  const handleSwitchToRegister = () => {
      setShowLoginScreen(false);
      setShowRegistrationScreen(true);
  };

  // ALL HOOKS MUST BE CALLED BEFORE CONDITIONAL RENDERS
  useEffect(() => {
      // DEMO MODE: Set demo license immediately
      if (demoMode) {
          const demoLicense = getDemoLicense();
          setCurrentUser({
              name: demoLicense.contactName,
              status: 'ACTIVE',
              license: demoLicense
          });
          setCurrentSession({
              email: 'demo@regiq.co.uk',
              sessionId: 'demo-session',
              licenseNumber: 'DEMO-MODE-KEY',
              deviceId: 'demo-device',
              tier: 'PROFESSIONAL'
          } as any);
          setIsLicenseLoaded(true);
          setShowLicenseModal(false);
          setShowLoginScreen(false);
          setShowRegistrationScreen(false);
          console.log('Demo mode activated - all features unlocked');
          return; // Skip normal license loading
      }
      
      LicenseManager.getMachineId().then(id => setDebugMachineId(id));
      setHasTrialUsed(LicenseManager.hasUsedTrial());

      const lic = LicenseManager.getStoredLicense();
      if (!lic) {
          // Use new registration system only - disable old license modal
          setShowRegistrationScreen(true);
          setShowLicenseModal(false);
      } else {
          setCurrentUser({ name: lic.contactName, status: 'ACTIVE', license: lic });
          
          // Daily license verification with server (non-blocking)
          LicenseManager.verifyLicenseWithServer(lic, customLicenseUrl).then(result => {
              // Update current user if expiry date was updated
              if (result.expiryDate) {
                  const updatedLicense = LicenseManager.getStoredLicense();
                  if (updatedLicense && updatedLicense.expiryDate !== lic.expiryDate) {
                      setCurrentUser({ name: updatedLicense.contactName, status: 'ACTIVE', license: updatedLicense });
                  }
              }
              if (!result.valid) {
                  // Only clear license if explicitly revoked or expired
                  if (result.revoked === true) {
                      setLicenseError(result.message || 'License has been revoked.');
                      LicenseManager.clear();
                      setCurrentUser(null);
                      setShowLicenseModal(true);
                  } else if (result.expired === true) {
                      setLicenseError(result.message || 'License has expired.');
                      LicenseManager.clear();
                      setCurrentUser(null);
                      setShowLicenseModal(true);
                  } else if (result.message && result.message.includes('60+ days')) {
                      // Show QR code option for offline verification (but don't block)
                      const qrUrl = LicenseManager.generateVerificationQR(lic, customLicenseUrl);
                      QRCode.toDataURL(qrUrl, { width: 300, margin: 2 }, (err, dataUrl) => {
                          if (!err) {
                              setOfflineVerificationQR(dataUrl);
                              setShowOfflineVerificationModal(true);
                          }
                      });
                  }
                  // For other errors (network, server down), keep license active
              }
              
              // Also sync print count during daily verification
              const currentPrintCount = parseInt(localStorage.getItem('ps_print_count') || '0');
              if (currentPrintCount > 0) {
                  LicenseManager.syncPrintCount(lic, currentPrintCount, customLicenseUrl).catch(() => {
                      // Silent fail - will retry next day
                  });
              }
          }).catch(() => {
              // Silent fail - allow offline use within grace period, don't clear license
          });
      }
      setIsLicenseLoaded(true);
      
      // Check license expiry and show reminders
      if (lic) {
          const daysRemaining = Math.ceil((lic.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
          if (daysRemaining <= 30) {
              const lastReminderKey = `ps_license_reminder_${lic.key}`;
              const lastReminder = localStorage.getItem(lastReminderKey);
              const now = Date.now();
              const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
              
              if (!lastReminder || (now - parseInt(lastReminder)) >= threeDaysInMs) {
                  setShowLicenseExpiryReminder(true);
                  localStorage.setItem(lastReminderKey, now.toString());
              }
          }
      }
      
      // Load print count from localStorage
      const storedPrintCount = parseInt(localStorage.getItem('ps_print_count') || '0');
      setPrintCount(storedPrintCount);

      const storedUrl = localStorage.getItem('ps_license_url');
      if (storedUrl) setCustomLicenseUrl(storedUrl);

      const savedT = localStorage.getItem('ps_templates');
      if (savedT) setTemplates(JSON.parse(savedT));
      const savedS = localStorage.getItem('ps_custom_sizes');
      if (savedS) setDimensions({...InitialDimensions, ...JSON.parse(savedS)});
      
      const savedProfiles = localStorage.getItem('ps_profiles');
      if (savedProfiles) {
          const parsedProfiles = JSON.parse(savedProfiles);
          setProfiles(parsedProfiles);
          
          // If there's only one profile and no default is set, set it as default
          const savedSettings = localStorage.getItem('ps_print_settings');
          if (savedSettings) {
              const parsedSettings = JSON.parse(savedSettings);
              if (parsedProfiles.length === 1 && !parsedSettings.singleProfileId) {
                  parsedSettings.singleProfileId = parsedProfiles[0].id;
                  setPrintSettings(parsedSettings);
                  localStorage.setItem('ps_print_settings', JSON.stringify(parsedSettings));
              } else {
                  setPrintSettings(parsedSettings);
              }
          } else if (parsedProfiles.length === 1) {
              // No settings exist, create default with first profile
              const defaultSettings: PrintSettings = {
                  dualMode: false,
                  quickPrintEnabled: false,
                  singleProfileId: parsedProfiles[0].id,
                  dualWhiteProfileId: '',
                  dualYellowProfileId: ''
              };
              setPrintSettings(defaultSettings);
              localStorage.setItem('ps_print_settings', JSON.stringify(defaultSettings));
          }
      } else {
      const savedSettings = localStorage.getItem('ps_print_settings');
      if (savedSettings) setPrintSettings(JSON.parse(savedSettings));
      }

      // Removed: RNPS path and auto-save settings - now using fixed Documents/RegIQ location

      // Load all config settings from localStorage
      const savedConfig = localStorage.getItem('ps_plate_config');
      if (savedConfig) {
          try {
              const parsedConfig = JSON.parse(savedConfig);
              setConfig(prev => {
                  const loaded = {
                      ...prev,
                      ...parsedConfig,
                      // Ensure nested objects are merged properly
                      badge: { ...prev.badge, ...(parsedConfig.badge || {}) },
                      showPlateOverrides: { ...prev.showPlateOverrides, ...(parsedConfig.showPlateOverrides || {}) },
                      dealerText: { ...prev.dealerText, ...(parsedConfig.dealerText || {}) },
                      dealerPostcodeConfig: { ...prev.dealerPostcodeConfig, ...(parsedConfig.dealerPostcodeConfig || {}) },
                      sloganConfig: { ...prev.sloganConfig, ...(parsedConfig.sloganConfig || {}) },
                      bsauText: { ...prev.bsauText, ...(parsedConfig.bsauText || {}) }
                  };
                  hasLoadedFromStorage.current = true;
                  return loaded;
              });
          } catch (e) {
              console.error('Failed to load config settings:', e);
              hasLoadedFromStorage.current = true;
          }
      } else {
          hasLoadedFromStorage.current = true;
      }

      if (/Electron/.test(navigator.userAgent) && (window as any).require) {
         try {
             const { ipcRenderer } = (window as any).require('electron');
             ipcRenderer.invoke('get-printers').then((list: any[]) => {
                 console.log('[PRINTERS] Found printers:', list);
                 setAvailableSystemPrinters(list || []);
             }).catch((err: any) => {
                 console.error('[PRINTERS] Failed to get printers:', err);
                 setAvailableSystemPrinters([]);
             });
         } catch(e) { 
             console.error('[PRINTERS] Error accessing electron:', e); 
             setAvailableSystemPrinters([]);
         }
      }

      // Load saved RNPS settings for desktop
      if (!Capacitor.isNativePlatform()) {
          const savedPath = localStorage.getItem('ps_rnps_path');
          if (savedPath) setRnpsDefaultPath(savedPath);
          const savedAuto = localStorage.getItem('ps_rnps_auto');
          if (savedAuto) setAutoSaveRnps(savedAuto === 'true');
      }
  }, []);

  // Firebase listener for RNPS QR code uploads (desktop only)
  useEffect(() => {
      if (!showRNPSModal || !rnpsSessionID || Capacitor.isNativePlatform()) return;
      
      try {
          const app = initializeApp(firebaseConfig);
          const db = getDatabase(app);
          const uploadRef = ref(db, `uploads/${rnpsSessionID}`);

          const unsubscribe = onValue(uploadRef, (snapshot) => {
              const data = snapshot.val();
              if (data && data.image && data.type) {
                  if (data.type === 'id') {
                      setRnpsRecord(prev => ({...prev, idImage: data.image}));
                  } else if (data.type === 'entitlement') {
                      setRnpsRecord(prev => ({...prev, entitlementImage: data.image}));
                  }
                  // Auto-save if enabled
                  if (autoSaveRnps && rnpsDefaultPath) {
                      // Save logic would go here if needed
                  }
                  // Remove from Firebase after processing (optional, keeps things clean)
                  remove(uploadRef);
              }
          });
          
          return () => {
              unsubscribe();
          };
      } catch (e) {
          console.error('Firebase setup error:', e);
      }
  }, [showRNPSModal, rnpsSessionID, autoSaveRnps, rnpsDefaultPath]);

  // Daily license verification and check expiry when user changes
  useEffect(() => {
      if (currentUser?.license) {
          // Daily verification with server (check if 24 hours have passed)
          const lastVerification = localStorage.getItem('ps_last_verification');
          const oneDay = 24 * 60 * 60 * 1000;
          const shouldVerify = !lastVerification || (Date.now() - parseInt(lastVerification)) >= oneDay;
          
          if (shouldVerify) {
              LicenseManager.verifyLicenseWithServer(currentUser.license, customLicenseUrl).then(result => {
                  // Update current user if expiry date was updated
                  if (result.expiryDate) {
                      const updatedLicense = LicenseManager.getStoredLicense();
                      if (updatedLicense && updatedLicense.expiryDate !== currentUser.license.expiryDate) {
                          setCurrentUser({ ...currentUser, license: updatedLicense });
                      }
                  }
                  
                  if (!result.valid) {
                      // Only clear license if explicitly revoked or expired
                      if (result.revoked === true) {
                          setLicenseError(result.message || 'License has been revoked.');
                          LicenseManager.clear();
                          setCurrentUser(null);
                          setShowLicenseModal(true);
                      } else if (result.expired === true) {
                          setLicenseError(result.message || 'License has expired.');
                          LicenseManager.clear();
                          setCurrentUser(null);
                          setShowLicenseModal(true);
                      } else if (result.message && result.message.includes('60+ days')) {
                          const qrUrl = LicenseManager.generateVerificationQR(currentUser.license, customLicenseUrl);
                          QRCode.toDataURL(qrUrl, { width: 300, margin: 2 }, (err, dataUrl) => {
                              if (!err) {
                                  setOfflineVerificationQR(dataUrl);
                                  setShowOfflineVerificationModal(true);
                              }
                          });
                      }
                      // For other errors (network, server down), keep license active
                  }
                  
                  // Also sync print count during daily verification
                  const currentPrintCount = parseInt(localStorage.getItem('ps_print_count') || '0');
                  if (currentPrintCount > 0) {
                      LicenseManager.syncPrintCount(currentUser.license, currentPrintCount, customLicenseUrl).catch(() => {
                          // Silent fail - will retry next day
                      });
                  }
              }).catch(() => {
                  // Silent fail - allow offline use within grace period, don't clear license
              });
          }
          
          // Check expiry and show reminders
          const daysRemaining = Math.ceil((currentUser.license.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
          if (daysRemaining <= 30) {
              const lastReminderKey = `ps_license_reminder_${currentUser.license.key}`;
              const lastReminder = localStorage.getItem(lastReminderKey);
              const now = Date.now();
              const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
              
              if (!lastReminder || (now - parseInt(lastReminder)) >= threeDaysInMs) {
                  setShowLicenseExpiryReminder(true);
                  localStorage.setItem(lastReminderKey, now.toString());
              }
          }
      }
  }, [currentUser, customLicenseUrl]);

  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (printDropdownRef.current && !printDropdownRef.current.contains(event.target as Node)) {
              setPrintDropdownOpen(false);
          }
          if (bulkExportDropdownRef.current && !bulkExportDropdownRef.current.contains(event.target as Node)) {
              setBulkExportDropdownOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load PDF export settings from localStorage
  useEffect(() => {
      const savedPdfSettings = localStorage.getItem('ps_pdf_export_settings');
      if (savedPdfSettings) {
          try {
              const parsed = JSON.parse(savedPdfSettings);
              setPdfExportSettings(prev => ({ ...prev, ...parsed }));
          } catch (e) {
              console.error('Failed to load PDF export settings:', e);
          }
      }
  }, []);

  // Save PDF export settings to localStorage whenever they change
  useEffect(() => {
      localStorage.setItem('ps_pdf_export_settings', JSON.stringify(pdfExportSettings));
  }, [pdfExportSettings]);

  // Save all config settings to localStorage whenever they change
  useEffect(() => {
      // Don't save until we've loaded from storage (prevents overwriting on initial mount)
      if (!hasLoadedFromStorage.current) return;
      
      // Don't save registration as it's user input, not a setting
      const configToSave = {
          type: config.type,
          flagLayout: config.flagLayout,
          showFlagGraphic: config.showFlagGraphic,
          badge: config.badge,
          plateSize: config.plateSize,
          isShowPlate: config.isShowPlate,
          showPlateOverrides: config.showPlateOverrides,
          mainFontFamily: config.mainFontFamily,
          mainFontSize: config.mainFontSize,
          mainTextLineHeight: config.mainTextLineHeight,
          mainTextVerticalOffset: config.mainTextVerticalOffset,
          mainTextHeightScale: config.mainTextHeightScale,
          mainTextAlignment: config.mainTextAlignment,
          mainTextBold: config.mainTextBold,
          mainTextItalic: config.mainTextItalic,
          enableKerning: config.enableKerning,
          letterSpacing: config.letterSpacing,
          textColor: config.textColor,
          forceUppercase: config.forceUppercase,
          textEffect: config.textEffect,
          globalOffsetX: config.globalOffsetX,
          globalOffsetY: config.globalOffsetY,
          border: config.border,
          borderThickness: config.borderThickness,
          borderMargin: config.borderMargin,
          borderColor: config.borderColor,
          isEV: config.isEV,
          dealerText: config.dealerText,
          useDealerLogo: config.useDealerLogo,
          dealerLogo: config.dealerLogo,
          dealerLogoHeight: config.dealerLogoHeight,
          dealerPostcode: config.dealerPostcode,
          dealerPostcodeConfig: config.dealerPostcodeConfig,
          dealerSeparator: config.dealerSeparator,
          dealerLocation: config.dealerLocation,
          dealerVerticalOffset: config.dealerVerticalOffset,
          slogan: config.slogan,
          sloganLocation: config.sloganLocation,
          sloganConfig: config.sloganConfig,
          supplierInCorner: config.supplierInCorner,
          bsauText: config.bsauText,
          bsauLocation: config.bsauLocation,
          lippedPlate: config.lippedPlate,
          lipImage: config.lipImage,
          lipHeight: config.lipHeight
      };
      localStorage.setItem('ps_plate_config', JSON.stringify(configToSave));
  }, [config]);

  // Mobile detection + compact mode based on aspect ratio
  useEffect(() => {
      const checkMobile = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        // Check if on Android/iOS via Capacitor
        const isNativeMobile = Capacitor.isNativePlatform() && (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios');
        const isMobileWidth = width < 768;
        setIsMobile(isNativeMobile || isMobileWidth);
        // Compact mode when height is less than 1.2x width (squarish/landscape) on mobile
        // This mimics when keyboard is open and taking up screen space
        setMobileCompact((isNativeMobile || isMobileWidth) && height < width * 1.3);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ESC key handler to close print preview modal and cancel bulk operations
  useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
              if (showPrintPreview) {
                  setShowPrintPreview(false);
                  setPrintPreviewImage(null);
              } else if (bulkPrintProgress !== null) {
                  cancelBulkPrint();
              } else if (bulkExportProgress !== null) {
                  cancelBulkExport();
              }
          }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPrintPreview, bulkPrintProgress, bulkExportProgress]);

  const handleLicenseSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLicenseError('');
      setIsOnlineVerification(true); 

      try {
          const mid = await LicenseManager.getMachineId();
          const platform = await LicenseManager.getPlatform();
          const details = {
              name: licenseForm.name,
              company: licenseForm.company,
              address: licenseForm.address,
              sid: licenseForm.sid,
              email: licenseForm.email,
              phone: licenseForm.phone,
              platform: platform
          };
          setDebugMachineId(mid); 

          if (navigator.onLine) {
              const onlineResult = await LicenseManager.attemptOnlineActivation(mid, licenseForm.key, details, customLicenseUrl);
              if (onlineResult.success && onlineResult.code) {
                  setActivationCode(onlineResult.code); 
                  setLicenseStep('QR'); 
                  setLicenseError('License found online. Please click Verify.');
              }
          }

          const url = LicenseManager.generateRegistrationQRUrl(mid, licenseForm.key, details, customLicenseUrl);
          
          QRCode.toDataURL(url, { width: 300, margin: 2 }, (err, dataUrl) => {
              if (err) throw err;
              setQrCodeData(dataUrl);
              setLicenseStep('QR');
          });

      } catch (e: any) {
          setLicenseError("Failed to process license: " + e.message);
      } finally {
          setIsOnlineVerification(false);
      }
  };

  const handleActivationVerify = async () => {
      setIsVerifyingLicense(true);
      setLicenseError('');
      try {
          await new Promise(r => setTimeout(r, 2000));
          
          const mid = await LicenseManager.getMachineId();
          const tier = await LicenseManager.validateActivationCode(activationCode, mid, licenseForm.key, customLicenseUrl);

          if (tier) {
              // Get expiry from server response if available, otherwise default to 365 days
              const expiryDate = tier.expiryDate || (Date.now() + (365 * 24 * 60 * 60 * 1000)); // Default 365 days
              const licInfo: LicenseInfo = {
                  key: licenseForm.key,
                  hwId: mid,
                  tier: tier.tier,
                  expiryDate: expiryDate,
                  contactName: licenseForm.name,
                  companyName: licenseForm.company
              };
              LicenseManager.saveLicense(licInfo);
              setCurrentUser({ name: licenseForm.name, status: 'ACTIVE', license: licInfo });
              setShowLicenseModal(false);
              showToast(`Activation Successful! Tier: ${tier.tier}`);
          } else {
              setLicenseError("Invalid Activation Code.");
          }
      } catch (e: any) {
          setLicenseError("Verification Error: " + e.message);
      } finally {
          setIsVerifyingLicense(false);
      }
  };

  const handleSendOtp = async () => {
      const trimmedEmail = licenseForm.email?.trim();
      if (!trimmedEmail) {
          setLicenseError("Please enter your email address first.");
          return;
      }
      // Basic email regex
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
           setLicenseError("Please enter a valid email address.");
           return;
      }

      setIsSendingOtp(true);
      setLicenseError('');
      
      try {
          const mid = await LicenseManager.getMachineId();
          const details = {
              name: licenseForm.name?.trim() || '',
              company: licenseForm.company?.trim() || '',
              phone: licenseForm.phone?.trim() || ''
          };

          const result = await LicenseManager.sendOtp(trimmedEmail, mid, details);
          if (result.success) {
              setOtpSent(true);
              const msg = result.message || "Verification code sent! Please check your email.";
              showToast(msg);
              setLicenseError(''); 
          }
      } catch (e: any) {
          const msg = e.message || "Failed to send OTP.";
          setLicenseError(msg);
      } finally {
          setIsSendingOtp(false);
      }
  };

  const handleTrialActivation = async () => {
      setLicenseError('');
      
      // Require user details before trial - check for empty/whitespace strings
      const trimmedName = licenseForm.name?.trim() || '';
      const trimmedCompany = licenseForm.company?.trim() || '';
      const trimmedEmail = licenseForm.email?.trim() || '';
      const trimmedPhone = licenseForm.phone?.trim() || '';
      const trimmedOtp = otp?.trim() || '';
      
      if (!trimmedName || !trimmedCompany || !trimmedEmail || !trimmedPhone) {
          setLicenseError("Please fill in all required details (Name, Company, Email, Phone) before starting trial.");
          return;
      }

      if (!otpSent) {
          setLicenseError("Please click 'Send Code' to verify your email address.");
          return;
      }

      if (!trimmedOtp) {
          setLicenseError("Please enter the verification code sent to your email.");
          return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
          setLicenseError("Please enter a valid email address.");
          return;
      }

      try {
          const mid = await LicenseManager.getMachineId();
          const platform = await LicenseManager.getPlatform();
          
          console.log('Trial activation - Machine ID:', mid);
          console.log('Trial activation - Platform:', platform);
          
          const details = {
              name: trimmedName,
              company: trimmedCompany,
              address: licenseForm.address?.trim() || '',
              email: trimmedEmail,
              phone: trimmedPhone,
              platform: platform || 'unknown',
              otp: trimmedOtp
          };
          
          const lic = await LicenseManager.activateTrial(mid, details, customLicenseUrl);
          setCurrentUser({ name: lic.contactName, status: 'ACTIVE', license: lic });
          setShowLicenseModal(false);
          setHasTrialUsed(true);

          // Auto-fill footer settings with registration data and apply default presets
          const trimmedPostcode = licenseForm.postcode?.trim().toUpperCase() || '';
          setConfig(prev => ({
              ...prev,
              // Company name preset: Charles Radial, bold+italic, size 5, black
              dealerText: {
                  ...prev.dealerText,
                  text: trimmedCompany,
                  fontFamily: 'Charles Wright Radial Eng',
                  fontSize: 5,
                  color: '#000000',
                  bold: true,
                  italic: true,
                  show: true
              },
              // Postcode preset: Charles Moto, bold, size 4, black
              dealerPostcode: trimmedPostcode,
              dealerPostcodeConfig: {
                  ...prev.dealerPostcodeConfig,
                  fontFamily: 'Charles Wright Moto 2012',
                  fontSize: 4,
                  color: '#000000',
                  bold: true
              },
              // On-border setting
              dealerLocation: 'ON_BORDER',
              // BSAU preset: Charles Moto, bold, size 3, horizontal offset 15, black
              bsauText: {
                  ...prev.bsauText,
                  fontFamily: 'Charles Wright Moto 2012',
                  fontSize: 3,
                  color: '#000000',
                  bold: true,
                  xOffset: 15,
                  show: true
              },
              bsauLocation: 'ON_BORDER'
          }));

          showToast(`7-Day Professional Trial Started!`);
      } catch (e: any) {
          console.error('Trial activation error:', e);
          setLicenseError(e.message || 'Failed to activate trial. Please try again.');
      }
  };

  const handleDeactivate = () => {
      LicenseManager.clear();
      setCurrentUser(null);
      // Use new registration system only - disable old license modal  
      setShowRegistrationScreen(true);
      setShowLicenseModal(false);
      setLicenseStep('FORM');
      setLicenseForm({ name: '', company: '', address: '', sid: '', key: '', email: '', phone: '' });
      setShowSignOutModal(false);
  };

  useEffect(() => {
      if (isRestoringTemplate.current) {
          isRestoringTemplate.current = false;
          return;
      }

      const preset: any = {};
      let isPresetSize = false;

      switch(config.plateSize) {
          case PlateSize.MOTO_MINI:
              preset.mainFontFamily = 'Charles Moto Radial';
              preset.mainFontSize = 78;
              preset.mainTextVerticalOffset = 0; 
              preset.borderThickness = 2;
              preset.borderMargin = 4;
              preset.mainTextLineHeight = 0.75;
              isPresetSize = true;
              break;
          case PlateSize.MOTO_COMPACT:
              preset.mainFontFamily = 'Charles Wright Radial';
              preset.mainFontSize = 81; 
              preset.mainTextVerticalOffset = 0;
              preset.borderThickness = 1;
              preset.borderMargin = 3;
              preset.mainTextLineHeight = 0.9;
              isPresetSize = true;
              break;
          case PlateSize.MOTO_MID:
              preset.mainFontFamily = 'Charles Moto Radial';
              preset.mainFontSize = 99; 
              preset.mainTextVerticalOffset = 8; 
              preset.mainTextLineHeight = 0.75;
              isPresetSize = true;
              break;
          case PlateSize.MOTO_MICRO:
              preset.mainFontFamily = 'Charles Moto Radial';
              preset.mainFontSize = 72;
              preset.mainTextVerticalOffset = -1; 
              preset.borderThickness = 1.5;
              preset.borderMargin = 2;
              preset.mainTextLineHeight = 0.7;
              preset.letterSpacing = -1;
              isPresetSize = true;
              break;
          case PlateSize.MOTO_LARGE:
              preset.mainFontFamily = 'Charles Moto Radial';
              preset.mainFontSize = 99;
              preset.mainTextVerticalOffset = 1; 
              preset.mainTextLineHeight = 0.8;
              isPresetSize = true;
              break;
          case PlateSize.SQUARE_4X4:
              preset.mainFontFamily = 'Charles Wright Radial';
              preset.mainFontSize = 99;
              preset.mainTextVerticalOffset = 0;
              preset.mainTextLineHeight = 0.95;
              isPresetSize = true;
              break;
          case PlateSize.JDM:
              preset.mainFontFamily = 'Charles Moto Radial';
              preset.mainFontSize = 99;
              preset.mainTextVerticalOffset = 1; 
              preset.mainTextLineHeight = 0.75;
              isPresetSize = true;
              break;
      }
      
      if (isPresetSize) {
          if (Object.keys(preset).length > 0) {
              setConfig(prev => {
                  // For two-line plate sizes, convert default "YOUR REG" to two-line format
                  const isTwoLinePlate = [PlateSize.MOTO_MINI, PlateSize.MOTO_COMPACT, PlateSize.MOTO_MID,
                      PlateSize.MOTO_MICRO, PlateSize.MOTO_LARGE, PlateSize.JDM, PlateSize.SQUARE_4X4].includes(config.plateSize as PlateSize);
                  // Hex plates are single-line plates
                  const isSingleLinePlate = [PlateSize.HEX_5, PlateSize.HEX_6, PlateSize.HEX_7].includes(config.plateSize as PlateSize);
                  let newRegistration = prev.registration;
                  if (isTwoLinePlate && prev.registration === 'YOUR REG') {
                      newRegistration = 'YOUR\nREG';
                  } else if (isSingleLinePlate && prev.registration === 'YOUR\nREG') {
                      // Convert back to single line when switching from two-line plate (including square) to single-line plate (hex)
                      newRegistration = 'YOUR REG';
                  } else if (!isTwoLinePlate && !isSingleLinePlate && prev.registration === 'YOUR\nREG') {
                      // Convert back to single line for other non-two-line preset plates
                      newRegistration = 'YOUR REG';
                  }
                  return { ...prev, ...preset, registration: newRegistration };
              });
          }
      } else {
          // Handle non-preset sizes (regular, short, oversize, hex plates)
          const isSingleLinePlate = [PlateSize.REGULAR, PlateSize.SHORT_7, PlateSize.SHORT_6, PlateSize.SHORT_5, 
              PlateSize.OVERSIZE, PlateSize.HEX_5, PlateSize.HEX_6, PlateSize.HEX_7].includes(config.plateSize as PlateSize);
          if (isSingleLinePlate) {
               setConfig(prev => {
                   // Convert back to single line when switching to regular/short/oversize/hex plates
                   let newRegistration = prev.registration;
                   if (prev.registration === 'YOUR\nREG') {
                       newRegistration = 'YOUR REG';
                   }
                   return {
                       ...prev,
                       mainFontFamily: 'Charles Wright Radial',
                       mainFontSize: 99,
                       mainTextVerticalOffset: 0,
                       mainTextLineHeight: 0.9,
                       letterSpacing: 0,
                       registration: newRegistration
                       // Don't reset borderThickness and borderMargin - preserve user's manual settings
                   };
               });
          }
      }
  }, [config.plateSize]);

  const openRNPSModal = (regOverride?: string) => {
      try {
          const currentReg = regOverride || config.registration;
          setRnpsRecord({
              ...rnpsRecord,
              registration: currentReg,
              date: new Date().toLocaleDateString('en-GB'),
              idImage: null,
              entitlementImage: null
          });
          // Generate QR code for desktop or web demo
          const isDesktopOrWeb = !Capacitor.isNativePlatform();
          if (isDesktopOrWeb) {
              const sessionId = `rnps_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              setRnpsSessionID(sessionId);
              const qrUrl = `https://regiq.co.uk/qr-upload/?session=${sessionId}&reg=${config.registration}`;
              QRCode.toDataURL(qrUrl, { width: 300, margin: 2 }, (err, dataUrl) => {
                  if (!err) {
                      setRnpsQrCode(dataUrl);
                  }
              });
              // Load saved path and auto-save preference (Electron only)
              if (/Electron/.test(navigator.userAgent) && (window as any).require) {
                  const savedPath = localStorage.getItem('ps_rnps_path');
                  if (savedPath) setRnpsDefaultPath(savedPath);
                  const savedAuto = localStorage.getItem('ps_rnps_auto');
                  if (savedAuto) setAutoSaveRnps(savedAuto === 'true');
              }
          }
          setShowRNPSModal(true);
      } catch(e) {
          console.error("Failed to open RNPS Modal", e);
          alert("Error opening compliance tool.");
      }
  };

  // Camera capture function
  const capturePhoto = async (field: 'id' | 'entitlement') => {
      try {
          if (Capacitor.isNativePlatform()) {
              // Use Capacitor Camera on Android/iOS
              const image = await CapacitorCamera.getPhoto({
                  quality: 90,
                  allowEditing: false,
                  resultType: CameraResultType.DataUrl,
                  source: CameraSource.Camera
              });
              
              if (image.dataUrl) {
                  const watermarked = await watermarkImage(image.dataUrl);
                  if (field === 'id') {
                      setRnpsRecord(prev => ({...prev, idImage: watermarked}));
                  } else {
                      setRnpsRecord(prev => ({...prev, entitlementImage: watermarked}));
                  }
              }
          } else {
              // Fallback to file input on web/Electron
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.capture = 'environment'; // Use camera if available
              input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                      const reader = new FileReader();
                      reader.onload = async (ev) => {
                          if (ev.target?.result) {
                              const watermarked = await watermarkImage(ev.target.result as string);
                              if (field === 'id') {
                                  setRnpsRecord(prev => ({...prev, idImage: watermarked}));
                              } else {
                                  setRnpsRecord(prev => ({...prev, entitlementImage: watermarked}));
                              }
                          }
                      };
                      reader.readAsDataURL(file);
                  }
              };
              input.click();
          }
      } catch (e: any) {
          console.error('Camera capture failed:', e);
          alert('Failed to capture photo: ' + (e.message || 'Unknown error'));
      }
  };

  // Browse/select image function
  const browseImage = (field: 'id' | 'entitlement') => {
      const input = document.getElementById(`${field}-upload`) as HTMLInputElement;
      input?.click();
  };

  // Removed Firebase upload listener - no longer using QR code scanning 

  const watermarkImage = (base64: string): Promise<string> => {
      return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                  ctx.drawImage(img, 0, 0);
                  const fontSize = Math.max(20, img.width / 25);
                  ctx.font = `bold ${fontSize}px Arial`;
                  ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  const text = `RNPS RECORD - ${new Date().toLocaleDateString('en-GB')}`;
                  ctx.save();
                  ctx.translate(canvas.width / 2, canvas.height / 2);
                  ctx.rotate(-Math.PI / 4);
                  ctx.fillText(text, 0, 0);
                  ctx.restore();
                  resolve(canvas.toDataURL('image/jpeg', 0.8));
              } else { resolve(base64); }
          };
          img.src = base64;
      });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, field: 'id' | 'entitlement') => {
      if (e.target.files && e.target.files[0]) {
          const reader = new FileReader();
          reader.onload = async (ev) => {
              if (ev.target?.result) {
                  const watermarked = await watermarkImage(ev.target.result as string);
                  if (field === 'id') {
                      setRnpsRecord(prev => ({...prev, idImage: watermarked}));
                  } else {
                      setRnpsRecord(prev => ({...prev, entitlementImage: watermarked}));
                  }
              }
          };
          reader.readAsDataURL(e.target.files[0]);
          // Reset input so same file can be selected again
          e.target.value = '';
      }
  };

  const selectRNPSFolder = async () => {
      if (/Electron/.test(navigator.userAgent) && (window as any).require) {
          try {
              const { ipcRenderer } = (window as any).require('electron');
              const path = await ipcRenderer.invoke('select-folder');
              if (path) {
                  setRnpsDefaultPath(path);
                  localStorage.setItem('ps_rnps_path', path);
              }
          } catch(e) {
              console.error("Folder Select Error", e);
          }
      } else {
          alert("Default folder selection is only available in the desktop app.");
      }
  };

  const toggleAutoSaveRnps = (val: boolean) => {
      setAutoSaveRnps(val);
      localStorage.setItem('ps_rnps_auto', val.toString());
  };

  const showToast = (msg: string) => {
      setToastMessage(msg);
      setTimeout(() => setToastMessage(null), 3000);
  };

  const saveCompliancePacket = async () => {
      if (!isLicenseLoaded) {
          alert('Please wait for license to load...');
          return;
      }
      // Check actual license state (respect trial tier override)
      const effectiveTier = currentUser?.license?.isTrial ? trialFeatureTier : currentUser?.license?.tier;
      if (!effectiveTier || effectiveTier.toUpperCase() !== 'PROFESSIONAL') {
          // Debug info
          const debugInfo = `License check failed. Loaded: ${isLicenseLoaded}, User: ${!!currentUser}, Tier: ${effectiveTier || 'undefined'}, Full License: ${JSON.stringify(currentUser?.license)}`;
          console.log('Compliance Packet Blocked:', debugInfo);
          setProModalErrors(['RNPS Record Download/Saving']);
          setShowProModal(true);
          return;
      }

      const zip = new JSZip();
      const textContent = `
RNPS RECORD KEEPING
-------------------
Date: ${rnpsRecord.date}
Registration: ${rnpsRecord.registration}

CUSTOMER DETAILS
Name: ${rnpsRecord.customerName}
Address: ${rnpsRecord.customerAddress}

IDENTITY DOCUMENT
Type: ${rnpsRecord.idType}
Reference: ${rnpsRecord.idReference}

ENTITLEMENT DOCUMENT
Type: ${rnpsRecord.entitlementType}
Reference: ${rnpsRecord.entitlementReference}
      `;
      zip.file(`Record_${rnpsRecord.registration}.txt`, textContent);
      if (rnpsRecord.idImage) zip.file(`ID_Evidence.jpg`, rnpsRecord.idImage.split(',')[1], {base64: true});
      if (rnpsRecord.entitlementImage) zip.file(`Entitlement_Evidence.jpg`, rnpsRecord.entitlementImage.split(',')[1], {base64: true});

      try {
          const dims = dimensions[config.plateSize];
          const lipHeight = config.lippedPlate ? config.lipHeight : 0;
          const pdfBlob = await generatePlatePDFBlob('plate-export-standard', dims.width, dims.height + lipHeight, false, 300, {
              topAligned: config.lippedPlate
          });
          zip.file(`Plate_Artwork_${rnpsRecord.registration}.pdf`, pdfBlob);
      } catch(e) { console.error("Failed to add PDF to zip", e); }

      const content = await zip.generateAsync({type:"base64"});
      
      // On Android, save to Documents/RegIQ folder
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
          try {
              const fileName = `RNPS_${rnpsRecord.registration}_${Date.now()}.zip`;
              await Filesystem.writeFile({
                  path: `RegIQ/${fileName}`,
                  data: content,
                  directory: Directory.Documents,
                  recursive: true
              });
              showToast(`Saved to Documents/RegIQ/${fileName}`);
              setShowRNPSModal(false);
              return;
          } catch(e: any) {
              console.error("Save failed:", e);
              alert('Failed to save: ' + (e.message || 'Unknown error'));
              return;
          }
      }
      
      // On Electron, use IPC
      if (/Electron/.test(navigator.userAgent) && (window as any).require) {
          try {
              const { ipcRenderer } = (window as any).require('electron');
              const result = await ipcRenderer.invoke('save-zip', {
                  path: rnpsDefaultPath || (typeof window !== 'undefined' && (window as any).require ? (window as any).require('os').homedir() : ''),
                  fileName: `RNPS_${rnpsRecord.registration}_${Date.now()}.zip`,
                  dataBase64: content
              });
              if(result.success) {
                  showToast(`Saved to ${result.path}`);
                  setShowRNPSModal(false);
                  return;
              }
          } catch(e) { 
              console.error("Save Failed", e); 
          }
      }

      // Fallback: browser download
      const link = document.createElement('a');
      link.href = "data:application/zip;base64," + content;
      link.download = `RNPS_Packet_${rnpsRecord.registration}.zip`;
      link.click();
      showToast("Download Started");
      setShowRNPSModal(false);
  };

  // Computed variable to check if any modal is open - used to block registration input interactions
  const isAnyModalOpen = showPrinterSettings || showPrinterProfileSelect || showPrintHistory ||
    showProModal || showSignOutModal || showLicenseModal || showRNPSModal || showBulkModal ||
    showTemplateModal || showAndroidPrinterSelect || showPrintPreview || showSizeModal ||
    showOfflineVerificationModal || showLicenseExpiryReminder || !!editingProfile;

  const handleResize = useCallback(() => {
      if (mainPreviewRef.current) {
        // Use window dimensions for mobile to avoid feedback loops with flex containers
        const containerWidth = isMobile ? window.innerWidth : mainPreviewRef.current.clientWidth;
        const containerHeight = isMobile ? window.innerHeight : mainPreviewRef.current.clientHeight;
        
        if (containerWidth === 0 || containerHeight === 0) return;
        
        const currentDims = dimensions[config.plateSize] || dimensions['REGULAR'];
        const lipHeight = config.lippedPlate ? config.lipHeight : 0;
        
        // Mobile-specific layout adjustments
        // On mobile, we reserve space for header (60px) and buttons (approx 80px)
        const mobileReservedHeight = isMobile ? (mobileCompact ? 100 : 140) : 0; 
        const availableHeight = Math.max(150, containerHeight - mobileReservedHeight); // Minimum 150px
        const availableWidth = Math.max(200, containerWidth - (isMobile ? 20 : 0)); // Minimum 200px

        const scaleX = availableWidth / currentDims.width;
        // On mobile, force fit to height to ensure buttons are visible
        const scaleY = availableHeight / (currentDims.height + lipHeight);

        // Use the smaller scale to ensure it fits both dimensions
        // For tall plates on mobile, force scaling to fit within available height
        const calculatedScale = Math.min(scaleX, scaleY);
        
        const baseScale = isFinite(calculatedScale) && calculatedScale > 0 
          ? Math.max(0.15, Math.min(1.5, calculatedScale))
          : 0.4; // Reduced default from 0.5
        
        // Only update if the change is significant (prevents micro-adjustments)
        setPreviewScale(prev => {
          const diff = Math.abs(prev - baseScale);
          return diff > 0.05 ? baseScale : prev; // Only update if change is > 5%
        });
      }
  }, [config.plateSize, config.lippedPlate, config.lipImage, config.lipHeight, dimensions, isMobile, mobileCompact]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    // Initial resize with small delay to ensure container dimensions are available
    const timeoutId = setTimeout(() => handleResize(), 100);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  // Wheel zoom handler
  const handleWheelZoom = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setManualZoom(z => Math.max(0.3, Math.min(5, z + delta)));
  }, []);

  // Touch handlers — plate preview pinch zoom + tap-to-type on mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Two fingers — start pinch zoom tracking
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      pinchStartZoomRef.current = manualZoom;
      lastTouchRef.current = null;
    } else if (e.touches.length === 1) {
      // Single touch
      const now = Date.now();
      const touch = e.touches[0];
      if (!isMobile) {
        // Desktop behavior: check for double tap
        if (lastTouchRef.current && now - lastTouchRef.current.time < 300) {
          // Double tap detected
          if (isSuperZoom) {
            // Reset zoom
            setManualZoom(1);
            setPanPosition({ x: 0, y: 0 });
            setIsSuperZoom(false);
          } else {
            // Super zoom to tap location
            setManualZoom(3);
            setIsSuperZoom(true);
            // Calculate pan to center on tap location
            if (mainPreviewRef.current) {
              const rect = mainPreviewRef.current.getBoundingClientRect();
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;
              const offsetX = (centerX - touch.clientX) * 0.5;
              const offsetY = (centerY - touch.clientY) * 0.5;
              setPanPosition({ x: offsetX, y: offsetY });
            }
          }
          lastTouchRef.current = null;
        } else {
          lastTouchRef.current = { x: touch.clientX, y: touch.clientY, time: now };
          // Start panning if super zoomed
          if (isSuperZoom || manualZoom > 1.5) {
            wasInteractingRef.current = true;
            setIsPanning(true);
          }
        }
      } else {
        // Mobile: store touch for tap-to-type
        lastTouchRef.current = { x: touch.clientX, y: touch.clientY, time: now };
      }
    }
  }, [manualZoom, isSuperZoom, isMobile]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDistanceRef.current) {
      // Pinch zoom on the plate preview
      e.preventDefault();
      wasInteractingRef.current = true;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const scale = distance / pinchStartDistanceRef.current;
      const newZoom = Math.max(0.5, Math.min(5, pinchStartZoomRef.current * scale));
      setManualZoom(newZoom);
    } else if (e.touches.length === 1 && isMobile && lastTouchRef.current) {
      // On mobile, if user moves finger, cancel tap-to-type
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - lastTouchRef.current.x);
      const dy = Math.abs(touch.clientY - lastTouchRef.current.y);
      if (dx > 10 || dy > 10) {
        lastTouchRef.current = null;
      }
    }
  }, [isMobile]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const topElement = elements[0];

    const wasInteracting = wasInteractingRef.current;
    const wasPinching = pinchStartDistanceRef.current !== null;
    pinchStartDistanceRef.current = null;
    setIsPanning(false);

    // On mobile, handle tap-to-type only if:
    // - Not zoomed
    // - Not panning
    // - Not pinching
    // - Single tap (not moved much)
    // - No modals are open
    // Tap-to-type is now handled by the transparent overlay <input> on the floating plate.
    // No programmatic focus needed here.

    lastTouchRef.current = null;
    wasInteractingRef.current = false;
  }, [isMobile, manualZoom, isAnyModalOpen, mobileTypingMode]);

  // Prevent iOS from scrolling the document when the hidden keyboard input is focused
  useEffect(() => {
    if (!isMobile || !showMobileRegistrationInput) return;
    const lockScroll = () => { window.scrollTo(0, 0); };
    lockScroll();
    window.addEventListener('scroll', lockScroll, { passive: false });
    return () => window.removeEventListener('scroll', lockScroll);
  }, [isMobile, showMobileRegistrationInput]);

  // Track keyboard height so floating plate + print bar stay above the keyboard
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const handleViewportChange = () => {
      const kh = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      setKeyboardHeight(kh);
    };
    vv.addEventListener('resize', handleViewportChange);
    vv.addEventListener('scroll', handleViewportChange);
    return () => {
      vv.removeEventListener('resize', handleViewportChange);
      vv.removeEventListener('scroll', handleViewportChange);
    };
  }, [isMobile]);

  // Double click for super zoom (mouse) - disabled on mobile
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (isMobile) return; // Disable double tap zoom on mobile
    if (isSuperZoom) {
      // Reset zoom
      setManualZoom(1);
      setPanPosition({ x: 0, y: 0 });
      setIsSuperZoom(false);
    } else {
      // Super zoom to click location
      setManualZoom(3);
      setIsSuperZoom(true);
      if (mainPreviewRef.current) {
        const rect = mainPreviewRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const offsetX = (centerX - e.clientX) * 0.5;
        const offsetY = (centerY - e.clientY) * 0.5;
        setPanPosition({ x: offsetX, y: offsetY });
      }
    }
  }, [isSuperZoom]);

  // Mouse drag for panning when zoomed
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (manualZoom > 1.2) {
      setIsPanning(true);
      lastTouchRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    }
  }, [manualZoom]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning && lastTouchRef.current) {
      const dx = e.clientX - lastTouchRef.current.x;
      const dy = e.clientY - lastTouchRef.current.y;
      setPanPosition(p => ({ x: p.x + dx, y: p.y + dy }));
      lastTouchRef.current = { x: e.clientX, y: e.clientY, time: lastTouchRef.current.time };
    }
  }, [isPanning]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    lastTouchRef.current = null;
  }, []);

  // Reset pan when zoom returns to normal
  useEffect(() => {
    if (manualZoom <= 1) {
      setPanPosition({ x: 0, y: 0 });
      setIsSuperZoom(false);
    }
  }, [manualZoom]);

  const handleAutoFit = () => {
      const currentDims = dimensions[config.plateSize];
      if (!currentDims) return;
      const flagSpace = config.badge.enabled ? 44 : 0;
      const margins = (config.borderMargin * 2) + 12; 
      const availableWidth = currentDims.width - flagSpace - margins;
      const text = config.registration || 'REG';
      const charCount = text.length;
      if (charCount === 0) return;
      const spacingTotal = config.letterSpacing * (charCount - 1);
      const widthForChars = availableWidth - spacingTotal;
      const aspectRatio = 0.7; 
      let optimalSize = widthForChars / (charCount * aspectRatio);
      const maxHeight = currentDims.height - (config.borderMargin * 2) - 10;
      optimalSize = Math.min(optimalSize, maxHeight);
      optimalSize = Math.max(optimalSize, 20);
      setConfig({...config, mainFontSize: Math.floor(optimalSize)});
  };

  const saveProfile = (p: PrinterProfile) => {
      const exists = profiles.find(ep => ep.id === p.id);
      let updated;
      if(exists) { updated = profiles.map(ep => ep.id === p.id ? p : ep); } else { updated = [...profiles, p]; }
      setProfiles(updated);
      localStorage.setItem('ps_profiles', JSON.stringify(updated));
      
      // If this is the first/only profile, set it as default
      if(updated.length === 1) {
          updatePrintSettings({ ...printSettings, singleProfileId: p.id });
      } else if(!printSettings.singleProfileId) {
          // If no default is set, use the first profile
          updatePrintSettings({ ...printSettings, singleProfileId: updated[0].id });
      }
      
      setEditingProfile(null);
  };

  const deleteProfile = (id: string) => {
      const updated = profiles.filter(p => p.id !== id);
      setProfiles(updated);
      localStorage.setItem('ps_profiles', JSON.stringify(updated));
      
      // If the deleted profile was the default, set the first remaining profile as default
      if (printSettings.singleProfileId === id && updated.length > 0) {
          updatePrintSettings({ ...printSettings, singleProfileId: updated[0].id });
      } else if (updated.length === 1) {
          // If only one profile remains, set it as default
          updatePrintSettings({ ...printSettings, singleProfileId: updated[0].id });
      }
  };

  const updatePrintSettings = (s: PrintSettings) => {
      setPrintSettings(s);
      localStorage.setItem('ps_print_settings', JSON.stringify(s));
  };

  const refreshSystemPrinters = async () => {
      if (/Electron/.test(navigator.userAgent) && (window as any).require) {
          try {
              const { ipcRenderer } = (window as any).require('electron');
              console.log('[PRINTERS] Refreshing printer list...');
              const list = await ipcRenderer.invoke('get-printers');
              console.log('[PRINTERS] Refreshed printers:', list);
              setAvailableSystemPrinters(list || []);
          } catch(e) { 
              console.error('[PRINTERS] Failed to refresh printers:', e); 
              setAvailableSystemPrinters([]);
          }
      }
  };

  const createNewProfile = () => {
      if (!isPro && profiles.length >= 1) {
          setProModalErrors(['Multiple Printer Profiles']);
          setShowProModal(true);
          return;
      }
      const newProfile: PrinterProfile = {
          id: Date.now().toString(),
          name: profiles.length === 0 ? 'default' : 'New Printer',
          systemPrinterName: availableSystemPrinters.length > 0 ? availableSystemPrinters[0].name : '',
          offsetX: 0, offsetY: 0,
          lengthCalibration: 100,
          rotate: false, mirror: false,
          enableCropMarks: false,
          cropMarksOffset: 0,
          enableSideGuidelines: false,
          sideGuidelinesPosition: 'side',
          sideGuidelinesOffset: 2,
          dpi: 203,
          driverType: 'meena_lite',
          printerLanguage: 'TSPL',
          connectionType: 'bluetooth',
          pageWidth: 109,
          pageHeight: 520,
          paperType: 'continuous',
          gapEnabled: false,
          compress: false,
          cutAfterPrint: false
      };
      setProfiles(prev => {
          const updated = [...prev, newProfile];
          // If this is the first profile, set it as default
          if (prev.length === 0) {
              const newSettings = { ...printSettings, singleProfileId: newProfile.id };
              setPrintSettings(newSettings);
              localStorage.setItem('ps_print_settings', JSON.stringify(newSettings));
          }
          return updated;
      });
      setEditingProfile(newProfile);
  };

  // --- PRINTER SDK HELPERS ---
  const scanBluetooth = async () => {
    setIsScanningBluetooth(true);
    setBluetoothDevices([]);
    try {
      PrinterBridge.addDiscoveryListener('onDeviceFound', (device: any) => {
          setBluetoothDevices(prev => {
              if (prev.some(p => p.address === device.address)) return prev;
              return [...prev, device];
          });
      });
      await PrinterBridge.startPrinterDiscovery();
      
      // Auto-stop after 15s
      setTimeout(async () => {
          await PrinterBridge.stopPrinterDiscovery();
          setIsScanningBluetooth(false);
      }, 15000);

    } catch (e) {
      alert("Bluetooth Scan failed: " + e);
      setIsScanningBluetooth(false);
    }
  };

  const connectToAndroidPrinter = async (profile: PrinterProfile) => {
    // OKI printers connect internally during executePrintJob via OkiPrinterPlugin.
    // Do NOT route them through the thermal PrinterConnectionPlugin.
    if ((profile as any).driverType === 'oki_usb') return;

    if (!profile.connectionType) {
      alert("Please select a connection type (Bluetooth, USB, or WiFi)");
      return;
    }

    if (profile.connectionType === 'bluetooth' && !profile.androidAddress) {
        alert("Please select a Bluetooth device or enter MAC address");
        return;
    }

    setIsConnectingAndroid(true);
    setAndroidConnectionStatus('Connecting...');
    try {
      const result = await PrinterBridge.connectAndroidPrinter({
        name: profile.name,
        address: profile.androidAddress || '',
        type: profile.connectionType as any,
        driverType: profile.driverType
      });

      setAndroidConnectionStatus('Connected!');
      setTimeout(() => setAndroidConnectionStatus(''), 3000);

    } catch (e) {
      console.error(e);
      setAndroidConnectionStatus('Failed');
      const errorMessage =
        (e as any)?.message ||
        (typeof e === 'string' ? e : '') ||
        'Unknown error';

      const hint =
        profile.connectionType === 'usb'
          ? 'USB: Make sure the printer is plugged in and accept the USB permission prompt.'
          : profile.connectionType === 'wifi'
            ? 'WiFi: Make sure the IP is correct and the printer is reachable on the network.'
            : 'Bluetooth: Make sure the printer is on and in range.';

      const driverNote = profile.driverType === 'godex' ? ' (Godex)'
        : profile.driverType === 'tsc' ? ' (TSC)'
        : profile.driverType === 'citizen' ? ' (Citizen)'
        : '';
      alert(`Connection failed${driverNote}.\n\n${errorMessage}\n\n${hint}`);
    } finally {
      setIsConnectingAndroid(false);
    }
  };

  const sendGodexRawCommand = async () => {
    if (!PrinterBridge.isAndroid) {
      setGodexRawResult('Raw commands are only supported on Android.');
      return;
    }
    if (!editingProfile || editingProfile.driverType !== 'godex') {
      setGodexRawResult('Select a Godex printer profile first.');
      return;
    }
    setGodexRawSending(true);
    setGodexRawResult(null);
    try {
      const res = await PrinterBridge.sendAndroidRawCommand({ command: godexRawCommand, driverType: 'godex' });
      setGodexRawResult(res?.message || 'Command sent');
    } catch (e: any) {
      setGodexRawResult(e?.message || 'Failed to send command');
    } finally {
      setGodexRawSending(false);
    }
  };

  const executePrintJob = async (profileId: string, overridePlateType?: PlateType, silent: boolean = false, cachedPrintData?: { type: 'image' | 'pdf'; data: string; pageWidth: number; pageHeight: number; }) => {
      // DEMO MODE: Block actual printing, show message
      if (demoMode) {
          if (!silent) {
              alert('Demo Mode: Printing is disabled.\n\nIn the full version, you can print directly to compatible printers.\n\nVisit the pricing page to get the full version!');
          }
          return;
      }
      console.time('[PRINT] Total execution time');
      console.log('[PRINT] Starting print job at:', new Date().toISOString());
      
      // Memory monitoring in development
      let memBefore: any = null;
      if (process.env.NODE_ENV === 'development' && (performance as any).memory) {
          memBefore = (performance as any).memory;
          console.log('[PRINT] Memory before:', {
              used: Math.round(memBefore.usedJSHeapSize / 1048576) + 'MB',
              total: Math.round(memBefore.totalJSHeapSize / 1048576) + 'MB',
              limit: Math.round(memBefore.jsHeapSizeLimit / 1048576) + 'MB'
          });
      }
      
      // On mobile, create a default profile if none exists
      let profile = profiles.find(p => p.id === profileId);
      if (PrinterBridge.isMobile && !profile) {
          // Create a default Android profile
          profile = {
              id: 'android-default',
              name: 'Android Printer',
              systemPrinterName: '',
              connectionType: 'bluetooth' as const,
              printerLanguage: 'TSPL' as const,
              offsetX: 0,
              offsetY: 0,
              lengthCalibration: 100,
              rotate: false,
              mirror: false,
              enableCropMarks: false,
              cropMarksOffset: 0,
              pageWidth: 109,
              pageHeight: 520,
              paperType: 'continuous',
              gapEnabled: false,
              compress: false,
              cutAfterPrint: false
          } as PrinterProfile;
      }
      if (!profile) { alert("Printer Profile not found. Please check settings."); return; }

      console.time('[PRINT] Setup phase');
      if (!silent) {
          setIsPrinting(true);
          setIsPreparingPrint(true);
          setPrintProgress(10);
      }

      setPrintJobConfig({ profile: profile, plateType: overridePlateType || config.type });
      if (!silent) setPrintProgress(30);

      // Minimal delay for DOM update - reduced from 400ms to 50ms
      await new Promise(r => setTimeout(r, 50));
      if (!silent) setPrintProgress(60);

      const isElectron = /Electron/.test(navigator.userAgent);
      const ipcRenderer = isElectron ? (window as any).require('electron').ipcRenderer : null;
      const dims = dimensions[config.plateSize];
      const lipHeight = config.lippedPlate ? config.lipHeight : 0;
      
      const calibrationScale = profile.lengthCalibrationEnabled ? (profile.lengthCalibration || 100) / 100 : 1;
      const cropOffset = profile.enableCropMarks ? (profile.cropMarksOffset || 0) : 0;
      
      // Desktop printing: dimensions need to match the page orientation
      // Rotation OFF: Horizontal/landscape (width > height) - normal plate orientation
      // Rotation ON: Vertical/portrait (height > width) - rotated 90 degrees
      // Standard plate: width (520mm) > height (111mm)
      // When rotated: swap dimensions so height > width
      const rawWidth = (!profile.rotate ? dims.width : dims.height) + (cropOffset * 2);
      const rawHeight = (!profile.rotate ? dims.height : dims.width) + lipHeight + (cropOffset * 2);
      
      // Length calibration stretches the WIDTH of the plate
      // When NOT rotated: the stretched width is the page WIDTH, so apply calibration to width (horizontal/landscape)
      // When rotated: the stretched width becomes the page HEIGHT, so apply calibration to height (vertical/portrait)
      const isRotated: boolean = profile.rotate || false;
      const pWidth = !isRotated 
          ? rawWidth * calibrationScale  // Not rotated: width gets calibration (horizontal/landscape)
          : rawWidth;  // Rotated: width doesn't get calibration (it's the original height, now page width)
      const pHeight = !isRotated 
          ? rawHeight  // Not rotated: height doesn't get calibration (vertical)
          : rawHeight * calibrationScale;  // Rotated: height gets calibration (it's the original width, now page height) 

      try {
          if (PrinterBridge.isMobile) {
            console.timeEnd('[PRINT] Setup phase');
            console.time('[PRINT] Android print flow');
            if (!silent) setPrintProgress(70);
            
            // Use dedicated Android print capture element (always white background)
            const exportElement = document.getElementById('plate-export-print');
            if (!exportElement) {
              throw new Error('Export element not found. Please refresh and try again.');
            }
            
            if (USE_FAST_PATH) {
              // ===== FAST PATH: Direct image printing =====
              let base64Image: string;
              let finalPageWidth: number;
              let finalPageHeight: number;
              
              // Check if we have cached print data
              if (cachedPrintData && cachedPrintData.type === 'image') {
                console.log('[PRINT] Using cached image data');
                base64Image = cachedPrintData.data;
                finalPageWidth = cachedPrintData.pageWidth;
                finalPageHeight = cachedPrintData.pageHeight;
              } else {
                // Generate image if not cached
                console.time('[PRINT] Font loading (fast)');
                await document.fonts.ready;
                console.timeEnd('[PRINT] Font loading (fast)');
                
                console.time('[PRINT] Initial delay (fast)');
                await new Promise(r => setTimeout(r, 100)); // 80% reduction: 500ms -> 100ms
                console.timeEnd('[PRINT] Initial delay (fast)');
                
                console.time('[PRINT] Element setup (fast)');
                exportElement.style.width = `${dims.width}px`;
                exportElement.style.height = `${dims.height + lipHeight}px`;
                await new Promise(r => setTimeout(r, 40)); // 80% reduction: 200ms -> 40ms
                console.timeEnd('[PRINT] Element setup (fast)');
                
                console.time('[PRINT] Image capture (fast)');
                // Use html-to-image directly to get base64 image
                const { toPng } = await import('html-to-image');
                
                // Pre-load fonts (minimal wait)
                // Import the function dynamically
                const pdfExportModule = await import('./utils/pdfExport');
                const fontCSS = await pdfExportModule.preloadFontsAsBase64();
                
                // Capture as PNG directly
                const dataUrl = await toPng(exportElement, {
                  pixelRatio: (profile.dpi || 203) / 25.4, // DPI-based pixel ratio
                  backgroundColor: '#ffffff',
                  width: dims.width,
                  height: dims.height + lipHeight,
                  cacheBust: true,
                  fontEmbedCSS: fontCSS || undefined,
                  quality: 1.0,
                });
                
                // Extract base64 (remove data:image/png;base64, prefix)
                base64Image = dataUrl.split(',')[1];
                console.timeEnd('[PRINT] Image capture (fast)');
                
                // Use PHYSICAL PAGE SIZE (not artwork size) for printer (Fast Path - Image)
                // Rotation OFF: Horizontal/landscape (width > height) - normal plate orientation
                // Rotation ON: Vertical/portrait (height > width) - rotated 90 degrees

                // FLEXI MODE: Use artwork dimensions instead of preset page size
                // This tells the printer to only dispense the length of the actual artwork
                if (profile.mediaSize === 'FLEXIBLE' || (profile.driverType === 'citizen' && profile.citizenSensorType === 'continuous')) {
                    console.log('[PRINT] Flexi mode (fast path): Using artwork dimensions for page size');
                    if (!profile.rotate) {
                        finalPageWidth = dims.width * calibrationScale;
                        finalPageHeight = dims.height + lipHeight;
                    } else {
                        finalPageWidth = dims.height + lipHeight;
                        finalPageHeight = dims.width * calibrationScale;
                    }
                } else if (profile.pageWidth && profile.pageHeight) {
                    // SIZE = profile dimensions as entered, no swap regardless of rotation
                    finalPageWidth = profile.pageWidth;
                    finalPageHeight = profile.pageHeight;
                } else {
                    // Fallback: use artwork dimensions
                    if (!profile.rotate) {
                        finalPageWidth = dims.width * calibrationScale;
                        finalPageHeight = dims.height + lipHeight;
                    } else {
                        finalPageWidth = dims.height + lipHeight;
                        finalPageHeight = dims.width * calibrationScale;
                    }
                }
              }

              console.time('[PRINT] Send image to printer (native)');
              const result = await PrinterBridge.printImage({
                base64Image,
                width: finalPageWidth,  // Physical paper width
                height: finalPageHeight, // Physical paper height
                              printerLanguage: profile.printerLanguage || 'TSPL',
                              connectionType: profile.connectionType as any,
                              androidAddress: (profile as any).androidAddress,
                              androidPort: (profile as any).androidPort,
                paperType: profile.paperType || 'label',
                gapEnabled: profile.gapEnabled || false,
                compress: profile.compress || false,
                cutAfterPrint: profile.cutAfterPrint || false,
                dpi: profile.dpi || 203,
                rotate: profile.rotate || false,
                mirror: profile.mirror || false,
                feedEnabled: profile.feedEnabled || false,
                feedAmount: profile.feedAmount || 5
              });
              console.timeEnd('[PRINT] Send image to printer (native)');
              console.timeEnd('[PRINT] Android print flow');
              
              if (!result || !result.success) {
                throw new Error(result?.message || 'Print failed - no response from printer');
              }
            } else {
              // ===== SLOW PATH: Original PDF flow =====
              let base64Pdf: string;
              let finalPageWidth: number;
              let finalPageHeight: number;
              
              // Check if we have cached print data
              if (cachedPrintData && cachedPrintData.type === 'pdf') {
                console.log('[PRINT] Using cached PDF data');
                base64Pdf = cachedPrintData.data;
                // For FLEXIBLE mode, override cached dimensions with actual artwork dimensions
                if (profile.mediaSize === 'FLEXIBLE' || (profile.driverType === 'citizen' && profile.citizenSensorType === 'continuous')) {
                    console.log('[PRINT] Flexi mode (cached): Using artwork dimensions instead of cached page size');
                    if (!profile.rotate) {
                        finalPageWidth = dims.width * calibrationScale;
                        finalPageHeight = dims.height + lipHeight;
                    } else {
                        finalPageWidth = dims.height + lipHeight;
                        finalPageHeight = dims.width * calibrationScale;
                    }
                } else {
                    finalPageWidth = cachedPrintData.pageWidth;
                    finalPageHeight = cachedPrintData.pageHeight;
                }
              } else {
                // Generate PDF if not cached
                console.time('[PRINT] Font loading');
                await document.fonts.ready;
                console.timeEnd('[PRINT] Font loading');
                
                console.time('[PRINT] Initial delay');
                // Reduced delay - DOM is already ready
                await new Promise(r => setTimeout(r, 50));
                console.timeEnd('[PRINT] Initial delay');
                
                console.time('[PRINT] Element setup');
                exportElement.style.width = `${dims.width}px`;
                exportElement.style.height = `${dims.height + lipHeight}px`;
                // Reduced delay - minimal time needed for layout
                await new Promise(r => setTimeout(r, 50));
                console.timeEnd('[PRINT] Element setup');
                
                console.time('[PRINT] Capacitor import');
                const { Filesystem, Directory } = await import('@capacitor/filesystem');
                console.timeEnd('[PRINT] Capacitor import');
                
                console.time('[PRINT] PDF generation');
                // Calculate artwork dimensions (with calibration)
                // (calibrationScale already declared above)
                
                // Calculate artwork dimensions (these will be rotated in the PDF generation)
                let artworkWidth = dims.width * calibrationScale;
                let artworkHeight = (dims.height + lipHeight);
                
                // Physical page size should be stretched by calibration to match artwork
                // This ensures artwork doesn't run off the page when offsets are applied
                // When rotated: calibration applies to the dimension that becomes height (original width)
                // When not rotated: calibration applies to width (the long dimension)
                let physicalPageWidth: number;
                let physicalPageHeight: number;

                // For OKI laser printers, use selected paper size (unless FLEXI)
                // This allows crop marks and guidelines when paper is larger than artwork
                if (profile.driverType === 'oki_usb' || profile.connectionType === 'usb_oki') {
                    const okiPaperSize = profile.okiPaperSize || 'FLEXI';
                    const artworkWidth = dims.width * calibrationScale;
                    const artworkHeight = dims.height + lipHeight;

                    if (okiPaperSize === 'FLEXI') {
                        // FLEXI mode: PDF matches artwork size
                        if (profile.rotate) {
                            physicalPageWidth = artworkHeight;
                            physicalPageHeight = artworkWidth;
                        } else {
                            physicalPageWidth = artworkWidth;
                            physicalPageHeight = artworkHeight;
                        }
                    } else if (okiPaperSize === 'CUSTOM' && profile.okiCustomPaperWidth && profile.okiCustomPaperLength) {
                        // Custom paper size
                        if (profile.rotate) {
                            physicalPageWidth = profile.okiCustomPaperLength;
                            physicalPageHeight = profile.okiCustomPaperWidth;
                        } else {
                            physicalPageWidth = profile.okiCustomPaperWidth;
                            physicalPageHeight = profile.okiCustomPaperLength;
                        }
                    } else {
                        // Use preset paper size from OKI_PAPER_SIZES
                        const okiSize = OKI_PAPER_SIZES[okiPaperSize];
                        if (okiSize && okiSize.width > 0) {
                            if (profile.rotate) {
                                physicalPageWidth = okiSize.length;
                                physicalPageHeight = okiSize.width;
                            } else {
                                physicalPageWidth = okiSize.width;
                                physicalPageHeight = okiSize.length;
                            }
                        } else {
                            // Fallback to artwork size
                            if (profile.rotate) {
                                physicalPageWidth = artworkHeight;
                                physicalPageHeight = artworkWidth;
                            } else {
                                physicalPageWidth = artworkWidth;
                                physicalPageHeight = artworkHeight;
                            }
                        }
                    }
                } else if (profile.mediaSize === 'FLEXIBLE' || (profile.driverType === 'citizen' && profile.citizenSensorType === 'continuous')) {
                    // FLEXI MODE: Height = artwork length (dispense only what's needed).
                    // Width = roll width (NOT artwork width) so side guidelines and crop marks have whitespace.
                    console.log('[PRINT] Flexi mode: Using roll width for page width, artwork length for page height');
                    const flexi_rollWidth = (profile.driverType === 'citizen' ? (profile.citizenRollWidth || null) : null)
                                         || profile.pageWidth || null;
                    const flexi_cropOffset = profile.enableCropMarks ? (profile.cropMarksOffset || 5) : 0;
                    if (profile.rotate) {
                        // Rotated portrait: cross-feed = roll width, feed = artwork long side
                        physicalPageWidth = flexi_rollWidth ?? (dims.height + lipHeight);
                        physicalPageHeight = dims.width * calibrationScale + (flexi_cropOffset * 2);
                    } else {
                        // Not rotated landscape: cross-feed = roll width (or artwork width if wider), feed = artwork short side
                        physicalPageWidth = flexi_rollWidth ? Math.max(flexi_rollWidth, dims.width * calibrationScale) : (dims.width * calibrationScale);
                        physicalPageHeight = dims.height + lipHeight + (flexi_cropOffset * 2);
                    }
                } else if (profile.pageWidth && profile.pageHeight) {
                    // Thermal printers: PDF page must match artwork orientation so Java scaleBitmap doesn't distort.
                    // SIZE command (profile.pageWidth/Height) is sent separately and never swapped.
                    // rotate=OFF: artwork is landscape (plate as seen on screen)
                    // rotate=ON:  artwork is portrait (canvas rotated 90°)
                    if (profile.rotate) {
                        physicalPageWidth = dims.height + lipHeight;      // portrait width  = plate height
                        physicalPageHeight = dims.width * calibrationScale; // portrait height = plate width (calibrated)
                    } else {
                        physicalPageWidth = dims.width * calibrationScale;  // landscape width  = plate width (calibrated)
                        physicalPageHeight = dims.height + lipHeight;       // landscape height = plate height
                    }
                } else {
                    // Fallback: use artwork dimensions with calibration
                    if (profile.rotate) {
                        physicalPageWidth = dims.height + lipHeight;
                        physicalPageHeight = dims.width * calibrationScale;
                    } else {
                        physicalPageWidth = dims.width * calibrationScale;
                        physicalPageHeight = dims.height + lipHeight;
                    }
                }
                
                // Use okiResolution for OKI printers, otherwise profile.dpi (thermal default 203)
                const pdfDpi = (profile.driverType === 'oki_usb' || profile.connectionType === 'usb_oki')
                  ? (profile.okiResolution || 600)
                  : (profile.dpi || 203);

                const pdfBlob = await generatePlatePDFBlob(
                  'plate-export-print',
                  dims.width,
                  dims.height + lipHeight,
                  profile.rotate || false,
                  pdfDpi,
                  {
                    offsetX: profile.offsetX || 0,
                    offsetY: profile.offsetY || 0,
                    lengthCalibration: profile.lengthCalibration || 100,
                    rotate: profile.rotate || false,
                    mirror: profile.mirror || false,
                    enableCropMarks: profile.enableCropMarks || false,
                    cropMarksOffset: profile.cropMarksOffset || 5,
                    enableSideGuidelines: profile.enableSideGuidelines || false,
                    sideGuidelinesPosition: profile.sideGuidelinesPosition || 'side',
                    sideGuidelinesOffset: profile.sideGuidelinesOffset || 0,
                    topAligned: config.lippedPlate, // Position at top when lip is present
                    mediaSize: profile.mediaSize || 'FIXED' // FLEXIBLE = dispense artwork length only, FIXED = use page size settings
                  },
                  physicalPageWidth, // Physical paper width
                  physicalPageHeight  // Physical paper height
                );
                console.timeEnd('[PRINT] PDF generation');
                
                console.log('Android: PDF blob generated, size:', pdfBlob.size, 'bytes', 'rotated:', profile.rotate || false);
                
                if (pdfBlob.size === 0) {
                  throw new Error('Generated PDF is empty');
                }
                
                // Save PDF to disk only if setting is enabled (mobile only)
                if (pdfExportSettings.savePrintPdfs && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
                  console.time('[PRINT] Save PDF to disk');
                  try {
                    const base64ForSave = await new Promise<string>((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const result = reader.result as string;
                        resolve(result.split(',')[1]);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(pdfBlob);
                    });
                    
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const filename = `RegIQ_Print_${timestamp}.pdf`;
                    
                    await Filesystem.writeFile({
                      path: `RegIQ/${filename}`,
                      data: base64ForSave,
                      directory: Directory.Documents,
                      recursive: true
                    });
                    
                    console.log('PDF saved to Documents/RegIQ/' + filename);
                  } catch (saveError) {
                    console.warn('Failed to save PDF before printing:', saveError);
                  }
                  console.timeEnd('[PRINT] Save PDF to disk');
                }
                
                console.time('[PRINT] Convert PDF to base64');
                const reader = new FileReader();
                try {
                  const base64Promise = new Promise<string>((resolve, reject) => {
                    reader.onloadend = () => {
                      const base64 = reader.result as string;
                      const cleanBase64 = base64.split(',')[1];
                      console.log('Android: Base64 PDF length:', cleanBase64.length);
                      if (!cleanBase64 || cleanBase64.length < 100) {
                        reject(new Error('Base64 PDF data is too small or invalid'));
                        return;
                      }
                      resolve(cleanBase64);
                    };
                    reader.onerror = (e) => reject(new Error('Failed to read PDF blob'));
                  });
                  reader.readAsDataURL(pdfBlob);
                  base64Pdf = await base64Promise;
                } finally {
                  // Clean up FileReader
                  if (reader.readyState === FileReader.LOADING) {
                    reader.abort();
                  }
                }
                console.timeEnd('[PRINT] Convert PDF to base64');

                if (!silent) setPrintProgress(85);
                
                // Use PHYSICAL PAGE SIZE for printer (Slow Path - PDF)
                // pageWidth = physical label width (perpendicular to feed direction / print head width)
                // pageHeight = physical label height (feed direction / label length)
                if (profile.mediaSize === 'FLEXIBLE' || (profile.driverType === 'citizen' && profile.citizenSensorType === 'continuous')) {
                    // FLEXI MODE: TSPL SIZE = roll width x artwork length (same logic as physicalPage above)
                    console.log('[PRINT] Flexi mode (slow path): Using roll width for TSPL SIZE width, artwork length for height');
                    const flexi_rollWidth2 = (profile.driverType === 'citizen' ? (profile.citizenRollWidth || null) : null)
                                          || profile.pageWidth || null;
                    const flexi_cropOffset2 = profile.enableCropMarks ? (profile.cropMarksOffset || 5) : 0;
                    if (!profile.rotate) {
                        finalPageWidth = flexi_rollWidth2 ? Math.max(flexi_rollWidth2, dims.width * calibrationScale) : (dims.width * calibrationScale);
                        finalPageHeight = dims.height + lipHeight + (flexi_cropOffset2 * 2);
                    } else {
                        finalPageWidth = flexi_rollWidth2 ?? (dims.height + lipHeight);
                        finalPageHeight = dims.width * calibrationScale + (flexi_cropOffset2 * 2);
                    }
                } else if (profile.pageWidth && profile.pageHeight) {
                    // Use profile page size directly as physical printer dimensions.
                    // pageWidth/pageHeight are the ACTUAL physical label dimensions (e.g. 109mm roll width, 520mm label length).
                    // Do NOT swap for rotation - the user has already specified the correct physical dimensions.
                    // Rotation affects the artwork orientation inside the PDF, not the label dimensions for the SIZE command.
                    finalPageWidth = profile.pageWidth;
                    finalPageHeight = profile.pageHeight;
                } else {
                    // Fallback: calculate from artwork dimensions
                    if (!profile.rotate) {
                        finalPageWidth = dims.width * calibrationScale;
                        finalPageHeight = dims.height + lipHeight;
                    } else {
                        finalPageWidth = dims.height + lipHeight;
                        finalPageHeight = dims.width * calibrationScale;
                    }
                }
              }
              
              // Check if using OKI laser printer (USB or Network)
              if (profile.driverType === 'oki_usb' || profile.connectionType === 'usb_oki') {
                console.log('OKI Laser: Sending PDF directly...', { pdfSize: base64Pdf.length, connectionType: profile.connectionType });
                console.time('[PRINT] OKI print flow');

                // Connect to OKI printer first (USB or Network)
                try {
                  if (profile.connectionType === 'wifi' && profile.androidAddress) {
                    console.log('OKI: Connecting via network to', profile.androidAddress);
                    await PrinterBridge.connectOkiPrinterNetwork(profile.androidAddress);
                  } else {
                    console.log('OKI: Connecting via USB');
                    await PrinterBridge.connectOkiPrinter();
                  }
                } catch (connectErr) {
                  console.log('OKI connection attempt:', connectErr);
                  // May already be connected, continue
                }

                // Auto-detect paper size based on artwork dimensions
                // Standard UK plates are larger than A4, so we need CUSTOM or a preset
                let pSize = profile.okiPaperSize;
                let cWidth = profile.okiCustomPaperWidth;
                let cLength = profile.okiCustomPaperLength;

                // Log what we're working with
                console.log('OKI Paper Size:', { profileSize: pSize, artworkW: finalPageWidth, artworkH: finalPageHeight });

                // If FLEXI mode, pass 'FLEXI' to Java - it will read PDF dimensions directly
                if (pSize === 'FLEXI') {
                    // Don't convert to CUSTOM - let Java read actual PDF dimensions
                    console.log('OKI: FLEXI mode - Java will read PDF dimensions directly');
                }
                // If CUSTOM or not set, auto-detect based on artwork size
                else if (pSize === 'CUSTOM' || !pSize) {
                    // Check if artwork fits a preset, otherwise use CUSTOM
                    // Standard plates: OBLONG=111x520, SHORT_7=111x460, etc.
                    if (Math.abs(finalPageWidth - 520) < 5 && Math.abs(finalPageHeight - 111) < 5) {
                        pSize = 'OBLONG';
                        console.log('OKI: Auto-detected OBLONG (520x111)');
                    } else if (Math.abs(finalPageWidth - 460) < 5 && Math.abs(finalPageHeight - 111) < 5) {
                        // Short 7 plate - use CUSTOM with exact dimensions
                        pSize = 'CUSTOM';
                        cWidth = finalPageWidth;
                        cLength = finalPageHeight;
                        console.log('OKI: Short plate detected - using CUSTOM:', cWidth, 'x', cLength);
                    } else if (finalPageHeight > 300 || finalPageWidth > 220) {
                        // Larger than A4, use CUSTOM
                        pSize = 'CUSTOM';
                        if (!cWidth) cWidth = finalPageWidth;
                        if (!cLength) cLength = finalPageHeight;
                        console.log('OKI: Large artwork - using CUSTOM:', cWidth, 'x', cLength);
                    } else if (!pSize) {
                        // Small artwork, default to OBLONG
                        pSize = 'OBLONG';
                        console.log('OKI: No size set, defaulting to OBLONG');
                    }
                }

                // Send PDF to OKI with PJL variables
                // Use profile offset values to adjust print position (can use negative Y to compensate for hardware margin)
                const result = await PrinterBridge.printToOki(base64Pdf, {
                  mediaSource: profile.okiMediaSource,
                  mediaWeight: profile.okiMediaWeight,
                  mediaType: profile.okiMediaType,
                  paperSize: pSize,
                  customPaperWidth: cWidth,
                  customPaperLength: cLength,
                  resolution: profile.okiResolution || 1200,
                  economode: profile.okiEconomode ?? false,
                  density: profile.okiDensity || 3,
                  orientation: profile.okiOrientation || 'PORTRAIT',
                  xOffset: profile.okiOffsetX || 0,
                  yOffset: profile.okiOffsetY || 0,
                  customPjlHeader: profile.okiCustomPjlHeader
                });
                console.timeEnd('[PRINT] OKI print flow');
                console.log('OKI: Print result:', result);

                if (!result || !result.success) {
                  throw new Error(result?.message || 'OKI print failed');
                }
              } else {
                // Thermal printer path
                console.log('Android: Sending to printer...', {
                  pageWidth: finalPageWidth,  // Physical paper width
                  pageHeight: finalPageHeight, // Physical paper height
                  rotated: profile.rotate,
                  language: profile.printerLanguage || 'TSPL'
                });

                console.time('[PRINT] Send to printer (native)');
                const result = await PrinterBridge.printPlate({
                  base64Pdf,
                  width: finalPageWidth,  // Physical paper width
                  height: finalPageHeight, // Physical paper height
                  driverType: (profile as any).driverType,
                  godexPdfMode: (profile as any).godexPdfMode || 'ezpl_hex',
                  printerLanguage: profile.printerLanguage || 'TSPL',
                  connectionType: profile.connectionType,
                  androidAddress: (profile as any).androidAddress,
                  androidPort: (profile as any).androidPort,
                  paperType: profile.citizenSensorType === 'continuous' ? 'continuous' :
                             profile.citizenSensorType === 'blackmark' ? 'black_label' :
                             profile.paperType || 'label',
                  gapEnabled: profile.gapEnabled || false,
                  compress: profile.compress || false,
                  cutAfterPrint: profile.cutAfterPrint || false,
                  dpi: profile.dpi || 203,
                  feedEnabled: profile.feedEnabled || false,
                  feedAmount: profile.feedAmount || 5,
                  xOffset: pdfExportSettings.printerXOffset,
                  yOffset: pdfExportSettings.printerYOffset,
                  // Citizen-specific settings
                  citizenSpeed: profile.citizenSpeed,
                  citizenDarkness: profile.citizenDarkness,
                  citizenAutoCut: profile.citizenAutoCut ?? true,
                  citizenPrintMethod: profile.citizenPrintMethod || 'direct',
                  citizenFeedBefore: profile.citizenFeedBefore || 0,
                  citizenFeedAfter: profile.citizenFeedAfter || 0,
                  citizenRollWidth: profile.citizenRollWidth || 80
                });

                console.timeEnd('[PRINT] Send to printer (native)');
                console.timeEnd('[PRINT] Android print flow');
                console.log('Android: Print result:', result);

                if (!result || !result.success) {
                  throw new Error(result?.message || 'Print failed - no response from printer');
                }
              }
            }
          } else if (ipcRenderer) {
            await ipcRenderer.invoke('print-plate', { 
                silent: true, 
                printerName: profile.systemPrinterName,
                width: pWidth, height: pHeight
            });
          } else {
            // Web fallback
            window.print();
          }
          
          // Track print count
          const newPrintCount = printCount + 1;
          setPrintCount(newPrintCount);
          localStorage.setItem('ps_print_count', newPrintCount.toString());
          
          // Sync print count to server (daily)
          if (currentUser?.license) {
              LicenseManager.syncPrintCount(currentUser.license, newPrintCount, customLicenseUrl).catch(() => {
                  // Silent fail - will retry next day
              });
          }
          
          if (!silent) setPrintProgress(90);
          // Toast messages removed - queue indicator shows status instead
          // Reduced delay - no need to wait after successful print
          await new Promise(r => setTimeout(r, 100));
      } catch (e) {
          alert("Print failed: " + e);
          console.timeEnd('[PRINT] Total execution time');
          console.log('[PRINT] Print job failed at:', new Date().toISOString());
      } finally {
          if (!silent) {
              setPrintProgress(100);
              // Minimal delay for UI update
              await new Promise(r => setTimeout(r, 50));
              setPrintJobConfig(null);
              setIsPrinting(false);
              setIsPreparingPrint(false);
              setPrintProgress(0);
          }
          console.timeEnd('[PRINT] Total execution time');
          console.log('[PRINT] Print job completed at:', new Date().toISOString());
          
          // Memory monitoring in development
          if (process.env.NODE_ENV === 'development' && (performance as any).memory && memBefore) {
              const memAfter = (performance as any).memory;
              const delta = memAfter.usedJSHeapSize - memBefore.usedJSHeapSize;
              console.log('[PRINT] Memory after:', {
                  used: Math.round(memAfter.usedJSHeapSize / 1048576) + 'MB',
                  total: Math.round(memAfter.totalJSHeapSize / 1048576) + 'MB',
                  limit: Math.round(memAfter.jsHeapSizeLimit / 1048576) + 'MB',
                  delta: Math.round(delta / 1048576) + 'MB' + (delta > 0 ? ' (increased)' : ' (decreased)')
              });
          }
      }
  };

  // Print Queue Processor - runs jobs in background without blocking UI
  useEffect(() => {
      const processQueue = async () => {
          if (isProcessingQueue || printQueue.length === 0) return;

          // Find next job that is queued and not paused
          const nextJob = printQueue.find(job => job.status === 'queued' && !job.paused);
          if (!nextJob) return;

          // On mobile, check connection and auto-connect if needed before processing
          if (PrinterBridge.isMobile) {
              const profileForJob = profiles.find(p => p.id === nextJob.profileId) || null;
              const driverType: string = (profileForJob as any)?.driverType || '';
              const isOki = driverType === 'oki_usb';
              const isCitizen = driverType === 'citizen';

              // OKI and Citizen manage their own connections during printing
              if (!isOki && !isCitizen) {
                  try {
                      const needsGodex = driverType === 'godex';
                      const status = await PrinterBridge.getAndroidConnectionStatus();
                      const needsConnect = !status.connected || (needsGodex && status.driverType !== 'godex');

                      if (needsConnect) {
                          if (!profileForJob) {
                              setPrintQueue(prev => prev.map(job =>
                                  job.id === nextJob.id ? { ...job, status: 'failed', message: 'Printer profile not found.' } : job
                              ));
                              setTimeout(() => setPrintQueue(prev => prev.filter(job => job.id !== nextJob.id)), 5000);
                              return;
                          }

                          const connType = profileForJob.connectionType;
                          const address = (profileForJob as any).androidAddress || '';
                          const canAutoConnect = connType === 'usb' || !!address;

                          if (!canAutoConnect) {
                              setPrintQueue(prev => prev.map(job =>
                                  job.id === nextJob.id ? { ...job, status: 'failed', message: 'Printer not connected. Please configure the connection in Settings.' } : job
                              ));
                              setTimeout(() => setPrintQueue(prev => prev.filter(job => job.id !== nextJob.id)), 5000);
                              return;
                          }

                          console.log('[PRINT] Not connected — auto-connecting to', profileForJob.name);
                          try {
                              await PrinterBridge.connectAndroidPrinter({
                                  name: profileForJob.name,
                                  address,
                                  type: connType as any,
                                  driverType: driverType as any
                              });
                              console.log('[PRINT] Auto-connect succeeded');
                          } catch (connectErr: any) {
                              const msg = connectErr?.message || 'Connection failed';
                              setPrintQueue(prev => prev.map(job =>
                                  job.id === nextJob.id ? { ...job, status: 'failed', message: 'Could not connect to printer: ' + msg } : job
                              ));
                              setTimeout(() => setPrintQueue(prev => prev.filter(job => job.id !== nextJob.id)), 5000);
                              return;
                          }
                      }
                  } catch (e) {
                      console.error('Connection check failed during queue processing', e);
                      // Proceed — let the print attempt surface any real error
                  }
              }
          }

          setIsProcessingQueue(true);

          try {
              // Update job to processing
              setPrintQueue(prev => prev.map(job =>
                  job.id === nextJob.id ? { ...job, status: 'processing', progress: 10, message: 'Preparing print...' } : job
              ));

              // Execute the print job in silent mode (runs in background, doesn't block UI)
              // Pass cached print data if available to avoid regenerating PDF/image
              await executePrintJob(nextJob.profileId, nextJob.overridePlateType, true, nextJob.cachedPrintData);
              
              // Mark as completed
              setPrintQueue(prev => prev.map(job => 
                  job.id === nextJob.id ? { ...job, status: 'completed', progress: 100, message: 'Print job sent successfully' } : job
              ));
              
              // Add to print history
              if (nextJob.registration && nextJob.registration !== 'UNKNOWN') {
                  setPrintHistory(prev => {
                      const newHistory = [{
                          registration: nextJob.registration,
                          timestamp: Date.now(),
                          plateType: nextJob.overridePlateType || config.type
                      }, ...prev].slice(0, 20); // Keep only last 20
                      return newHistory;
                  });
              }
              
              // Remove completed job after 2 seconds
              setTimeout(() => {
                  setPrintQueue(prev => prev.filter(job => job.id !== nextJob.id));
              }, 2000);
              
          } catch (error: any) {
              // Mark as failed
              setPrintQueue(prev => prev.map(job => 
                  job.id === nextJob.id ? { ...job, status: 'failed', message: error.message || 'Print failed' } : job
              ));
              
              // Remove failed job after 5 seconds
              setTimeout(() => {
                  setPrintQueue(prev => prev.filter(job => job.id !== nextJob.id));
              }, 5000);
          } finally {
              setIsProcessingQueue(false);
              // Clear printJobConfig after job completes to free memory
              setPrintJobConfig(null);
          }
      };
      
      // Process queue when it changes
      processQueue();
  }, [printQueue, isProcessingQueue, profiles]);

  const generatePrintPreview = async () => {
      setIsGeneratingPreview(true);
      setPrintPreviewImage(null);
      setShowPrintPreview(false);
      
      try {
          // Ensure print job config is set up
          const profileIdToUse = profiles.length === 1 
              ? profiles[0].id 
              : (printSettings.singleProfileId || (profiles.length > 0 ? profiles[0].id : ''));
          
          if (!profileIdToUse) {
              alert('No printer profile selected');
              setIsGeneratingPreview(false);
              return;
          }

          const profile = profiles.find(p => p.id === profileIdToUse);
          if (!profile) {
              alert('Printer profile not found');
              setIsGeneratingPreview(false);
              return;
          }

          // Set up print job config temporarily
          setPrintJobConfig({ profile: profile, plateType: config.type });
          
          // Reduced delay - minimal time needed for DOM update
          await new Promise(r => setTimeout(r, 100));
          
          // Capture the print output container
          const container = document.getElementById('print-output-container');
          if (!container) {
              alert('Print output container not found. Please try again.');
              setIsGeneratingPreview(false);
              setPrintJobConfig(null);
              return;
          }

          // Get the actual styled child element (the one with mm dimensions)
          const styledChild = container.firstElementChild as HTMLElement;
          if (!styledChild) {
              alert('Print content not found. Please try again.');
              setIsGeneratingPreview(false);
              setPrintJobConfig(null);
              return;
          }

          // Temporarily make container visible for capture
          const originalContainerStyle = container.style.cssText;
          
          container.style.position = 'fixed';
          container.style.left = '0';
          container.style.top = '0';
          container.style.zIndex = '9999';
          container.style.visibility = 'visible';
          container.style.opacity = '1';
          container.style.overflow = 'visible';

          // Wait for fonts and images to load, and for transforms to apply
          await document.fonts.ready;
          // Reduced delay - minimal time needed
          await new Promise(r => setTimeout(r, 100));

          // Get the actual rendered dimensions of the styled child (which has mm dimensions)
          const rect = styledChild.getBoundingClientRect();
          const captureWidth = Math.ceil(rect.width);
          const captureHeight = Math.ceil(rect.height);

          // Capture the styled child element (not the outer wrapper)
          const dataUrl = await toPng(styledChild, {
              backgroundColor: '#ffffff',
              quality: 1.0,
              pixelRatio: 2,
              cacheBust: true,
              width: captureWidth,
              height: captureHeight
          });

          // Restore original style
          container.style.cssText = originalContainerStyle;

          setPrintPreviewImage(dataUrl);
          setShowPrintPreview(true);
      } catch (error) {
          console.error('Failed to generate preview:', error);
          alert(`Failed to generate print preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
          setIsGeneratingPreview(false);
      }
  };

  const handlePrintAction = async (action: 'SINGLE' | 'DOUBLE' | 'WHITE' | 'YELLOW' | 'PAIR') => {
      if (!isPro) {
          const violations = checkProFeatures(config);
          if (violations.length > 0) {
              setProModalErrors(violations);
              setShowProModal(true);
              return;
          }
      }

      const isElectron = /Electron/.test(navigator.userAgent);
      const isAndroid = PrinterBridge.isMobile;
      
      console.log('handlePrintAction', { action, isAndroid });

      const androidProfileIdToUse =
        printSettings.singleProfileId ||
        (profiles.length > 0 ? profiles[0].id : 'android-default');

      const profileForAndroidCheck =
        profiles.find(p => p.id === androidProfileIdToUse) || (profiles.length > 0 ? profiles[0] : null);
      
      // On Android, show printer selection modal only if no default profile is set
      if (isAndroid) {
          const hasDefaultProfile = !!printSettings.singleProfileId && profiles.some(p => p.id === printSettings.singleProfileId);

          if (!hasDefaultProfile) {
            // No default profile - show selection dialog
            setPendingPrintAction(action);
            setShowPrinterProfileSelect(true);
            return;
          }
          // Default profile is set - proceed with auto-connect if needed
          // Connection will be handled in executePrintJob
      } else if (!isElectron) { 
          window.print(); 
          return; 
      }

      // On mobile, use a default profile or create one on the fly
      const profileIdToUse = PrinterBridge.isMobile
          ? androidProfileIdToUse
          : (profiles.length === 1 
              ? profiles[0].id 
              : (printSettings.singleProfileId || (profiles.length > 0 ? profiles[0].id : '')));

      // Queue print jobs instead of executing directly (non-blocking)
      const currentRegistration = config.registration || 'UNKNOWN';
      
      if (action === 'SINGLE') {
          const jobId = `print-${Date.now()}-${Math.random()}`;
          setPrintQueue(prev => [...prev, {
              id: jobId,
              profileId: profileIdToUse,
              status: 'queued',
              progress: 0,
              message: 'Queued for printing',
              registration: currentRegistration,
              paused: false
          }]);
      } else if (action === 'DOUBLE') {
          // For DOUBLE action, generate print data once and reuse for both jobs
          let cachedPrintData: { type: 'image' | 'pdf'; data: string; pageWidth: number; pageHeight: number; cacheKey: string } | undefined;
          
          // Only cache for Android (where we generate PDF/image)
          if (isAndroid) {
              try {
                  // Get the profile
                  let profile = profiles.find(p => p.id === profileIdToUse);
                  if (!profile) {
                      profile = {
                          id: 'android-default',
                          name: 'Android Printer',
                          systemPrinterName: '',
                          connectionType: 'bluetooth' as const,
                          printerLanguage: 'TSPL' as const,
                          offsetX: 0,
                          offsetY: 0,
                          lengthCalibration: 100,
                          rotate: false,
                          mirror: false,
                          enableCropMarks: false,
                          cropMarksOffset: 0,
                          pageWidth: 109,
                          pageHeight: 520,
                          paperType: 'continuous',
                          gapEnabled: false,
                          compress: false,
                          cutAfterPrint: false,
                          androidAddress: undefined,
                          androidPort: undefined
                      } as PrinterProfile;
                  }
                  
                  // Set print job config to prepare DOM
                  setPrintJobConfig({ profile: profile as PrinterProfile, plateType: config.type });
                  await new Promise(r => setTimeout(r, 50)); // Allow DOM to update
                  
                  // Prepare dimensions and calibration
                  const dims = dimensions[config.plateSize];
                  const lipHeight = config.lippedPlate ? config.lipHeight : 0;
                  const calibrationScale = profile.lengthCalibrationEnabled ? (profile.lengthCalibration || 100) / 100 : 1;
                  
                  // Use dedicated Android print capture element (always white background)
                  const exportElement = document.getElementById('plate-export-print');
                  if (exportElement) {
                      // Calculate page dimensions for Android
                      // Rotation OFF: Horizontal/landscape (width > height) - normal plate orientation
                      // Rotation ON: Vertical/portrait (height > width) - rotated 90 degrees
                      // The PDF generation handles rotation internally, so we calculate page dimensions
                      // based on whether rotation is applied or not
                      let finalPageWidth: number;
                      let finalPageHeight: number;
                      
                      if (profile.pageWidth && profile.pageHeight) {
                          // SIZE = profile dimensions as entered, no swap regardless of rotation
                          finalPageWidth = profile.pageWidth;
                          finalPageHeight = profile.pageHeight;
                      } else {
                          // Fallback: use artwork dimensions
                          if (!profile.rotate) {
                              // Not rotated: horizontal/landscape
                              finalPageWidth = dims.width * calibrationScale;
                              finalPageHeight = dims.height + lipHeight;
                          } else {
                              // Rotated: vertical/portrait (swapped)
                              finalPageWidth = dims.height + lipHeight;
                              finalPageHeight = dims.width * calibrationScale;
                          }
                      }
                      
                      // Generate print data based on USE_FAST_PATH
                      const USE_FAST_PATH = false; // Match the constant in the file
                      
                      if (USE_FAST_PATH) {
                          // Fast path: Generate image
                          await document.fonts.ready;
                          await new Promise(r => setTimeout(r, 100));
                          
                          exportElement.style.width = `${dims.width}px`;
                          exportElement.style.height = `${dims.height + lipHeight}px`;
                          await new Promise(r => setTimeout(r, 40));
                          
                          const { toPng } = await import('html-to-image');
                          const pdfExportModule = await import('./utils/pdfExport');
                          const fontCSS = await pdfExportModule.preloadFontsAsBase64();
                          
                          const dataUrl = await toPng(exportElement, {
                              pixelRatio: (profile.dpi || 203) / 25.4,
                              backgroundColor: '#ffffff',
                              width: dims.width,
                              height: dims.height + lipHeight,
                              cacheBust: true,
                              fontEmbedCSS: fontCSS || undefined,
                              quality: 1.0,
                          });
                          
                          const base64Image = dataUrl.split(',')[1];
                          const cacheKey = `double-${profileIdToUse}-${config.registration}-${Date.now()}`;
                          
                          cachedPrintData = {
                              type: 'image',
                              data: base64Image,
                              pageWidth: finalPageWidth,
                              pageHeight: finalPageHeight,
                              cacheKey: cacheKey
                          };
                      } else {
                          // Slow path: Generate PDF
                          await document.fonts.ready;
                          await new Promise(r => setTimeout(r, 50));
                          
                          exportElement.style.width = `${dims.width}px`;
                          exportElement.style.height = `${dims.height + lipHeight}px`;
                          await new Promise(r => setTimeout(r, 50));
                          
                          let physicalPageWidth: number;
                          let physicalPageHeight: number;
                          
                          if (profile.pageWidth && profile.pageHeight) {
                              // PDF page must match artwork orientation so Java scaleBitmap doesn't distort.
                              // rotate=OFF: landscape (plate as on screen)
                              // rotate=ON:  portrait (canvas rotated 90°)
                              if (!profile.rotate) {
                                  physicalPageWidth = dims.width * calibrationScale;
                                  physicalPageHeight = dims.height + lipHeight;
                              } else {
                                  physicalPageWidth = dims.height + lipHeight;
                                  physicalPageHeight = dims.width * calibrationScale;
                              }
                          } else {
                              // Fallback: use artwork dimensions
                              if (!profile.rotate) {
                                  physicalPageWidth = dims.width * calibrationScale;
                                  physicalPageHeight = dims.height + lipHeight;
                              } else {
                                  physicalPageWidth = dims.height + lipHeight;
                                  physicalPageHeight = dims.width * calibrationScale;
                              }
                          }
                          
                          const pdfBlob = await generatePlatePDFBlob(
                              'plate-export-print',
                              dims.width,
                              dims.height + lipHeight,
                              profile.rotate || false,
                              profile.dpi || 203,
                              {
                                  offsetX: profile.offsetX || 0,
                                  offsetY: profile.offsetY || 0,
                                  lengthCalibration: profile.lengthCalibration || 100,
                                  rotate: profile.rotate || false,
                                  mirror: profile.mirror || false,
                                  enableCropMarks: profile.enableCropMarks || false,
                                  cropMarksOffset: profile.cropMarksOffset || 5,
                                  enableSideGuidelines: profile.enableSideGuidelines || false,
                                  sideGuidelinesPosition: profile.sideGuidelinesPosition || 'side',
                                  sideGuidelinesOffset: profile.sideGuidelinesOffset || 0,
                                  topAligned: config.lippedPlate, // Position at top when lip is present
                                  mediaSize: profile.mediaSize || 'FLEXIBLE'
                              },
                              physicalPageWidth,
                              physicalPageHeight
                          );
                          
                          const base64Pdf = await new Promise<string>((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                  const base64 = reader.result as string;
                                  resolve(base64.split(',')[1]);
                              };
                              reader.onerror = reject;
                              reader.readAsDataURL(pdfBlob);
                          });
                          
                          const cacheKey = `double-${profileIdToUse}-${config.registration}-${Date.now()}`;
                          
                          cachedPrintData = {
                              type: 'pdf',
                              data: base64Pdf,
                              pageWidth: finalPageWidth,
                              pageHeight: finalPageHeight,
                              cacheKey: cacheKey
                          };
                      }
                      
                      console.log('[PRINT] Generated cached print data for DOUBLE action:', cachedPrintData.type, 'cache key:', cachedPrintData.cacheKey);
                  }
              } catch (error) {
                  console.error('[PRINT] Failed to generate cached print data for DOUBLE action:', error);
                  // Continue without cache - jobs will generate individually
              }
          }
          
          const jobId1 = `print-${Date.now()}-${Math.random()}`;
          const jobId2 = `print-${Date.now() + 1}-${Math.random()}`;
          setPrintQueue(prev => [...prev, 
              { 
                  id: jobId1, 
                  profileId: profileIdToUse, 
                  status: 'queued', 
                  progress: 0, 
                  message: 'Queued for printing', 
                  registration: currentRegistration, 
                  paused: false,
                  cachedPrintData: cachedPrintData // First job uses cached data
              },
              { 
                  id: jobId2, 
                  profileId: profileIdToUse, 
                  status: 'queued', 
                  progress: 0, 
                  message: 'Queued for printing', 
                  registration: currentRegistration, 
                  paused: false,
                  cachedPrintData: cachedPrintData // Second job also uses cached data
              }
          ]);
      } else if (action === 'WHITE') {
          const jobId = `print-${Date.now()}-${Math.random()}`;
          setPrintQueue(prev => [...prev, {
              id: jobId,
              profileId: printSettings.dualWhiteProfileId || profileIdToUse,
              overridePlateType: PlateType.FRONT,
              status: 'queued',
              progress: 0,
              message: 'Queued for printing',
              registration: currentRegistration,
              paused: false
          }]);
      } else if (action === 'YELLOW') {
          const jobId = `print-${Date.now()}-${Math.random()}`;
          setPrintQueue(prev => [...prev, {
              id: jobId,
              profileId: printSettings.dualYellowProfileId || profileIdToUse,
              overridePlateType: PlateType.REAR,
              status: 'queued',
              progress: 0,
              message: 'Queued for printing',
              registration: currentRegistration,
              paused: false
          }]);
      } else if (action === 'PAIR') {
          const jobId1 = `print-${Date.now()}-${Math.random()}`;
          const jobId2 = `print-${Date.now() + 1}-${Math.random()}`;
          setPrintQueue(prev => [...prev,
              { id: jobId1, profileId: printSettings.dualWhiteProfileId || profileIdToUse, overridePlateType: PlateType.FRONT, status: 'queued', progress: 0, message: 'Queued for printing', registration: currentRegistration, paused: false },
              { id: jobId2, profileId: printSettings.dualYellowProfileId || profileIdToUse, overridePlateType: PlateType.REAR, status: 'queued', progress: 0, message: 'Queued for printing', registration: currentRegistration, paused: false }
          ]);
      }
  };

  const handleAndroidPrinterConnected = async (settings?: {
    language: 'TSPL' | 'CPCL' | 'ESC',
    rotate: boolean,
    driverType: 'android_tspl' | 'godex' | 'oki_usb' | 'tsc' | 'citizen',
    // Godex Settings
    godexPdfMode?: 'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex',
    // Oki Settings
    okiMediaSource?: 'AUTO' | 'TRAY1' | 'TRAY2' | 'MPTRAY' | 'MANUALFEED',
    okiMediaWeight?: 'LIGHT' | 'MEDIUM' | 'HEAVY' | 'ULTRAHEAVY',
    okiMediaType?: 'PLAIN' | 'LABELS' | 'BOND' | 'RECYCLED' | 'CARDSTOCK' | 'ROUGH',
    okiPaperSize?: string
  }) => {
      setShowAndroidPrinterSelect(false);
      
      // Store printer settings for later use
      if (settings) {
          // Update or create a default profile with these settings
          const existingProfile = profiles.find(p => p.id === 'android-default');
          
          const newSettings = {
              connectionType: settings.driverType === 'oki_usb' ? 'usb_oki' : 'bluetooth',
              printerLanguage: settings.language,
              driverType: settings.driverType,
              rotate: settings.rotate,
              // Explicitly unset hardcoded dimensions to allow dynamic sizing based on plate
              pageWidth: undefined,
              pageHeight: undefined,
              // Godex Settings
              godexPdfMode: settings.godexPdfMode || 'ezpl_hex',
              // Oki Settings
              okiMediaSource: settings.okiMediaSource,
              okiMediaWeight: settings.okiMediaWeight,
              okiMediaType: settings.okiMediaType,
              okiPaperSize: settings.okiPaperSize as any
          };

          if (!existingProfile) {
              const defaultProfile: any = {
                  id: 'android-default',
                  name: 'Android Printer',
                  lengthCalibration: 100,
                  enableCropMarks: false,
                  cropMarksOffset: 0,
                  paperType: 'continuous',
                  gapEnabled: false,
                  compress: false,
                  cutAfterPrint: false,
                  mirror: false,
                  offsetX: 0,
                  offsetY: 0,
                  systemPrinterName: 'Android Printer',
                  ...newSettings
              };
              setProfiles([...profiles, defaultProfile]);
          } else {
              // Update existing profile with new settings
              setProfiles(profiles.map(p => p.id === 'android-default' ? { ...p, ...newSettings } as PrinterProfile : p));
          }
      }
      
      // Send a small 1 inch test PDF for debugging
      try {
          await sendTestPrint();
      } catch (e) {
          console.error('Test print failed:', e);
          setToastMessage('Test print failed. Check connection.');
          setTimeout(() => setToastMessage(null), 3000);
      }
      
      if (pendingPrintAction) {
          const action = pendingPrintAction;
          setPendingPrintAction(null);
          // Small delay to ensure connection is established
          await new Promise(r => setTimeout(r, 1000));
          await handlePrintAction(action);
      }
  };

  // Handle PDF file selection and printing (Android only)
  const handlePdfFilePrint = async (file: File, profileId?: string) => {
      if (!PrinterBridge.isMobile) {
          alert('PDF file printing is only available on Android/iOS');
          return;
      }

      try {
          setIsPrinting(true);
          setToastMessage('Reading PDF file...');
          
          // Read file as base64
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                  const base64 = reader.result as string;
                  const cleanBase64 = base64.split(',')[1]; // Remove data:application/pdf;base64,
                  resolve(cleanBase64);
              };
              reader.onerror = reject;
          });
          reader.readAsDataURL(file);
          
          const base64Pdf = await base64Promise;
          console.log('PDF file loaded:', file.name, 'Size:', file.size, 'bytes');
          
          const targetProfileId = profileId || editingProfile?.id || printSettings.singleProfileId;
          const profile = (targetProfileId ? profiles.find(p => p.id === targetProfileId) : null) || profiles.find(p => p.id === 'android-default') || profiles[0] || {
              id: 'android-default',
              name: 'Android Printer',
              connectionType: 'bluetooth' as const,
              printerLanguage: 'TSPL' as const,
              lengthCalibration: 100,
              enableCropMarks: false,
              cropMarksOffset: 0,
              rotate: false,
              pageWidth: 109,
              pageHeight: 520,
              paperType: 'continuous',
              gapEnabled: false,
              compress: false,
              cutAfterPrint: false
          };
          
          // Use profile page dimensions or defaults
          const printWidth = profile.pageWidth || 109; // mm
          const printHeight = profile.pageHeight || 520; // mm
          
          setToastMessage('Sending PDF to printer...');

          try {
              const status = await PrinterBridge.getAndroidConnectionStatus();
              const needsGodex = (profile as any).driverType === 'godex';
              if (!status.connected || (needsGodex && status.driverType !== 'godex')) {
                  await connectToAndroidPrinter(profile as any);
              }
          } catch (e) {
              await connectToAndroidPrinter(profile as any);
          }
          
          const result = await PrinterBridge.printPlate({
              base64Pdf,
              width: printWidth, // mm
              height: printHeight, // mm
              driverType: (profile as any).driverType,
              godexPdfMode: (profile as any).godexPdfMode || 'ezpl_hex',
              printerLanguage: profile.printerLanguage || 'TSPL',
              connectionType: profile.connectionType as any,
              androidAddress: (profile as any).androidAddress,
              androidPort: (profile as any).androidPort,
              paperType: profile.paperType || 'label',
              gapEnabled: profile.gapEnabled || false,
              compress: profile.compress || false,
              cutAfterPrint: profile.cutAfterPrint || false,
              feedEnabled: profile.feedEnabled || false,
              feedAmount: profile.feedAmount || 5
          });
          
          console.log('PDF print result:', result);
          
          if (result && result.success) {
              setToastMessage(`PDF sent to printer successfully! (${Math.round(file.size / 1024)}KB)`);
              setTimeout(() => setToastMessage(null), 3000);
          } else {
              throw new Error(result?.message || 'Print failed');
          }
      } catch (error: any) {
          console.error('PDF file print error:', error);
          setToastMessage(`Error printing PDF: ${error.message || 'Unknown error'}`);
          setTimeout(() => setToastMessage(null), 5000);
      } finally {
          setIsPrinting(false);
      }
  };

  // Debug function: Send a small 1 inch (25.4mm) test PDF optimized for thermal printing
  const sendTestPrint = async () => {
      if (!PrinterBridge.isMobile) return;
      
      try {
          // Check connection first
          const status = await PrinterBridge.getAndroidConnectionStatus();
          if (!status.connected) {
              throw new Error('Printer not connected');
          }
          
          // Create a small 1 inch square test PDF (25.4mm x 25.4mm)
          // Optimized for thermal printers: 203 DPI = 8 dots/mm
          const testSize = 25.4; // 1 inch in mm
          
          // Create a simple test PDF with jsPDF (minimal content for small file size)
          const { jsPDF } = await import('jspdf');
          const pdf = new jsPDF({
              orientation: 'p',
              unit: 'mm',
              format: [testSize, testSize],
              compress: true
          });
          
          // Add a simple test pattern - minimal graphics for small file size
          pdf.setFillColor(0, 0, 0);
          // Small border
          pdf.rect(1, 1, testSize - 2, testSize - 2, 'S');
          // Small filled square in center
          pdf.rect(testSize/2 - 5, testSize/2 - 5, 10, 10, 'F');
          // Simple text
          pdf.setFontSize(6);
          pdf.setTextColor(255, 255, 255);
          pdf.text('TEST', testSize / 2, testSize / 2, { align: 'center', baseline: 'middle' });
          
          // Convert to base64 (small file due to compression and minimal content)
          const pdfBlob = pdf.output('blob');
          console.log('Test PDF size:', pdfBlob.size, 'bytes');
          
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                  const base64 = reader.result as string;
                  resolve(base64.split(',')[1]);
              };
              reader.onerror = reject;
          });
          reader.readAsDataURL(pdfBlob);
          const base64Pdf = await base64Promise;
          
          // Get printer language from profile or default to TSPL
          const profile = profiles.find(p => p.id === 'android-default') || {
              id: 'android-default',
              name: 'Android Printer',
              connectionType: 'bluetooth' as const,
              printerLanguage: 'TSPL' as const,
              lengthCalibration: 100,
              enableCropMarks: false,
              cropMarksOffset: 0,
              rotate: false
          };
          
          console.log('Sending test print:', { 
              size: `${testSize}mm x ${testSize}mm`, 
              language: profile.printerLanguage,
              pdfSize: `${Math.round(pdfBlob.size / 1024)}KB`
          });
          
          // Send to printer
          const result = await PrinterBridge.printPlate({
              base64Pdf,
              width: testSize,
              height: testSize,
              driverType: (profile as any).driverType,
              godexPdfMode: (profile as any).godexPdfMode || 'ezpl_hex',
              printerLanguage: profile.printerLanguage || 'TSPL',
              connectionType: profile.connectionType as any,
              androidAddress: (profile as any).androidAddress,
              androidPort: (profile as any).androidPort
          });
          
          if (result && result.success) {
              setToastMessage(`Test print sent! (${Math.round(pdfBlob.size / 1024)}KB)`);
              setTimeout(() => setToastMessage(null), 3000);
          } else {
              throw new Error(result?.message || 'Test print failed');
          }
      } catch (e: any) {
          console.error('Test print error:', e);
          setToastMessage(`Test print failed: ${e.message || 'Unknown error'}`);
          setTimeout(() => setToastMessage(null), 5000);
          throw e;
      }
  };

  const handleRegKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
          if (e.shiftKey) return;
          if (printSettings.quickPrintEnabled) {
              e.preventDefault();
              handlePrintAction('SINGLE');
          }
      }
  };

  // --- BULK ---
  const parseBulkCSV = () => {
      const lines = bulkCsvInput.split('\n');
      const newItems: BulkItem[] = [];
      lines.forEach((line, idx) => {
          const parts = line.split(',').map(s => s?.trim().toUpperCase());
          if (parts.length >= 1 && parts[0]) {
              const reg = parts[0];
              let type: BulkColor = 'P';
              if (parts[1]) {
                  if (parts[1].startsWith('F')) type = 'F';
                  else if (parts[1].startsWith('R')) type = 'R';
                  else if (parts[1].startsWith('P')) type = 'P';
              }
              const border = parts[2] ? parts[2].startsWith('Y') : config.border;
              const flag = parts[3] ? parts[3].startsWith('Y') : config.badge.enabled;
              newItems.push({
                  id: `bulk-${idx}-${Date.now()}`,
                  registration: reg, type: type, border: border, flagLayout: flag, selected: false,
                  status: { printedWhite: false, printedYellow: false }
              });
          }
      });
      setBulkItems([...bulkItems, ...newItems]);
      setBulkCsvInput('');
      if (newItems.length > 0) {
          setShowBulkCsvInput(false); // Collapse after parsing
      }
  };

  const cycleBulkType = (idx: number) => {
      const items = [...bulkItems];
      const types: BulkColor[] = ['P', 'F', 'R'];
      items[idx].type = types[(types.indexOf(items[idx].type) + 1) % types.length];
      setBulkItems(items);
  };

  const toggleBulkBorder = (idx: number) => { const items = [...bulkItems]; items[idx].border = !items[idx].border; setBulkItems(items); };
  const toggleBulkFlag = (idx: number) => { const items = [...bulkItems]; items[idx].flagLayout = !items[idx].flagLayout; setBulkItems(items); };

  const printBulkPair = async (index: number) => {
      await printBulkItem(index, 'WHITE');
      if (bulkPrintCancelledRef.current) return;
      await new Promise(r => setTimeout(r, bulkPrintDelay * 1000));
      if (bulkPrintCancelledRef.current) return;
      await printBulkItem(index, 'YELLOW');
  };

  const printBulkItem = async (index: number, plateType: 'WHITE' | 'YELLOW') => {
      if (bulkPrintCancelledRef.current) return;
      
      const item = bulkItems[index];
      const tempConfig = {
          ...config, registration: item.registration, border: item.border, flagLayout: item.flagLayout,
          badge: { ...config.badge, enabled: item.flagLayout }
      };
      const savedConfig = { ...config };
      setConfig(tempConfig);
      // Wait for state to update before printing
      await new Promise(r => setTimeout(r, 300));
      if (bulkPrintCancelledRef.current) {
          setConfig(savedConfig);
          return;
      }
      
      let profileId = printSettings.dualMode ? (plateType === 'WHITE' ? printSettings.dualWhiteProfileId : printSettings.dualYellowProfileId) : printSettings.singleProfileId;
      const realPlateType = plateType === 'WHITE' ? PlateType.FRONT : PlateType.REAR;
      await executePrintJob(profileId, realPlateType);
      setConfig(savedConfig);
      
      // Mark as printed
      const updatedItems = [...bulkItems];
      if (plateType === 'WHITE') updatedItems[index].status.printedWhite = true;
      if (plateType === 'YELLOW') updatedItems[index].status.printedYellow = true;
      setBulkItems(updatedItems);
  };

  const printSelectedBulk = async (mode: 'WHITE' | 'YELLOW' | 'PAIR') => {
      if (!isPro) {
          setProModalErrors(['Bulk Printing']);
          setShowProModal(true);
          return;
      }

      const selectedIndices = bulkItems.map((item, idx) => item.selected ? idx : -1).filter(i => i !== -1);
      if (selectedIndices.length === 0) return;

      // Calculate total print jobs
      let totalJobs = 0;
      selectedIndices.forEach(idx => {
          const item = bulkItems[idx];
          if (mode === 'WHITE' && (item.type === 'F' || item.type === 'P')) totalJobs++;
          if (mode === 'YELLOW' && (item.type === 'R' || item.type === 'P')) totalJobs++;
          if (mode === 'PAIR') {
              if (item.type === 'P') totalJobs += 2;
              else totalJobs++;
          }
      });

      bulkPrintCancelledRef.current = false;
      setBulkPrintProgress({ current: 0, total: totalJobs });
      let currentJob = 0;

      try {
          for (let i = 0; i < selectedIndices.length; i++) {
              if (bulkPrintCancelledRef.current) break;
              
              const idx = selectedIndices[i];
              const item = bulkItems[idx];
              
              if (mode === 'WHITE' && (item.type === 'F' || item.type === 'P')) {
                  await printBulkItem(idx, 'WHITE');
                  currentJob++;
                  setBulkPrintProgress({ current: currentJob, total: totalJobs });
                  if (bulkPrintCancelledRef.current) break;
              }
              if (mode === 'YELLOW' && (item.type === 'R' || item.type === 'P')) {
                  await printBulkItem(idx, 'YELLOW');
                  currentJob++;
                  setBulkPrintProgress({ current: currentJob, total: totalJobs });
                  if (bulkPrintCancelledRef.current) break;
              }
              if (mode === 'PAIR') {
                  if (item.type === 'P') {
                      await printBulkPair(idx);
                      currentJob += 2;
                      setBulkPrintProgress({ current: currentJob, total: totalJobs });
                      if (bulkPrintCancelledRef.current) break;
                  } else if (item.type === 'F') {
                      await printBulkItem(idx, 'WHITE');
                      currentJob++;
                      setBulkPrintProgress({ current: currentJob, total: totalJobs });
                      if (bulkPrintCancelledRef.current) break;
                  } else if (item.type === 'R') {
                      await printBulkItem(idx, 'YELLOW');
                      currentJob++;
                      setBulkPrintProgress({ current: currentJob, total: totalJobs });
                      if (bulkPrintCancelledRef.current) break;
                  }
              }
              
              // Apply delay between items (not after the last one)
              if (i < selectedIndices.length - 1 && !bulkPrintCancelledRef.current) {
                  await new Promise(r => setTimeout(r, bulkPrintDelay * 1000));
              }
          }
      } finally {
          setBulkPrintProgress(null);
      }
  };

  const cancelBulkPrint = () => {
      bulkPrintCancelledRef.current = true;
  };

  const toggleBulkSelection = (idx: number) => { const newItems = [...bulkItems]; newItems[idx].selected = !newItems[idx].selected; setBulkItems(newItems); };
  const toggleAllBulk = () => { const allSelected = bulkItems.every(i => i.selected); setBulkItems(bulkItems.map(i => ({...i, selected: !allSelected}))); };
  const deleteSelectedBulk = () => { setBulkItems(bulkItems.filter(item => !item.selected)); };
  const selectedBulkCount = bulkItems.filter(i => i.selected).length;

  const handleBulkExport = async (filter: 'all' | 'whites' | 'yellows' = 'all') => {
      if (!isPro) {
          setProModalErrors(['Bulk PDF Export']);
          setShowProModal(true);
          return;
      }

      // Get only selected items
      const selectedItems = bulkItems.filter(item => item.selected);
      if (selectedItems.length === 0) {
          alert('Please select at least one item to export');
          return;
      }

      setExporting(true);
      bulkExportCancelledRef.current = false;
      setBulkExportDropdownOpen(false);
      
      try {
          const originalState = { ...config };
          // Flatten items - pairs become two separate pages
          const flatItems: Array<{item: BulkItem; plateType: 'WHITE' | 'YELLOW'}> = [];
          selectedItems.forEach(item => {
              if (filter === 'all') {
                  // Export all pages for selected items
                  if(item.type === 'P') { 
                      flatItems.push({item, plateType: 'WHITE'}); 
                      flatItems.push({item, plateType: 'YELLOW'}); 
                  } else if(item.type === 'F') {
                      flatItems.push({item, plateType: 'WHITE'});
                  } else if(item.type === 'R') {
                      flatItems.push({item, plateType: 'YELLOW'});
                  }
              } else if (filter === 'whites') {
                  // Export only white/front pages
                  if(item.type === 'P' || item.type === 'F') {
                      flatItems.push({item, plateType: 'WHITE'});
                  }
              } else if (filter === 'yellows') {
                  // Export only yellow/rear pages
                  if(item.type === 'P' || item.type === 'R') {
                      flatItems.push({item, plateType: 'YELLOW'});
                  }
              }
          });
          
          setBulkExportProgress({ current: 0, total: flatItems.length });
          
          const captures: Array<{ imgData: string; pageWidth: number; pageHeight: number; artworkWidth: number; artworkHeight: number; artworkX: number; artworkY: number; enableCropMarks: boolean; cropMarksOffset: number }> = [];
          
          for (let i = 0; i < flatItems.length; i++) {
              // Check for cancellation
              if (bulkExportCancelledRef.current) {
                  break;
              }
              
              const {item, plateType} = flatItems[i];
              
              // Apply item config
              setConfig(prev => ({ 
                  ...prev, 
                  registration: item.registration, 
                  border: item.border, 
                  flagLayout: item.flagLayout, 
                  badge: { ...prev.badge, enabled: item.flagLayout } 
              }));
              
              // Wait for React to update
              await new Promise(r => setTimeout(r, 500));
              
              // Update progress
              setBulkExportProgress({ current: i + 1, total: flatItems.length });
              
              // Capture the plate
              const dims = dimensions[config.plateSize];
              const lipHeight = config.lippedPlate ? config.lipHeight : 0;
              const capture = await capturePlateForPDF(
                  'plate-export-standard', 
                  dims.width, 
                  dims.height + lipHeight, 
                  pdfExportSettings.dpi,
                  {
                      offsetX: pdfExportSettings.offsetX,
                      offsetY: pdfExportSettings.offsetY,
                      lengthCalibration: pdfExportSettings.lengthCalibration,
                      rotate: pdfExportSettings.rotate,
                      mirror: pdfExportSettings.mirror,
                      enableCropMarks: pdfExportSettings.enableCropMarks,
                      cropMarksOffset: pdfExportSettings.cropMarksOffset
                  }
              );
              
              captures.push({
                  ...capture,
                  enableCropMarks: pdfExportSettings.enableCropMarks,
                  cropMarksOffset: pdfExportSettings.cropMarksOffset
              });
          }
          
          // Build single multi-page PDF
          if (captures.length > 0 && !bulkExportCancelledRef.current) {
              await buildMultiPagePDF(captures, `Bulk_Plates_${Date.now()}.pdf`);
          }
          
          setConfig(originalState);
      } catch (e) { 
          console.error(e); 
          alert("Bulk Export Failed"); 
      } finally { 
          setExporting(false); 
          setBulkExportProgress(null);
      }
  };
  
  const cancelBulkExport = () => {
      bulkExportCancelledRef.current = true;
  };

  // Print a single bulk item directly
  const printSingleBulkItem = async (itemIndex: number, item: BulkItem, color: 'WHITE' | 'YELLOW' | 'SINGLE' | 'DOUBLE') => {
      if (!isPro) {
          setProModalErrors(['Bulk Print']);
          setShowProModal(true);
          return;
      }
      const originalState = { ...config };
      try {
          // Apply the item config
          setConfig(prev => ({ 
              ...prev, 
              registration: item.registration, 
              border: item.border, 
              flagLayout: item.flagLayout, 
              badge: { ...prev.badge, enabled: item.flagLayout } 
          }));
          await new Promise(r => setTimeout(r, 300)); // Let state update
          
          if (printSettings.dualMode) {
              if (color === 'WHITE') {
                  await executePrintJob(profiles.find(p => p.name.toLowerCase().includes('white'))?.id || profiles[0]?.id);
                  const updatedItems = [...bulkItems];
                  updatedItems[itemIndex].status.printedWhite = true;
                  setBulkItems(updatedItems);
              } else if (color === 'YELLOW') {
                  await executePrintJob(profiles.find(p => p.name.toLowerCase().includes('yellow'))?.id || profiles[1]?.id || profiles[0]?.id);
                  const updatedItems = [...bulkItems];
                  updatedItems[itemIndex].status.printedYellow = true;
                  setBulkItems(updatedItems);
              }
          } else {
              const profileIdToUse = profiles.length === 1 
                  ? profiles[0].id 
                  : (printSettings.singleProfileId || (profiles.length > 0 ? profiles[0].id : ''));
              
              if (color === 'SINGLE') {
                  await executePrintJob(profileIdToUse);
                  const updatedItems = [...bulkItems];
                  if (item.type === 'F') updatedItems[itemIndex].status.printedWhite = true;
                  if (item.type === 'R') updatedItems[itemIndex].status.printedYellow = true;
                  setBulkItems(updatedItems);
              } else if (color === 'DOUBLE') {
                  await executePrintJob(profileIdToUse);
                  await new Promise(r => setTimeout(r, bulkPrintDelay * 1000));
                  await executePrintJob(profileIdToUse);
                  const updatedItems = [...bulkItems];
                  updatedItems[itemIndex].status.printedWhite = true;
                  updatedItems[itemIndex].status.printedYellow = true;
                  setBulkItems(updatedItems);
              }
          }
      } finally {
          setConfig(originalState);
      }
  };

  const updateDealer = (key: string, value: any) => { setConfig(prev => ({ ...prev, dealerText: { ...prev.dealerText, [key]: value } })); };
  const updateBSAU = (key: string, value: any) => { setConfig(prev => ({ ...prev, bsauText: { ...prev.bsauText, [key]: value } })); };
  const updateSloganConfig = (key: string, value: any) => { setConfig(prev => ({ ...prev, sloganConfig: { ...prev.sloganConfig, [key]: value } })); };

  const handleFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onload = (ev) => {
              const fontName = file.name.split('.')[0];
              const fontUrl = ev.target?.result as string;
              const style = document.createElement('style');
              style.textContent = `@font-face { font-family: '${fontName}'; src: url('${fontUrl}') format('truetype'); }`;
              document.head.appendChild(style);
              // Register font for PDF export (needed for Android printing)
              registerCustomFont(fontName, fontUrl);
              setCustomFonts([...customFonts, { name: fontName, value: fontName, isPro: true }]);
          };
          reader.readAsDataURL(file);
      }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const reader = new FileReader();
          reader.onload = (ev) => { setConfig({...config, dealerLogo: ev.target?.result as string, useDealerLogo: true}); };
          reader.readAsDataURL(e.target.files[0]);
      }
  };

  const handleBadgeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const reader = new FileReader();
          reader.onload = (ev) => { 
              const result = ev.target?.result as string;
              setConfig({
                  ...config, 
                  badge: {
                      ...config.badge,
                      type: FlagType.CUSTOM,
                      customSvgUrl: result
                  }
              }); 
          };
          reader.readAsDataURL(e.target.files[0]);
      }
  };

  const handleLipUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const reader = new FileReader();
          reader.onload = (ev) => { setConfig({...config, lipImage: ev.target?.result as string}); };
          reader.readAsDataURL(e.target.files[0]);
      }
  };

  const saveTemplate = () => {
      if (!newTemplateName) return;
      const newT = { id: Date.now().toString(), name: newTemplateName, config: config };
      const updated = [...templates, newT];
      setTemplates(updated);
      localStorage.setItem('ps_templates', JSON.stringify(updated));
      setNewTemplateName('');
  };

  const loadTemplate = (t: SavedTemplate) => { 
      isRestoringTemplate.current = true;
      setConfig(t.config); 
      setShowTemplateModal(false); 
  };
  
  const deleteTemplate = (id: string) => {
      const updated = templates.filter(t => t.id !== id);
      setTemplates(updated);
      localStorage.setItem('ps_templates', JSON.stringify(updated));
  };

  const parseSVGCode = (code: string) => {
      try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(code, "image/svg+xml");
          const svg = doc.querySelector('svg');
          if(!svg) return;

          const viewBox = svg.getAttribute('viewBox') || '';
          const w = svg.getAttribute('width')?.replace('mm', '') || '0';
          const h = svg.getAttribute('height')?.replace('mm', '') || '0';
          
          const path = svg.querySelector('path');
          const d = path?.getAttribute('d') || '';
          const transform = path?.getAttribute('transform') || '';

          if (viewBox) setNewSizeViewBox(viewBox);
          if (parseFloat(w) > 0) setNewSizeW(parseFloat(w));
          if (parseFloat(h) > 0) setNewSizeH(parseFloat(h));
          if (d) setNewSizePath(d);
          if (transform) setNewSizeTransform(transform);

      } catch(e) {
          console.error("Failed to parse SVG", e);
      }
  };

  const addCustomSize = () => {
      if (!newSizeName) return;
      const key = newSizeName.toUpperCase().replace(/\s/g, '_');
      const newDims = { 
          ...dimensions, 
          [key]: { 
              width: newSizeW, 
              height: newSizeH, 
              label: newSizeName, 
              pathData: newSizePath || undefined,
              viewBox: newSizeViewBox || undefined,
              pathTransform: newSizeTransform || undefined,
              isHex: !!newSizePath 
          } 
      };
      setDimensions(newDims);
      const customOnly: Record<string, Dimensions> = {};
      Object.keys(newDims).forEach(k => { if (!InitialDimensions[k]) customOnly[k] = newDims[k]; });
      localStorage.setItem('ps_custom_sizes', JSON.stringify(customOnly));
      setConfig({...config, plateSize: key});
      
      setNewSizeName('');
      setNewSizeW(520);
      setNewSizeH(111);
      setNewSizePath('');
      setNewSizeViewBox('');
      setNewSizeTransform('');
      setNewSizeRawSVG('');
      setShowSizeModal(false);
      setShowShapeInputs(false);
  };

  const editCustomSize = (key: string, dim: Dimensions) => {
      setNewSizeName(dim.label || key);
      setNewSizeW(dim.width);
      setNewSizeH(dim.height);
      setNewSizePath(dim.pathData || '');
      setNewSizeViewBox(dim.viewBox || '');
      setNewSizeTransform(dim.pathTransform || '');
      setNewSizeRawSVG(''); 
      setShowShapeInputs(!!dim.pathData);
  };

  const deleteCustomSize = (key: string) => {
      const newDims = { ...dimensions };
      delete newDims[key];
      setDimensions(newDims);
      
      const customOnly: Record<string, Dimensions> = {};
      Object.keys(newDims).forEach(k => { if (!InitialDimensions[k]) customOnly[k] = newDims[k]; });
      localStorage.setItem('ps_custom_sizes', JSON.stringify(customOnly));
      
      if (config.plateSize === key) {
          setConfig({...config, plateSize: 'REGULAR'});
      }
  };

  const handleExport = useCallback(async () => {
    // DEMO MODE: Show watermarking message
    if (demoMode) {
        const proceed = window.confirm('Demo Mode: This export will be watermarked with the RegIQ logo.\n\nAll exports in demo mode include a watermark.\n\nContinue with watermarked export?');
        if (!proceed) return;
    }
    
    if (!isLicenseLoaded && !demoMode) {
        alert('Please wait for license to load...');
        return;
    }
    // Check actual license state directly (skip in demo mode)
    if (!demoMode) {
        const effectiveTier = currentUser?.license?.isTrial ? trialFeatureTier : currentUser?.license?.tier;
        if (!effectiveTier || effectiveTier.toUpperCase() !== 'PROFESSIONAL') {
            // Debug info
            const debugInfo = `License check failed. Loaded: ${isLicenseLoaded}, User: ${!!currentUser}, Tier: ${effectiveTier || 'undefined'}, Full License: ${JSON.stringify(currentUser?.license)}`;
            console.log('PDF Export Blocked:', debugInfo);
            setProModalErrors(['PDF Artwork Export (Full Blocked)']);
            setShowProModal(true);
            return;
        }
    }

    setExporting(true);
    try {
      await new Promise(r => setTimeout(r, 200));
      const dims = dimensions[config.plateSize];
      const lipHeight = config.lippedPlate ? config.lipHeight : 0;
      // Use PDF export settings
      await exportPlateToPDF(
        'plate-export-standard', 
        `Plate_${config.registration.replace(/\n/g,'_')}${demoMode ? '_DEMO' : ''}`, 
        dims.width, 
        dims.height + lipHeight, 
        pdfExportSettings.dpi,
        {
          offsetX: pdfExportSettings.offsetX,
          offsetY: pdfExportSettings.offsetY,
          lengthCalibration: pdfExportSettings.lengthCalibration,
          rotate: pdfExportSettings.rotate,
          topAligned: config.lippedPlate, // Position at top when lip is present
          mirror: pdfExportSettings.mirror,
          enableCropMarks: pdfExportSettings.enableCropMarks,
          cropMarksOffset: pdfExportSettings.cropMarksOffset
        }
      );
      if (demoMode) {
          setToastMessage(`Demo: PDF exported with watermark`);
      } else {
          setToastMessage(`PDF saved to Documents folder`);
      }
      setTimeout(() => setToastMessage(null), 5000);
    } catch (e: any) { 
      console.error('PDF Export Error:', e); 
      const errorMsg = e instanceof Error ? e.message : 'Unknown error';
      setToastMessage(`Failed to export PDF: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    } finally { 
      setExporting(false); 
    }
  }, [config, dimensions, isLicenseLoaded, currentUser, pdfExportSettings, demoMode]);

  const ALL_FONTS = shouldHidePro
      ? [...SYSTEM_FONTS, ...customFonts].filter(f => !f.isPro)
      : [...SYSTEM_FONTS, ...customFonts];

  const handleBadgeToggle = (checked: boolean) => {
      setConfig({
          ...config,
          flagLayout: checked,
          badge: { ...config.badge, enabled: checked },
          mainTextAlignment: checked ? config.mainTextAlignment : 'center'
      });
  };

  const existingCustomSizes = Object.entries(dimensions).filter(([key]) => !InitialDimensions[key]);

  // If still checking session, show loading screen
  if (isCheckingSession) {
      return (
          <div className="fixed inset-0 flex items-center justify-center bg-slate-950">
              <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-slate-400 font-mono">Loading...</p>
              </div>
          </div>
      );
  }

  // If registration screen requested, show registration
  if (showRegistrationScreen) {
      return (
          <ErrorBoundary>
              <RegistrationScreen 
                  onRegistrationSuccess={handleRegistrationSuccess}
                  onSwitchToLogin={handleSwitchToLogin}
              />
          </ErrorBoundary>
      );
  }

  // If trial activation screen requested, show trial activation
  if (showTrialActivation && currentSession && userSettings) {
      return (
          <ErrorBoundary>
              <TrialActivationScreen
                  session={currentSession}
                  userSettings={userSettings}
                  onTrialActivated={handleTrialActivated}
                  onLicenseActivated={handleTrialActivated}
              />
          </ErrorBoundary>
      );
  }

  // If time tamper detected, show error modal blocking all access
  if (showTimeTamperError) {
      return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
              <div className="bg-slate-900 border border-red-500/50 w-full max-w-md rounded-2xl shadow-2xl p-6 text-white">
                  <div className="flex items-center justify-center w-16 h-16 bg-red-900/30 rounded-full mx-auto mb-4 border border-red-500/50">
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2 text-red-400">System Clock Error</h3>
                  <p className="text-slate-400 text-center text-sm mb-4">
                      {timeTamperMessage}
                  </p>
                  <p className="text-slate-500 text-center text-xs mb-6">
                      Your system clock appears to have been set backwards. This may indicate an attempt to extend trial periods or bypass license expiration.
                  </p>
                  <p className="text-slate-500 text-center text-xs mb-6">
                      Please connect to the internet and correct your system time, then restart the application.
                  </p>
                  <button
                      onClick={async () => {
                          // Try to verify online and clear tamper flag
                          if (navigator.onLine) {
                              try {
                                  // Attempt heartbeat to verify
                                  const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/heartbeat', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ timestamp: Date.now() })
                                  });
                                  if (response.ok) {
                                      LicenseManager.clearTimeTamperFlag();
                                      setShowTimeTamperError(false);
                                      window.location.reload();
                                  }
                              } catch (e) {
                                  console.error('Failed to verify online:', e);
                              }
                          }
                      }}
                      className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-lg transition-colors"
                  >
                      {navigator.onLine ? 'Verify Online & Retry' : 'Connect to Internet'}
                  </button>
              </div>
          </div>
      );
  }

  // If login screen requested, show login
  if (showLoginScreen) {
      return (
          <ErrorBoundary>
              <LoginScreen 
                  onLoginSuccess={handleLoginSuccess}
                  onSwitchToRegister={handleSwitchToRegister}
              />
          </ErrorBoundary>
      );
  }

  // If license expired, show license key entry modal
  if (showLicenseExpiredModal) {
      const handleActivateExpiredLicense = async () => {
          if (!upgradeLicenseKey.trim()) {
              setUpgradeError('Please enter a license key');
              return;
          }

          setUpgradeLoading(true);
          setUpgradeError('');

          try {
              const machineId = await LicenseManager.getMachineId();
              const platform = await LicenseManager.getPlatform();

              const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/activateLicense', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      licenseKey: upgradeLicenseKey.trim(),
                      email: expiredSessionEmail,
                      deviceId: machineId,
                      platform
                  })
              });

              const data = await response.json();

              if (!response.ok) {
                  throw new Error(data.error || 'Failed to activate license');
              }

              // Create new session
              const newSession: AuthSession = {
                  email: expiredSessionEmail,
                  licenseNumber: data.licenseId || upgradeLicenseKey.trim(),
                  deviceId: machineId,
                  platform,
                  tier: data.tier || 'PROFESSIONAL',
                  validUntil: data.expiryDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                  issuedAt: Date.now(),
                  isValid: true
              };

              // Save locally
              await AuthService.saveSession(newSession);
              LicenseManager.saveLicense({
                  key: newSession.licenseNumber,
                  hwId: machineId,
                  tier: newSession.tier,
                  expiryDate: new Date(newSession.validUntil).getTime(),
                  isTrial: false
              });

              // Success - reload app
              setUpgradeSuccess(true);
              setTimeout(() => {
                  window.location.reload();
              }, 1500);
          } catch (error: any) {
              setUpgradeError(error.message || 'Failed to activate license');
          }
          setUpgradeLoading(false);
      };

      return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
              <div className="bg-slate-900 border border-amber-500/50 w-full max-w-md rounded-2xl shadow-2xl p-6 text-white">
                  {upgradeSuccess ? (
                      <div className="text-center py-8">
                          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                          <h4 className="text-lg font-bold mb-2 text-white">License Activated!</h4>
                          <p className="text-sm text-slate-400">Restarting app...</p>
                      </div>
                  ) : (
                      <>
                          <div className="flex items-center justify-center w-16 h-16 bg-amber-900/30 rounded-full mx-auto mb-4 border border-amber-500/50">
                              <Key className="w-8 h-8 text-amber-500" />
                          </div>
                          <h3 className="text-xl font-bold text-center mb-2 text-amber-400">License Expired</h3>
                          <p className="text-slate-400 text-center text-sm mb-4">
                              Your trial or license has expired. Enter a valid license key to continue using RegIQ.
                          </p>
                          
                          <div className="space-y-4">
                              <div>
                                  <label className="block text-sm font-medium mb-2 text-slate-300">License Key</label>
                                  <input
                                      type="text"
                                      value={upgradeLicenseKey}
                                      onChange={(e) => {
                                          setUpgradeLicenseKey(e.target.value.toUpperCase());
                                          setUpgradeError('');
                                      }}
                                      placeholder="XXXX-XXXX-XXXX-XXXX"
                                      className="w-full px-4 py-3 rounded-lg border bg-slate-800 border-slate-700 text-white placeholder-slate-500 text-center font-mono text-lg tracking-wider focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  />
                              </div>

                              {upgradeError && (
                                  <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3">
                                      <span className="text-red-500 text-lg flex-shrink-0">⚠</span>
                                      <p className="text-sm text-red-400">{upgradeError}</p>
                                  </div>
                              )}

                              <button
                                  onClick={handleActivateExpiredLicense}
                                  disabled={upgradeLoading || !upgradeLicenseKey.trim()}
                                  className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                              >
                                  {upgradeLoading ? (
                                      <>
                                          <Loader2 className="w-5 h-5 animate-spin" />
                                          Activating...
                                      </>
                                  ) : (
                                      <>
                                          <Unlock className="w-5 h-5" />
                                          Activate License
                                      </>
                                  )}
                              </button>

                              <p className="text-xs text-center text-slate-500">
                                  Don't have a license? <a href="https://regiq.co.uk" target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400 underline">Purchase at regiq.co.uk</a>
                              </p>
                              
                              <div className="border-t border-slate-700 pt-4 mt-4">
                                  <button
                                      onClick={() => {
                                          setShowLicenseExpiredModal(false);
                                          setUpgradeLicenseKey('');
                                          setUpgradeError('');
                                          setShowLoginScreen(true);
                                      }}
                                      className="w-full text-slate-400 hover:text-white text-sm py-2 transition-colors"
                                  >
                                      Use a different account
                                  </button>
                              </div>
                          </div>
                      </>
                  )}
              </div>
          </div>
      );
  }

  // If no current session, show login by default (but not in demo mode - session is set async)
  if (!currentSession && !demoMode) {
      return (
          <ErrorBoundary>
              <LoginScreen
                  onLoginSuccess={handleLoginSuccess}
                  onSwitchToRegister={handleSwitchToRegister}
              />
          </ErrorBoundary>
      );
  }

  // Demo mode notification messages - shown one at a time as friendly modals
  const demoNotifications = [
      { id: 'demo-welcome', icon: '👋', title: 'Welcome to RegIQ Demo!', text: "Explore the interface and see what's possible. This is a fully interactive preview of our plate design software." },
      { id: 'demo-features', icon: '💡', title: 'Demo Mode', text: "This demo is for exploring the layout and getting a feel for the app. Some features may be limited or work differently than the full version." },
      { id: 'demo-crossplatform', icon: '🖥️', title: 'Works Everywhere', text: "RegIQ runs natively on Windows, Mac, Android & iOS with direct printing support. Try opening this page on another device!" },
      { id: 'demo-trial', icon: '🎁', title: 'Try It Free!', text: "Love what you see? Download RegIQ and get a full 7-day Professional trial - completely free, no credit card required.", cta: 'Download Now', ctaUrl: 'https://regiq.co.uk/download' },
  ];

  // Get the first non-dismissed notification to show
  const currentDemoNotification = demoMode ? demoNotifications.find(n => !dismissedMessages.includes(n.id)) : null;

  return (
    <div id="regiq-app-root" className={`${demoMode ? 'relative w-full' : 'fixed inset-0'} flex flex-col font-sans transition-colors duration-200 overflow-hidden ${darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-800'}`} style={{ height: demoMode && containerHeight ? `${containerHeight}px` : (demoMode ? '100%' : '100dvh') }}>
      {/* Demo Mode Notification Modal - shows one at a time */}
      {currentDemoNotification && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
              <div className={`max-w-sm w-full rounded-2xl shadow-2xl p-6 text-center animate-in fade-in zoom-in duration-300 ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <div className="text-4xl mb-3">{currentDemoNotification.icon}</div>
                  <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{currentDemoNotification.title}</h3>
                  <p className={`text-sm mb-5 leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{currentDemoNotification.text}</p>
                  <div className="flex flex-col gap-2">
                      {currentDemoNotification.cta && currentDemoNotification.ctaUrl && (
                          <a
                              href={currentDemoNotification.ctaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full py-2.5 px-4 rounded-lg font-semibold text-white text-sm transition-all"
                              style={{ background: 'linear-gradient(135deg, #10b981, #06b6d4)' }}
                          >
                              {currentDemoNotification.cta}
                          </a>
                      )}
                      <button
                          onClick={() => {
                              const updated = [...dismissedMessages, currentDemoNotification.id];
                              setDismissedMessages(updated);
                              localStorage.setItem('dismissedMessages', JSON.stringify(updated));
                          }}
                          className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                      >
                          {currentDemoNotification.cta ? 'Maybe Later' : 'Got It'}
                      </button>
                  </div>
              </div>
          </div>
      )}
      {/* Admin Messages Banner */}
      {adminMessages.filter(m => !dismissedMessages.includes(m.id)).map(msg => (
          <div key={msg.id} className="w-full bg-blue-600 text-white px-4 py-3 flex items-start gap-3 shadow-lg z-50 print-hidden">
              <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{msg.subject}</div>
                  <div className="text-sm opacity-90 mt-1" dangerouslySetInnerHTML={{ __html: msg.htmlContent }} />
              </div>
              <button
                  onClick={() => {
                      const updated = [...dismissedMessages, msg.id];
                      setDismissedMessages(updated);
                      localStorage.setItem('dismissedMessages', JSON.stringify(updated));
                  }}
                  className="text-white/80 hover:text-white flex-shrink-0 text-lg font-bold leading-none mt-0.5"
              >
                  ✕
              </button>
          </div>
      ))}
      {/* Full-width header bar on desktop (hidden in demo/WordPress mode) */}
      {!isMobile && (
        <div className={`w-full border-b flex justify-between items-center px-4 py-2 sticky top-0 z-40 transition-all flex-shrink-0 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-100'}`} style={{ height: demoMode ? '50px' : '80px' }}>
            <div className="flex items-center flex-1 min-w-0">
                <div className={`${demoMode ? 'w-[150px]' : 'w-[250px]'} flex-shrink-0 flex items-center h-full`}>
                    <div style={{ transform: demoMode ? 'scale(1)' : 'scale(1.65)', transformOrigin: 'left center' }}>
                        <Logo darkMode={darkMode} className={demoMode ? "h-8 w-auto" : "h-12 w-auto"}/>
                    </div>
                </div>
                {/* License Status Display */}
                {currentUser?.license && (() => {
                    const daysRemaining = Math.ceil((currentUser.license.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
                    const isExpiringSoon = daysRemaining <= 7;

                    if (currentUser.license.isTrial) {
                        // Trial Mode - Compact Horizontal Layout
                        return (
                            <div className="flex items-center gap-3">
                                {/* Trial Badge + Days */}
                                <div className="flex items-center gap-2">
                                    <span className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${
                                        isExpiringSoon
                                            ? 'bg-red-500 text-white'
                                            : 'bg-amber-500 text-white'
                                    }`}>
                                        🎁 Trial
                                    </span>
                                    <span className={`text-sm font-semibold ${
                                        isExpiringSoon
                                            ? darkMode ? 'text-red-400' : 'text-red-600'
                                            : darkMode ? 'text-amber-400' : 'text-amber-600'
                                    }`}>
                                        {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
                                    </span>
                                </div>

                                {/* Tier Toggle */}
                                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                                    darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                }`}>
                                    <span className={`text-xs font-medium ${
                                        trialFeatureTier === 'BASIC' ? (darkMode ? 'text-blue-400 font-bold' : 'text-blue-600 font-bold') : (darkMode ? 'text-slate-400' : 'text-slate-500')
                                    }`}>Essential</span>
                                    <button
                                        onClick={handleTrialTierToggle}
                                        className={`relative w-11 h-5 rounded-full transition-all ${
                                            trialFeatureTier === 'PROFESSIONAL'
                                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600'
                                                : darkMode ? 'bg-slate-600' : 'bg-slate-300'
                                        }`}
                                        title={`Switch to ${trialFeatureTier === 'PROFESSIONAL' ? 'Essential' : 'Professional'}`}
                                    >
                                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${
                                            trialFeatureTier === 'PROFESSIONAL' ? 'translate-x-6' : 'translate-x-0.5'
                                        }`}/>
                                    </button>
                                    <span className={`text-xs font-medium ${
                                        trialFeatureTier === 'PROFESSIONAL' ? (darkMode ? 'text-indigo-400 font-bold' : 'text-indigo-600 font-bold') : (darkMode ? 'text-slate-400' : 'text-slate-500')
                                    }`}>Pro</span>
                                </div>

                                {/* License Key Button */}
                                <button
                                    onClick={() => setShowUpgradeLicenseModal(true)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                        darkMode
                                            ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:border-slate-600'
                                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                                    }`}
                                    title="Enter a license key to upgrade"
                                >
                                    <Key className="w-3.5 h-3.5" />
                                    <span>License Key</span>
                                </button>
                            </div>
                        );
                    } else {
                        // Licensed User - Simple Display
                        return (
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                                darkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'
                            }`}>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                    currentUser.license.tier === 'PROFESSIONAL' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                                }`}>
                                    {currentUser.license.tier === 'PROFESSIONAL' ? 'Pro' : 'Essential'}
                                </span>
                                <span className={`text-xs ${isExpiringSoon ? 'text-red-500 font-semibold' : darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                    {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                                </span>
                                {currentUser.license.tier === 'BASIC' && (
                                    <button
                                        onClick={handleProUpgradeTrial}
                                        className="text-[10px] text-purple-500 hover:text-purple-400 font-bold underline ml-2"
                                    >
                                        Try Pro Free
                                    </button>
                                )}
                            </div>
                        );
                    }
                })()}
                {!currentUser?.license && (
                    <span className={`text-[10px] font-mono px-2 py-1 rounded ${darkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-500'}`}>
                        UNREGISTERED
                    </span>
                )}
            </div>
            <div className="flex gap-1 items-center">
                {!shouldHidePro && <button onClick={() => setShowPrintHistory(true)} title="Print History" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><History className="w-5 h-5"/></button>}
                <button onClick={() => setDarkMode(!darkMode)} title="Toggle Theme" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-yellow-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}>{darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}</button>
                <button onClick={() => setShowPrinterSettings(true)} title="Printer Settings" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><Settings className="w-5 h-5"/></button>
                {!shouldHidePro && <button onClick={() => setShowBulkModal(true)} title="Bulk Import" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><List className="w-5 h-5"/></button>}
                <button onClick={() => setShowTemplateModal(true)} title="Templates" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><FolderOpen className="w-5 h-5"/></button>
                <button onClick={handleLogout} title="Deactivate License (Logout)" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-red-500 hover:bg-slate-800' : 'text-slate-600 hover:text-red-500 hover:bg-slate-200'}`}><LogOut className="w-5 h-5"/></button>
            </div>
        </div>
      )}
      <div className="flex flex-1 min-h-0 overflow-hidden" style={{ flexDirection: isMobile ? 'column' : 'row' }}>
      <style>{`
        #print-output-container { position: fixed; left: -20000px; top: 0; z-index: -10; overflow: visible !important; }
        #print-output-container * { overflow: visible !important; }
        @keyframes blink-cursor {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .cursor-blink { animation: blink-cursor 1s infinite; }
        
        /* Custom Scrollbar Styling - Dynamic based on theme */
        ::-webkit-scrollbar {
          width: 8px !important;
          height: 8px !important;
        }
        ::-webkit-scrollbar-corner {
          background: transparent !important;
        }
        ${darkMode ? `
          ::-webkit-scrollbar-track {
            background: #0f172a !important;
          }
          ::-webkit-scrollbar-thumb {
            background: #334155 !important;
            border-radius: 4px !important;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #475569 !important;
          }
          * {
            scrollbar-width: thin !important;
            scrollbar-color: #334155 #0f172a !important;
          }
        ` : `
          ::-webkit-scrollbar-track {
            background: #f1f5f9 !important;
          }
          ::-webkit-scrollbar-thumb {
            background: #94a3b8 !important;
            border-radius: 4px !important;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #64748b !important;
          }
          * {
            scrollbar-width: thin !important;
            scrollbar-color: #94a3b8 #f1f5f9 !important;
          }
        `}
        @media print {
            @page { margin: 0 !important; padding: 0 !important; }
            * { 
              -webkit-print-color-adjust: exact !important; 
              print-color-adjust: exact !important;
              box-shadow: none !important;
              text-shadow: none !important;
            }
            html, body, #root { 
              background: #ffffff !important; 
              background-color: #ffffff !important;
              margin: 0 !important; 
              padding: 0 !important;
              width: 100% !important; 
              height: 100% !important; 
            }
            aside, main, .print-hidden, #plate-export-standard, #plate-export-print { display: none !important; visibility: hidden !important; }
            #print-output-container { 
              position: fixed !important; 
              left: 0 !important; 
              top: 0 !important; 
              width: 100% !important;
              height: 100% !important;
              opacity: 1 !important; 
              z-index: 9999 !important; 
              background: #ffffff !important; 
              background-color: #ffffff !important;
              display: block !important;
              overflow: visible !important; 
            }
            #print-content-wrapper {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              height: 100% !important;
              background: #ffffff !important;
              background-color: #ffffff !important;
            }
            #print-output-container * { 
              overflow: visible !important;
              filter: none !important;
              -webkit-filter: none !important;
              box-shadow: none !important;
              border-radius: 0 !important;
            }
            #print-output-container svg {
              filter: none !important;
            }
        }
      `}</style>
      
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
      
      {/* Print Preview Modal - Full page simulation (hidden on mobile) */}
      {showPrintPreview && !isMobile && (
          <div
              className="fixed inset-0 z-[10000] bg-slate-700 flex flex-col"
              onClick={() => {
                  setShowPrintPreview(false);
                  setPrintPreviewImage(null);
              }}
          >
              {/* Header bar */}
              <div 
                  className="bg-slate-800 px-4 py-2 flex items-center justify-between flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
              >
                  <h3 className="text-sm font-medium text-white">
                      Print Preview - {(() => {
                          const dims = currentDims;
                          const rotate = printJobConfig?.profile?.rotate || false;
                          const w = rotate ? dims.height : dims.width;
                          const h = rotate ? dims.width : dims.height;
                          return `${Math.round(w)}mm × ${Math.round(h)}mm`;
                      })()}
                  </h3>
                  <button 
                      onClick={() => {
                          setShowPrintPreview(false);
                          setPrintPreviewImage(null);
                      }}
                      className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                      title="Close (ESC)"
                  >
                      <X className="w-5 h-5 text-white" />
                  </button>
              </div>
              {/* Preview area - simulates paper on desk */}
              <div className="flex-1 overflow-auto flex items-center justify-center p-8">
                  {printPreviewImage ? (
                      <img 
                          src={printPreviewImage} 
                          alt="Print Preview" 
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                              maxWidth: '95vw',
                              maxHeight: 'calc(100vh - 80px)',
                              width: 'auto',
                              height: 'auto',
                              backgroundColor: '#ffffff',
                              display: 'block'
                          }}
                      />
                  ) : (
                      <div className="text-white">Generating preview...</div>
                  )}
              </div>
              {/* Footer with info */}
              <div 
                  className="bg-slate-800 px-4 py-2 text-xs text-slate-400 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
              >
                  Click anywhere or press ESC to close • This shows exactly what will be sent to the printer
              </div>
          </div>
      )}
      
      {/* Compact print queue indicator - shows queue status without blocking UI */}
      <PrintQueueIndicator />
      
      {/* Legacy printing overlay - only show on desktop (mobile uses new queue indicator) */}
      {isPreparingPrint && !isMobile && <PrintingOverlay progress={printProgress} />}
      {isPreparingPrint && !isMobile && <div ref={(node) => node && document.body.classList.add('is-printing')} />}
      {!isPreparingPrint && <div ref={(node) => node && document.body.classList.remove('is-printing')} />}

      {/* PRINT HISTORY MODAL */}
      {showPrintHistory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowPrintHistory(false)}>
              <div className={`${darkMode ? 'bg-slate-900' : 'bg-white'} rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col`} onClick={e => e.stopPropagation()}>
                  <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <h3 className={`text-lg font-bold ${darkMode ? 'text-slate-200' : 'text-slate-900'}`}>Print History</h3>
                      <button onClick={() => setShowPrintHistory(false)} className={`p-1 rounded ${darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}>
                          <X className="w-5 h-5"/>
                      </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                      {printHistory.length === 0 ? (
                          <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              <History className="w-12 h-12 mx-auto mb-2 opacity-50"/>
                              <p>No print history yet</p>
                          </div>
                      ) : (
                          <div className="space-y-2">
                              {printHistory.map((item, index) => (
                                  <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                      <div className="flex-1">
                                          <div className={`font-mono font-bold text-lg ${darkMode ? 'text-slate-200' : 'text-slate-900'}`}>{item.registration}</div>
                                          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {new Date(item.timestamp).toLocaleString()}
                                          </div>
                                          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                                              {item.plateType === PlateType.FRONT ? 'White' : 'Yellow'}
                                          </div>
                                      </div>
                                      <button
                                          onClick={() => {
                                              setConfig({...config, registration: item.registration, type: item.plateType});
                                              setShowPrintHistory(false);
                                              handlePrintAction('SINGLE');
                                          }}
                                          className={`p-2 rounded transition-colors ${darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                                          title="Reprint"
                                      >
                                          <Printer className="w-5 h-5"/>
                                      </button>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* PRO FEATURE MODAL */}
      {showProModal && (
          <ProFeatureModal 
            errors={proModalErrors} 
            onClose={() => setShowProModal(false)} 
          />
      )}

      {/* UPGRADE LICENSE MODAL (for trial users) */}
      {showUpgradeLicenseModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 print-hidden">
              <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200 ${darkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-xl font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                          <Key className="w-6 h-6 text-green-500" />
                          Upgrade License
                      </h3>
                      <button onClick={() => {
                          setShowUpgradeLicenseModal(false);
                          setUpgradeLicenseKey('');
                          setUpgradeError('');
                          setUpgradeSuccess(false);
                      }}>
                          <X className="w-5 h-5 text-slate-400 hover:text-red-500" />
                      </button>
                  </div>

                  {upgradeSuccess ? (
                      <div className="text-center py-8">
                          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                          <h4 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                              License Upgraded!
                          </h4>
                          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              Your license has been successfully activated.
                          </p>
                      </div>
                  ) : (
                      <>
                          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              Enter your license key to upgrade from trial to a full license without logging out.
                          </p>

                          <div className="space-y-4">
                              <div>
                                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                      License Key
                                  </label>
                                  <input
                                      type="text"
                                      value={upgradeLicenseKey}
                                      onChange={(e) => {
                                          setUpgradeLicenseKey(e.target.value.toUpperCase());
                                          setUpgradeError('');
                                      }}
                                      placeholder="XXXX-XXXX-XXXX-XXXX"
                                      className={`w-full px-4 py-3 rounded-lg border text-center font-mono text-lg tracking-wider focus:outline-none focus:ring-2 focus:ring-green-500 ${
                                          darkMode 
                                              ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' 
                                              : 'bg-slate-50 border-slate-300 text-slate-800 placeholder-slate-400'
                                      }`}
                                  />
                              </div>

                              {upgradeError && (
                                  <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-start gap-3">
                                      <span className="text-red-500 text-lg flex-shrink-0">⚠</span>
                                      <p className="text-sm text-red-400">{upgradeError}</p>
                                  </div>
                              )}

                              <button
                                  onClick={handleUpgradeLicense}
                                  disabled={upgradeLoading || !upgradeLicenseKey.trim()}
                                  className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                              >
                                  {upgradeLoading ? (
                                      <>
                                          <Loader2 className="w-5 h-5 animate-spin" />
                                          Activating...
                                      </>
                                  ) : (
                                      <>
                                          <Unlock className="w-5 h-5" />
                                          Activate License
                                      </>
                                  )}
                              </button>

                              <p className={`text-xs text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  Don't have a license? <a href="https://regiq.co.uk" target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400 underline">Purchase at regiq.co.uk</a>
                              </p>
                          </div>
                      </>
                  )}
              </div>
          </div>
      )}

      {/* SIGN OUT MODAL */}
      {showSignOutModal && (
          <SignOutModal 
            onClose={() => setShowSignOutModal(false)}
            onConfirm={handleDeactivate}
          />
      )}
      {showLicenseExpiryReminder && currentUser?.license && (() => {
          const daysRemaining = Math.ceil((currentUser.license.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
          return (
              <LicenseExpiryReminderModal 
                  onClose={() => setShowLicenseExpiryReminder(false)}
                  daysRemaining={daysRemaining}
              />
          );
      })()}
      {showOfflineVerificationModal && (
          <OfflineVerificationModalEnhanced 
              onClose={() => setShowOfflineVerificationModal(false)}
              qrCode={offlineVerificationQR}
              userEmail={currentSession?.email || currentUser?.name || ''}
              deviceId={debugMachineId}
              onVerified={() => {
                  setShowOfflineVerificationModal(false);
                  // Refresh license state
                  const savedLicense = LicenseManager.getStoredLicense();
                  if (savedLicense) {
                      setCurrentUser(prev => prev ? { ...prev, license: savedLicense } : null);
                  }
              }}
          />
      )}
      {showAndroidPrinterSelect && PrinterBridge.isMobile && (
          <ErrorBoundary>
              <AndroidPrinterSelect
                  onConnected={handleAndroidPrinterConnected}
                  onClose={() => {
                      setShowAndroidPrinterSelect(false);
                      setPendingPrintAction(null);
                  }}
                  darkMode={darkMode}
              />
          </ErrorBoundary>
      )}
      
      {/* Printer Profile Selection Modal - shown when no printer connected */}
      {showPrinterProfileSelect && PrinterBridge.isMobile && (
          <div 
              className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-4"
              onClick={(e) => {
                  e.stopPropagation();
                  if (e.target === e.currentTarget) {
                      setShowPrinterProfileSelect(false);
                      setPendingPrintAction(null);
                  }
              }}
          >
              <div 
                  className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}
                  onClick={e => e.stopPropagation()}
              >
                  <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                      <h3 className="font-bold text-lg">Select Printer</h3>
                      <button onClick={() => { setShowPrinterProfileSelect(false); setPendingPrintAction(null); }} className="text-sm font-medium opacity-70 hover:opacity-100">Cancel</button>
                  </div>

                  <div className="p-4 flex-1 overflow-y-auto min-h-[200px]">
                      <p className="text-sm opacity-70 mb-4">Select a printer to connect and print:</p>
                      <div className="space-y-2">
                          {profiles.map((profile) => (
                              <button
                                  key={profile.id}
                                  onClick={async () => {
                                      // Connect to the printer and execute the pending print job
                                      setShowPrinterProfileSelect(false);
                                      
                                      try {
                                          // Connect to the printer
                                          await connectToAndroidPrinter(profile);
                                          
                                          // Execute the pending print action
                                          if (pendingPrintAction) {
                                              if (pendingPrintAction === 'SINGLE') {
                                                  await executePrintJob(profile.id);
                                              } else if (pendingPrintAction === 'DOUBLE') {
                                                  await executePrintJob(profile.id, 'double');
                                              } else if (pendingPrintAction === 'WHITE') {
                                                  await executePrintJob(printSettings.dualWhiteProfileId || profile.id);
                                              } else if (pendingPrintAction === 'YELLOW') {
                                                  await executePrintJob(printSettings.dualYellowProfileId || profile.id);
                                              } else if (pendingPrintAction === 'PAIR') {
                                                  // Execute both white and yellow
                                                  await executePrintJob(printSettings.dualWhiteProfileId || profile.id);
                                                  await executePrintJob(printSettings.dualYellowProfileId || profile.id);
                                              }
                                              setPendingPrintAction(null);
                                          }
                                      } catch (e) {
                                          console.error('Print failed:', e);
                                          alert('Print failed: ' + (e as Error).message);
                                      }
                                  }}
                                  className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                                      darkMode 
                                          ? 'border-slate-700 hover:bg-slate-800' 
                                          : 'border-slate-200 hover:bg-slate-50'
                                  }`}
                              >
                                  {/* Connection type icon */}
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                      profile.connectionType === 'usb' 
                                          ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-600')
                                          : profile.connectionType === 'bluetooth'
                                          ? (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600')
                                          : (darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600')
                                  }`}>
                                      {profile.connectionType === 'usb' ? (
                                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                                          </svg>
                                      ) : profile.connectionType === 'bluetooth' ? (
                                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                              <path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z"/>
                                          </svg>
                                      ) : (
                                          <Printer className="w-5 h-5" />
                                      )}
                                  </div>
                                  
                                  <div className="flex-1">
                                      <div className="font-bold text-sm">{profile.name}</div>
                                      <div className="text-xs opacity-60 mt-0.5 flex items-center gap-2">
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                              profile.connectionType === 'usb' 
                                                  ? 'bg-green-500/20 text-green-500'
                                                  : profile.connectionType === 'bluetooth'
                                                  ? 'bg-blue-500/20 text-blue-500'
                                                  : 'bg-slate-500/20 text-slate-500'
                                          }`}>
                                              {profile.connectionType === 'usb' ? 'USB' : profile.connectionType === 'bluetooth' ? 'BT' : '?'}
                                          </span>
                                          {profile.androidAddress && <span className="font-mono text-[10px]">{profile.androidAddress.substring(0, 8)}...</span>}
                                      </div>
                                  </div>
                              </button>
                          ))}
                          
                          {profiles.length === 0 && (
                              <div className="text-center py-6 opacity-50 text-sm">
                                  No printer profiles found.
                              </div>
                          )}
                          
                          {/* Add New Profile Button - Goes to Settings */}
                          <button
                              onClick={() => {
                                  setShowPrinterProfileSelect(false);
                                  setShowPrinterSettings(true);
                              }}
                              className={`w-full p-3 rounded-lg border-2 border-dashed text-center transition-all ${
                                  darkMode
                                      ? 'border-slate-700 hover:border-blue-500 hover:bg-slate-800/50 text-slate-400 hover:text-blue-400'
                                      : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-500 hover:text-blue-600'
                              }`}
                          >
                              <span className="text-sm font-medium">+ Add Printer Profile (Settings)</span>
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* OLD LICENSE MODAL - DISABLED IN FAVOR OF NEW REGISTRATION SYSTEM */}
      {false && (showLicenseModal || !isLicenseLoaded) && (
          <div className="fixed inset-0 bg-slate-950 z-[9999] flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 w-full max-w-lg rounded-2xl shadow-2xl p-8">
                  <div className="flex justify-center mb-6 h-12">
                      <Logo className="h-full w-auto" darkMode={true} />
                  </div>
                  
                  {!isLicenseLoaded ? (
                      <div className="text-center text-white flex flex-col items-center">
                          <Loader2 className="w-8 h-8 animate-spin mb-2 text-blue-500" />
                          <p>Loading License...</p>
                      </div>
                  ) : (
                    <>
                      {licenseStep === 'FORM' && (
                          <form onSubmit={handleLicenseSubmit} className="space-y-4">
                              <h2 className="text-xl font-bold text-center text-white mb-2">Activation Required</h2>
                              <p className="text-sm text-slate-400 text-center mb-6">Please enter your company details to generate your activation QR code.</p>
                              
                              <div className="grid grid-cols-2 gap-3">
                                  <input required placeholder="Contact Name" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full" value={licenseForm.name} onChange={e => setLicenseForm({...licenseForm, name: e.target.value})} />
                                  <input required placeholder="Company Name" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full" value={licenseForm.company} onChange={e => setLicenseForm({...licenseForm, company: e.target.value})} />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                  <div className="flex gap-2">
                                    <input required type="email" placeholder="Email Address" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full min-w-0" value={licenseForm.email} onChange={e => { setLicenseForm({...licenseForm, email: e.target.value}); setOtpSent(false); }} />
                                    <button 
                                        type="button" 
                                        onClick={handleSendOtp}
                                        disabled={isSendingOtp || otpSent || !licenseForm.email}
                                        className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap transition-colors ${otpSent ? 'bg-green-600 text-white cursor-default' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                                    >
                                        {isSendingOtp ? <Loader2 className="w-4 h-4 animate-spin"/> : (otpSent ? <Check className="w-4 h-4"/> : "Send Code")}
                                    </button>
                                  </div>
                                  <input required type="tel" placeholder="Phone Number" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full" value={licenseForm.phone} onChange={e => setLicenseForm({...licenseForm, phone: e.target.value})} />
                              </div>
                              
                              {otpSent && (
                                  <div className="animate-in fade-in slide-in-from-top-2">
                                      <input 
                                          required 
                                          placeholder="Enter 6-digit Verification Code" 
                                          className="bg-slate-800 border-2 border-green-500/50 text-white rounded p-3 text-sm w-full text-center tracking-widest font-mono" 
                                          value={otp} 
                                          onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))} 
                                      />
                                      <p className="text-[10px] text-slate-400 text-center mt-1">Check your email for the code</p>
                                  </div>
                              )}

                              <div className="grid grid-cols-3 gap-3">
                                  <input required placeholder="Full Address" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full col-span-2" value={licenseForm.address} onChange={e => setLicenseForm({...licenseForm, address: e.target.value})} />
                                  <input required placeholder="Postcode" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full font-mono uppercase" value={licenseForm.postcode} onChange={e => setLicenseForm({...licenseForm, postcode: e.target.value.toUpperCase()})} />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                  <input required placeholder="DVLA SID Number" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full" value={licenseForm.sid} onChange={e => setLicenseForm({...licenseForm, sid: e.target.value})} />
                                  <input required placeholder="License Key" className="bg-slate-800 border-slate-700 text-white rounded p-3 text-sm w-full font-mono uppercase" value={licenseForm.key} onChange={e => setLicenseForm({...licenseForm, key: e.target.value})} />
                              </div>
                              
                              {licenseError && <div className="text-red-500 text-sm text-center font-bold bg-red-900/20 p-2 rounded">{licenseError}</div>}
                              
                              <button type="submit" disabled={isOnlineVerification} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2">
                                  {isOnlineVerification ? <Loader2 className="animate-spin w-5 h-5"/> : <QrCode className="w-5 h-5" />}
                                  {isOnlineVerification ? "Checking Online..." : "Generate Activation QR"}
                              </button>

                                                            {!hasTrialUsed ? (
                                                                    <button 
                                                                        type="button"
                                                                        onClick={handleTrialActivation}
                                                                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 rounded-lg transition-all text-xs flex items-center justify-center gap-2"
                                                                    >
                                                                            <Timer className="w-4 h-4"/> Start 7-Day Professional Trial (Free)
                                                                    </button>
                                                            ) : (
                                                                    <div className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-center">
                                                                            <p className="text-xs text-slate-400 mb-2">Your 7-day trial has ended.</p>
                                                                            <p className="text-xs text-slate-500 mb-3">If you haven't had enough time to test the software, please contact us at <a href="https://regiq.co.uk" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">regiq.co.uk</a> and we may be able to offer an extension.</p>
                                                                            <a href="https://regiq.co.uk" target="_blank" rel="noreferrer" className="text-sm text-blue-400 hover:text-blue-300 font-bold">Purchase a License →</a>

                                                                            {/* Show HWID and linked email if available */}
                                                                            {debugMachineId && (
                                                                                <div className="mt-4 text-xs text-slate-400">
                                                                                    <span className="font-bold text-slate-300">Device ID (HWID):</span>
                                                                                    <span className="ml-1 select-all bg-slate-900 px-2 py-1 rounded cursor-pointer" title="Click to copy" onClick={() => {navigator.clipboard.writeText(debugMachineId)}}>{debugMachineId}</span>
                                                                                </div>
                                                                            )}
                                                                            {currentSession?.email && (
                                                                                <div className="mt-1 text-xs text-slate-400">
                                                                                    <span className="font-bold text-slate-300">Linked Email:</span>
                                                                                    <span className="ml-1 bg-slate-900 px-2 py-1 rounded">{currentSession.email}</span>
                                                                                </div>
                                                                            )}

                                                                            {/* Button to enter license key */}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { setShowLicenseModal(false); setShowUpgradeLicenseModal(true); }}
                                                                                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition-all text-xs flex items-center justify-center gap-2"
                                                                            >
                                                                                <KeyRound className="w-4 h-4"/> Enter License Key
                                                                            </button>
                                                                    </div>
                                                            )}
                          </form>
                      )}

                      {licenseStep === 'QR' && (
                          <div className="text-center space-y-6">
                              <h2 className="text-xl font-bold text-white">Scan to Activate</h2>
                              <div className="bg-white p-4 rounded-xl inline-block mx-auto shadow-lg">
                                  {qrCodeData && <img src={qrCodeData} alt="Activation QR" className="w-48 h-48" />}
                              </div>
                              <p className="text-sm text-slate-400 max-w-xs mx-auto">Scan this code with your phone. Enter the 4-digit code displayed on your device below.</p>
                              
                              <div className="flex justify-center">
                                <input 
                                    autoFocus
                                    placeholder="####" 
                                    maxLength={4}
                                    className="bg-slate-800 border-2 border-blue-500 text-white rounded-lg p-4 text-3xl font-mono text-center w-40 tracking-widest uppercase focus:outline-none focus:ring-4 focus:ring-blue-500/30" 
                                    value={activationCode} 
                                    onChange={e => setActivationCode(e.target.value)} 
                                />
                              </div>

                              {licenseError && <div className="text-red-500 text-sm font-bold animate-pulse">{licenseError}</div>}

                              <div className="flex gap-3">
                                  <button onClick={() => { setLicenseStep('FORM'); setLicenseError(''); }} className="flex-1 bg-slate-800 text-slate-300 py-3 rounded-lg font-bold hover:bg-slate-700">Back</button>
                                  <button 
                                    onClick={handleActivationVerify} 
                                    disabled={activationCode.length < 4 || isVerifyingLicense}
                                    className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
                                  >
                                      {isVerifyingLicense ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Activate'}
                                  </button>
                              </div>
                              <div className="text-[10px] text-slate-600 font-mono mt-4">
                                  Device ID: {debugMachineId}
                              </div>
                          </div>
                      )}
                    </>
                  )}
              </div>
          </div>
      )}

      {/* DEBUG BUTTON - REMOVED */}

      {/* TEMPLATE MODAL */}
      {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] print-hidden">
              <div className={`p-6 rounded-xl w-[400px] max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-white'}`}>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><FolderOpen className="w-5 h-5"/> Saved Templates</h3>
                  <div className="space-y-2 mb-4">
                      {templates.map(t => (
                          <div key={t.id} className={`flex justify-between items-center p-2 border rounded ${darkMode ? 'border-slate-700 hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
                              <span className="font-medium cursor-pointer flex-1" onClick={() => loadTemplate(t)}>{t.name}</span>
                              <button onClick={() => deleteTemplate(t.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 className="w-4 h-4"/></button>
                          </div>
                      ))}
                      {templates.length === 0 && <p className="text-slate-500 text-sm italic">No templates saved yet.</p>}
                  </div>
                  <div className="flex gap-2">
                      <input 
                          value={newTemplateName} 
                          onChange={e => setNewTemplateName(e.target.value)} 
                          placeholder="New Template Name" 
                          className={`flex-1 p-2 border rounded text-sm ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}
                      />
                      <button onClick={saveTemplate} disabled={!newTemplateName} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-blue-700 disabled:opacity-50">Save Current</button>
                  </div>
                  <button onClick={() => setShowTemplateModal(false)} className={`w-full mt-4 py-2 border rounded text-sm font-bold ${darkMode ? 'border-slate-600 hover:bg-slate-800' : 'hover:bg-slate-100'}`}>Close</button>
              </div>
          </div>
      )}

      {/* BADGE OPTIONS MODAL - positioned to not cover plate */}
      {false && (
        <div className="fixed inset-x-0 top-0 md:top-auto md:left-0 md:bottom-0 z-[100] print-hidden p-2 md:p-4 md:w-[28rem] pointer-events-none">
            <div className={`pointer-events-auto p-3 md:p-4 rounded-xl shadow-2xl max-h-[50vh] md:max-h-[60vh] overflow-y-auto border ${darkMode ? 'bg-slate-900 text-slate-100 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-sm font-bold flex items-center gap-2"><Flag className="w-4 h-4 text-blue-600"/> Badge Options</h2>
                    <button onClick={() => null} className="p-1"><X className="w-4 h-4 text-slate-400 hover:text-red-500"/></button>
                </div>
                <div className="space-y-3">
                    {/* Identifier Text - moved here */}
                    <div>
                        <label className="text-[10px] font-bold text-slate-500 mb-1 block">Identifier Text</label>
                        <div className="flex gap-2">
                            <input value={config.badge.identifierText} onChange={e => setConfig({...config, badge: {...config.badge, identifierText: e.target.value.toUpperCase()}})} className={`flex-1 p-2 border rounded text-sm uppercase font-mono font-bold text-center ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'border-slate-300'}`} placeholder="UK" />
                            <input type="color" value={config.badge.identifierTextColor || '#FFFFFF'} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextColor: e.target.value}})} className="w-10 h-9 rounded border cursor-pointer" title="Text Color"/>
                        </div>
                    </div>

                    {/* Add Graphic checkbox */}
                    <label className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                        <input type="checkbox" checked={config.showFlagGraphic} onChange={(e) => setConfig({...config, showFlagGraphic: e.target.checked})} className="w-4 h-4 rounded text-blue-600"/>
                        <span className="text-xs font-bold">Add Graphic (Ink)</span>
                    </label>
                    
                    {/* Badge Type */}
                    <div className="flex flex-wrap gap-1">
                        {[FlagType.UK, FlagType.SCO, FlagType.ENG, FlagType.CYM, FlagType.EU, FlagType.CUSTOM].map(f => (
                            <button key={f} onClick={async () => {
                                if (f === FlagType.CUSTOM) {
                                    setConfig({...config, badge: {...config.badge, type: f}});
                                    return;
                                }

                                try {
                                    const response = await fetch(`/flags/${f}.svg`);
                                    const blob = await response.blob();
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        const dataUrl = reader.result as string;
                                        setConfig({
                                            ...config, 
                                            badge: {
                                                ...config.badge, 
                                                type: f, 
                                                customSvgUrl: dataUrl,
                                                // Auto-set background for EU/EV if needed, though logic is mostly in PlateView
                                                backgroundColor: f === FlagType.EU ? 'BLUE' : config.badge.backgroundColor
                                            }
                                        });
                                    };
                                    reader.readAsDataURL(blob);
                                } catch (error) {
                                    console.error('Error loading flag:', error);
                                    // Fallback to just setting type if fetch fails
                                    setConfig({...config, badge: {...config.badge, type: f}});
                                }
                            }} className={`px-2 py-1 text-[10px] rounded border font-bold ${config.badge.type === f ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-800 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}>{f}</button>
                        ))}
                    </div>
                    
                    {config.badge.type === FlagType.CUSTOM && (
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <input 
                                    placeholder="Paste SVG or Image URL" 
                                    value={config.badge.customSvgUrl} 
                                    onChange={e => setConfig({...config, badge: {...config.badge, customSvgUrl: e.target.value}})} 
                                    className={`flex-1 p-2 border rounded text-xs ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`} 
                                />
                                <label className={`cursor-pointer border border-dashed rounded p-2 text-xs text-center transition-colors flex items-center gap-1 ${darkMode ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}>
                                    <ImageIcon className="w-3 h-3" />
                                    <span className="hidden sm:inline">Browse</span>
                                    <input 
                                        type="file" 
                                        accept="image/*,.svg" 
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            
                                            try {
                                                // On mobile, use Capacitor Filesystem to read the file
                                                if (PrinterBridge.isMobile) {
                                                    // For Android, we need to convert the file to a data URL
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        const dataUrl = reader.result as string;
                                                        setConfig({...config, badge: {...config.badge, customSvgUrl: dataUrl}});
                                                    };
                                                    reader.onerror = () => {
                                                        alert('Failed to read image file');
                                                    };
                                                    reader.readAsDataURL(file);
                                                } else {
                                                    // For web/Electron, use FileReader
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        const dataUrl = reader.result as string;
                                                        setConfig({...config, badge: {...config.badge, customSvgUrl: dataUrl}});
                                                    };
                                                    reader.onerror = () => {
                                                        alert('Failed to read image file');
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            } catch (error: any) {
                                                console.error('Error loading image:', error);
                                                alert('Failed to load image: ' + (error.message || 'Unknown error'));
                                            }
                                        }} 
                                        className="hidden" 
                                    />
                                </label>
                            </div>
                            {config.badge.customSvgUrl && (
                                <div className="text-[10px] text-slate-500">
                                    {config.badge.customSvgUrl.startsWith('data:') ? 'Image loaded from file' : 'Using URL'}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Background Color */}
                    <div className="flex gap-1">
                        {['BLUE', 'GREEN', 'BLACK', 'NONE'].map(c => (
                            <button key={c} onClick={() => setConfig({...config, badge: {...config.badge, backgroundColor: c as any}})} className={`flex-1 py-1.5 text-[10px] rounded border font-bold ${config.badge.backgroundColor === c ? 'ring-2 ring-offset-1 ring-blue-500' : ''} ${c === 'BLUE' ? 'bg-blue-700 text-white' : c === 'GREEN' ? 'bg-green-600 text-white' : c === 'BLACK' ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 border-slate-300'}`}>{c}</button>
                        ))}
                    </div>
                    
                    {/* Options Row */}
                    <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={config.badge.isVertical} onChange={e => setConfig({...config, badge: {...config.badge, isVertical: e.target.checked}})} className="w-4 h-4 rounded text-blue-600"/>
                            Vertical
                        </label>
                        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-500 cursor-pointer">
                            <input type="checkbox" checked={config.badge.showIdentifier} onChange={e => setConfig({...config, badge: {...config.badge, showIdentifier: e.target.checked}})} className="w-4 h-4 rounded text-blue-600"/>
                            Show Text
                        </label>
                    </div>
                    
                    {/* Text Size/Position */}
                    {config.badge.showIdentifier && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block">Text Size: {config.badge.identifierTextSize || 14}</label>
                                <input type="range" min="8" max="50" step="1" value={config.badge.identifierTextSize || 14} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextSize: parseInt(e.target.value)}})} className="w-full"/>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block">Text Y: {config.badge.identifierTextVOffset || 0}</label>
                                <input type="range" min="-20" max="20" step="1" value={config.badge.identifierTextVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextVOffset: parseInt(e.target.value)}})} className="w-full"/>
                            </div>
                        </div>
                    )}
                    
                    {/* Graphic Settings */}
                    {config.badge.type !== FlagType.EV && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block">Graphic Size: {(config.badge.graphicScale || 1).toFixed(1)}</label>
                                <input type="range" min="0" max="2.0" step="0.1" value={config.badge.graphicScale || 1} onChange={e => setConfig({...config, badge: {...config.badge, graphicScale: parseFloat(e.target.value)}})} className="w-full"/>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 block">Graphic Y: {config.badge.graphicVOffset || 0}</label>
                                <input type="range" min="-20" max="20" step="1" value={config.badge.graphicVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, graphicVOffset: parseFloat(e.target.value)}})} className="w-full"/>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
      )}

      {showBorderSettingsModal && (
        <div className="fixed inset-x-0 top-0 md:top-auto md:left-0 md:bottom-0 z-[100] print-hidden p-2 md:p-4 md:w-[28rem] pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className={`pointer-events-auto p-3 md:p-4 rounded-xl shadow-2xl max-h-[50vh] md:max-h-[60vh] overflow-y-auto border ${darkMode ? 'bg-slate-900 text-slate-100 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-bold flex items-center gap-2"><Maximize className="w-4 h-4"/> Border Settings</h2>
              <button onClick={() => setShowBorderSettingsModal(false)} className="p-1"><X className="w-4 h-4 text-slate-400 hover:text-red-500"/></button>
            </div>

            <div className="space-y-3">
              <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                <label className="text-xs font-bold text-slate-500">Enable Border</label>
                <input type="checkbox" checked={config.border} onChange={e => setConfig({...config, border: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
              </div>

              <div className={`${!config.border ? 'opacity-50 pointer-events-none' : ''} space-y-3`}>
                <div className={`p-3 rounded border space-y-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                      <span>Stroke</span>
                      <span className="text-slate-400">{config.borderThickness}mm</span>
                    </label>
                    <input type="range" min="0.5" max="10" step="0.5" value={config.borderThickness} onChange={e => setConfig({...config, borderThickness: Number(e.target.value)})} className="w-full"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                      <span>Margin</span>
                      <span className="text-slate-400">{config.borderMargin}mm</span>
                    </label>
                    <input type="range" min="0" max="50" step="1" value={config.borderMargin} onChange={e => setConfig({...config, borderMargin: Number(e.target.value)})} className="w-full"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                      <span>Corner Radius</span>
                      <span className="text-slate-400">{config.borderCornerRadius ?? 10}mm</span>
                    </label>
                    <input type="range" min="0" max="10" step="1" value={config.borderCornerRadius ?? 10} onChange={e => setConfig({...config, borderCornerRadius: Number(e.target.value)})} className="w-full"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                      <span>Color</span>
                      <span className="text-slate-400">{config.borderColor}</span>
                    </label>
                    <input type="color" value={config.borderColor} onChange={e => setConfig({...config, borderColor: e.target.value})} className="w-full h-8 border rounded cursor-pointer"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMobile && showBadgeSettingsModal && (
        <div className="fixed inset-x-0 top-0 z-[100] print-hidden p-2 pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className={`pointer-events-auto p-3 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto border ${darkMode ? 'bg-slate-900 text-slate-100 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-bold flex items-center gap-2"><Flag className="w-4 h-4 text-blue-600"/> Badge / Flag</h2>
              <button onClick={() => setShowBadgeSettingsModal(false)} className="p-1"><X className="w-4 h-4 text-slate-400 hover:text-red-500"/></button>
            </div>

            <div className={`p-3 rounded-lg border space-y-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              {/* Enable Badge */}
              <div className="flex justify-between items-center">
                <label className="text-sm font-bold text-slate-500">Enable Badge</label>
                <input type="checkbox" checked={config.badge.enabled} onChange={e => handleBadgeToggle(e.target.checked)} className="w-5 h-5 accent-blue-600"/>
              </div>

              {config.badge.enabled && (<div className="space-y-3">
                {/* Show Graphic checkbox */}
                <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                  <label className="text-xs font-bold text-slate-500">Show Graphic</label>
                  <input type="checkbox" checked={config.showFlagGraphic} onChange={e => setConfig({...config, showFlagGraphic: e.target.checked})} className="toggle-checkbox"/>
                </div>

                {/* Graphic settings box - greyed out unless Show Graphic is ticked */}
                <div className={`p-3 rounded border space-y-3 transition-opacity ${config.showFlagGraphic ? '' : 'opacity-40 pointer-events-none'} ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                  {/* Flag style icons - single row, no labels */}
                  <div className="flex gap-1.5">
                    {['UK', 'SCO', 'ENG', 'CYM', 'EU', 'CUSTOM'].map(ft => (
                      <button
                        key={ft}
                        onClick={async () => {
                          if (ft === 'CUSTOM') {
                            setConfig({...config, badge: {...config.badge, type: ft as FlagType}});
                            return;
                          }
                          try {
                            const response = await fetch(`/flags/${ft}.svg`);
                            const blob = await response.blob();
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const dataUrl = reader.result as string;
                              setConfig({...config, badge: {...config.badge, type: ft as FlagType, customSvgUrl: dataUrl}});
                            };
                            reader.readAsDataURL(blob);
                          } catch (error) {
                            setConfig({...config, badge: {...config.badge, type: ft as FlagType}});
                          }
                        }}
                        className={`flex-1 h-14 flex items-center justify-center rounded border transition-all ${config.badge.type === ft ? 'ring-2 ring-blue-500 border-blue-600' : (darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-300')}`}
                      >
                        {ft === 'CUSTOM' ? (
                          <span className="text-[9px] font-bold">Custom</span>
                        ) : (
                          <img src={`/flags/${ft}.svg`} alt={ft} className="h-10 w-auto" style={{ objectFit: 'contain' }} />
                        )}
                      </button>
                    ))}
                  </div>

                  {config.badge.type === 'CUSTOM' && (
                    <div className={`relative border-dashed border-2 rounded-lg p-2 text-center cursor-pointer transition-colors ${darkMode ? 'bg-slate-800 border-slate-600 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'}`}>
                      <span className="text-xs text-slate-500">Upload custom badge</span>
                      <input type="file" accept="image/*" onChange={handleBadgeUpload} className="absolute inset-0 opacity-0 cursor-pointer"/>
                    </div>
                  )}

                  <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                    <label className="text-xs font-bold text-slate-500">Vertical Orientation</label>
                    <input type="checkbox" checked={config.badge.isVertical} onChange={e => setConfig({...config, badge: {...config.badge, isVertical: e.target.checked}})} className="toggle-checkbox" />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Graphic Size</span><span className="text-slate-400">{(config.badge.graphicScale || 1).toFixed(1)}</span></label>
                    <input type="range" min="0.5" max="2.5" step="0.1" value={config.badge.graphicScale || 1} onChange={e => setConfig({...config, badge: {...config.badge, graphicScale: parseFloat(e.target.value)}})} className="w-full"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Graphic Vertical</span><span className="text-slate-400">{config.badge.graphicVOffset || 0}</span></label>
                    <input type="range" min="-20" max="20" step="1" value={config.badge.graphicVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, graphicVOffset: parseFloat(e.target.value)}})} className="w-full"/>
                  </div>
                </div>

                {/* Identifier Text checkbox */}
                <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                  <label className="text-xs font-bold text-slate-500">Show Identifier Text</label>
                  <input type="checkbox" checked={config.badge.showIdentifier} onChange={e => setConfig({...config, badge: {...config.badge, showIdentifier: e.target.checked}})} className="toggle-checkbox" />
                </div>

                {config.badge.showIdentifier && (
                  <div className={`p-3 rounded border space-y-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                    <div className="flex gap-2">
                      <input type="text" value={config.badge.identifierText} onChange={e => setConfig({...config, badge: {...config.badge, identifierText: e.target.value.toUpperCase()}})} className={`flex-1 p-1.5 border rounded text-xs uppercase ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`} placeholder="UK"/>
                      <input type="color" value={config.badge.identifierTextColor || '#ffffff'} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextColor: e.target.value}})} className="w-8 h-8 rounded border p-0 overflow-hidden"/>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Text Size</span><span className="text-slate-400">{config.badge.identifierTextSize || 14}</span></label>
                      <input type="range" min="8" max="50" step="1" value={config.badge.identifierTextSize || 14} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextSize: parseInt(e.target.value)}})} className="w-full"/>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Text Vertical</span><span className="text-slate-400">{config.badge.identifierTextVOffset || 0}</span></label>
                      <input type="range" min="-20" max="20" step="1" value={config.badge.identifierTextVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextVOffset: parseInt(e.target.value)}})} className="w-full"/>
                    </div>
                  </div>
                )}

                {/* Background colour */}
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block">Background</label>
                  <div className="flex gap-2">
                    {['BLUE', 'GREEN', 'BLACK', 'NONE'].map((c: any) => (
                      <button key={c} onClick={() => setConfig({...config, badge: {...config.badge, backgroundColor: c}})} className={`flex-1 h-6 rounded border ${config.badge.backgroundColor === c ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`} style={{ backgroundColor: c === 'NONE' ? 'transparent' : c.toLowerCase(), backgroundImage: c === 'NONE' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none', backgroundSize: '10px 10px' }} title={c}/>
                    ))}
                  </div>
                </div>
              </div>)}
            </div>
          </div>
        </div>
      )}

      {/* PRINTER SETTINGS MODAL */}
      {showPrinterSettings && (
        <div
          id="settings-modal-backdrop"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] print-hidden"
          style={{ pointerEvents: 'auto', zIndex: 100, touchAction: 'none', overscrollBehavior: 'none' }}
          onClick={(e) => {
            e.stopPropagation();
            // Close modal on backdrop click, block all other clicks from going through
            if (e.target === e.currentTarget) {
              setShowPrinterSettings(false);
              setEditingProfile(null);
            }
          }}
          onMouseDown={(e) => {
            // Block all mouse events
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              setShowPrinterSettings(false);
              setEditingProfile(null);
            }
          }}
          onTouchStart={(e) => {
            // Block all touch events on mobile
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              setShowPrinterSettings(false);
              setEditingProfile(null);
            }
          }}
        >
            <div 
              className={`p-6 rounded-xl w-[500px] shadow-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-white'}`}
              style={{ pointerEvents: 'auto', zIndex: 101 }}
              onClick={(e) => {
                // Prevent clicks inside modal from bubbling up
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Prevent all mouse events from reaching elements beneath
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                // Prevent all touch events from reaching elements beneath on mobile
                e.stopPropagation();
              }}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold flex items-center gap-2"><Printer className="w-6 h-6"/> Printer Profiles <ProBadge /></h2>
                    <button onClick={() => { setShowPrinterSettings(false); setEditingProfile(null); }}><X className="w-5 h-5 text-slate-400 hover:text-red-500"/></button>
                </div>
                {/* License info - shown on mobile */}
                {isMobile && currentUser?.license && (
                  <div className={`flex flex-col gap-2 p-3 rounded-lg text-xs mb-4 ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100 border border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      {(() => {
                        const daysRemaining = Math.ceil((currentUser.license.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
                        const isExpiringSoon = daysRemaining <= 30;
                        return (
                          <>
                            <span className={`font-mono ${isExpiringSoon ? 'text-red-500 font-bold' : 'text-slate-400'}`}>
                              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                            </span>
                            <span className={`font-mono font-bold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              {currentUser.license.isTrial ? 'Trial' : currentUser.license.tier}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    {currentUser.license.isTrial && (
                      <button
                        onClick={() => {
                          setShowPrinterSettings(false);
                          setShowUpgradeLicenseModal(true);
                        }}
                        className={`w-full py-2 px-3 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-2 ${
                          darkMode 
                            ? 'bg-green-600 hover:bg-green-500 text-white' 
                            : 'bg-green-500 hover:bg-green-600 text-white'
                        }`}
                      >
                        <Key className="w-4 h-4" />
                        Enter License Key to Upgrade
                      </button>
                    )}
                  </div>
                )}
                <div className="space-y-4 mb-6">
                    {profiles.map(p => (
                        <div key={p.id} className={`border rounded-lg p-3 relative group ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            {editingProfile?.id === p.id ? (
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <input value={editingProfile.name} onChange={e => setEditingProfile({...editingProfile, name: e.target.value})} className={`flex-1 p-2 border rounded text-sm font-bold ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`} placeholder="Nickname"/>
                                        {PrinterBridge.isElectron && (
                                          <div className="flex gap-2 flex-1">
                                            <select value={editingProfile.systemPrinterName} onChange={e => setEditingProfile({...editingProfile, systemPrinterName: e.target.value})} className={`flex-1 p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                                                <option value="">Select Printer...</option>
                                                {availableSystemPrinters.map(ap => <option key={ap.name} value={ap.name}>{ap.name}</option>)}
                                            </select>
                                            <button 
                                                onClick={refreshSystemPrinters} 
                                                className={`p-2 border rounded ${darkMode ? 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                title="Refresh printer list"
                                            >
                                                <RefreshCw className="w-4 h-4" />
                                            </button>
                                          </div>
                                        )}
                                        {PrinterBridge.isElectron && availableSystemPrinters.length === 0 && (
                                          <div className={`text-xs p-2 rounded ${darkMode ? 'bg-amber-900/20 text-amber-300 border border-amber-800' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                            No printers found. Click the refresh button to scan for printers, or make sure your printer is connected and turned on.
                                          </div>
                                        )}
                                        {PrinterBridge.isMobile && (
                                          <div className="flex flex-col gap-2 flex-1">
                                            <div className="flex flex-col">
                                                <label className="text-[10px] font-bold text-slate-500 mb-1">Printer Driver</label>
                                                <select
                                                    value={editingProfile.driverType || 'meena_lite'}
                                                    onChange={e => {
                                                        const newDriver = e.target.value as any;
                                                        // Apply preset settings for Meena Lite
                                                        if (newDriver === 'meena_lite') {
                                                            setEditingProfile({
                                                                ...editingProfile,
                                                                driverType: newDriver,
                                                                printerLanguage: 'TSPL',
                                                                compress: true,
                                                                cutAfterPrint: false,
                                                                feedEnabled: true,
                                                                feedAmount: 15,
                                                                rotate: true,
                                                                dpi: 200
                                                            });
                                                        } else {
                                                            setEditingProfile({...editingProfile, driverType: newDriver});
                                                        }
                                                    }}
                                                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                >
                                                    <option value="meena_lite">Meena Lite (Thermal)</option>
                                                    <option value="godex">Godex (EZio SDK)</option>
                                                    <option value="tsc">TSC (TSPL)</option>
                                                    <option value="citizen">Citizen (TSPL/CPCL)</option>
                                                    <option value="oki_usb">OKI Laser (USB/Network)</option>
                                                </select>
                                            </div>

                                            <div className="flex flex-col">
                                                <label className="text-[10px] font-bold text-slate-500 mb-1">Connection Method</label>
                                                <select
                                                    value={editingProfile.driverType === 'oki_usb'
                                                        ? (editingProfile.connectionType === 'wifi' ? 'wifi' : 'usb_oki')
                                                        : (editingProfile.connectionType || 'bluetooth')}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        // For OKI, map to appropriate connection type
                                                        if (editingProfile.driverType === 'oki_usb') {
                                                            setEditingProfile({...editingProfile, connectionType: val as any});
                                                        } else {
                                                            setEditingProfile({...editingProfile, connectionType: val as any});
                                                        }
                                                    }}
                                                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                >
                                                    {editingProfile.driverType === 'oki_usb' ? (
                                                        <>
                                                            <option value="usb_oki">USB</option>
                                                            <option value="wifi">Network (IP)</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value="bluetooth">Bluetooth</option>
                                                            <option value="usb">USB</option>
                                                            <option value="wifi">WiFi / Network</option>
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                          </div>
                                        )}
                                    </div>

                                    {PrinterBridge.isMobile && (
                                      <div className={`p-3 rounded border space-y-3 ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <div className="flex items-center justify-between">
                                          <label className="text-[10px] font-bold text-slate-500">Mobile Connectivity</label>
                                          {editingProfile.driverType === 'tsc' && (
                                            <select
                                              value={editingProfile.printerLanguage || 'TSPL'}
                                              onChange={e => setEditingProfile({...editingProfile, printerLanguage: e.target.value as any})}
                                              className={`p-1 text-[10px] border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                            >
                                              <option value="TSPL">TSPL (Generic)</option>
                                              <option value="CPCL">CPCL (Zebra/TSC)</option>
                                              <option value="ESC">ESC/POS (Epson/Receipt)</option>
                                            </select>
                                          )}
                                          {editingProfile.driverType === 'oki_usb' && (
                                            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold">PJL/PDF Direct</span>
                                          )}
                                          {editingProfile.driverType === 'meena_lite' && (
                                            <span className="text-[10px] text-purple-600 dark:text-purple-400 font-bold">TSPL</span>
                                          )}
                                        </div>

                                        {(!editingProfile.connectionType || editingProfile.connectionType === 'bluetooth') && (
                                          <div className="space-y-2">
                                            <div className="flex gap-2">
                                              <input 
                                                value={editingProfile.androidAddress || ''} 
                                                onChange={e => setEditingProfile({...editingProfile, androidAddress: e.target.value})}
                                                className={`flex-1 p-2 border rounded text-xs ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`} 
                                                placeholder="MAC Address (e.g. 00:11:22:33:44:55)"
                                              />
                                              <button 
                                                onClick={scanBluetooth}
                                                disabled={isScanningBluetooth}
                                                className={`p-2 rounded border flex items-center gap-1 text-xs font-bold transition-all ${darkMode ? 'bg-blue-900/20 border-blue-700 text-blue-400 hover:bg-blue-900/30' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}
                                              >
                                                {isScanningBluetooth ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                                                Scan
                                              </button>
                                            </div>
                                            {bluetoothDevices.length > 0 && (
                                              <div className={`max-h-32 overflow-y-auto rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                {bluetoothDevices.map(dev => (
                                                  <button 
                                                    key={dev.address}
                                                    onClick={() => setEditingProfile({...editingProfile, androidAddress: dev.address, name: editingProfile.name || dev.name})}
                                                    className={`w-full text-left p-2 text-xs border-b last:border-0 hover:bg-blue-500/10 ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-700'}`}
                                                  >
                                                    <div className="font-bold">{dev.name || 'Unknown Device'}</div>
                                                    <div className="opacity-50">{dev.address}</div>
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {editingProfile.connectionType === 'wifi' && (
                                          <div className="flex gap-2">
                                            <input 
                                              value={editingProfile.androidAddress || ''} 
                                              onChange={e => setEditingProfile({...editingProfile, androidAddress: e.target.value})}
                                              className={`flex-1 p-2 border rounded text-xs ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`} 
                                              placeholder="IP Address (e.g. 192.168.1.100)"
                                            />
                                            <input 
                                              value={editingProfile.androidPort || '9100'} 
                                              onChange={e => setEditingProfile({...editingProfile, androidPort: e.target.value})}
                                              className={`w-20 p-2 border rounded text-xs ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`} 
                                              placeholder="Port"
                                            />
                                          </div>
                                        )}

                                        {editingProfile.connectionType === 'usb' && (
                                          <div className="text-[10px] text-slate-500 italic p-1">
                                            Connect your thermal printer via USB OTG cable.
                                          </div>
                                        )}

                                        {editingProfile.driverType === 'oki_usb' && (
                                          <div className={`space-y-3 p-3 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                            <div className="text-[10px] text-blue-600 dark:text-blue-400 font-bold mb-2">OKI B432 Laser Printer Settings</div>

                                            {/* Network IP Address (only for wifi connection) */}
                                            {editingProfile.connectionType === 'wifi' && (
                                              <div>
                                                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Printer IP Address</label>
                                                <input
                                                  type="text"
                                                  value={editingProfile.androidAddress || ''}
                                                  onChange={e => setEditingProfile({...editingProfile, androidAddress: e.target.value})}
                                                  placeholder="e.g. 192.168.1.100"
                                                  className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                                />
                                              </div>
                                            )}
                                            
                                            {/* Media Source */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Media Source</label>
                                              <select 
                                                value={editingProfile.okiMediaSource || 'AUTO'} 
                                                onChange={e => setEditingProfile({...editingProfile, okiMediaSource: e.target.value as any})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                <option value="AUTO">AUTO (Default)</option>
                                                <option value="TRAY1">TRAY1 (Main tray)</option>
                                                <option value="TRAY2">TRAY2 (2nd tray)</option>
                                                <option value="MPTRAY">MPTRAY (Multi-Purpose)</option>
                                                <option value="MANUALFEED">MANUALFEED (Front slot)</option>
                                              </select>
                                            </div>

                                            {/* Media Weight */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Media Weight (Fuser Temp)</label>
                                              <select 
                                                value={editingProfile.okiMediaWeight || 'ULTRAHEAVY'} 
                                                onChange={e => setEditingProfile({...editingProfile, okiMediaWeight: e.target.value as any})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                <option value="LIGHT">LIGHT (60-74g/m²)</option>
                                                <option value="MEDIUM">MEDIUM (75-90g/m²)</option>
                                                <option value="HEAVY">HEAVY (91-120g/m²)</option>
                                                <option value="ULTRAHEAVY">ULTRAHEAVY (121-163g/m² - Best for artwork)</option>
                                              </select>
                                            </div>

                                            {/* Media Type */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Media Type</label>
                                              <select 
                                                value={editingProfile.okiMediaType || 'PLAIN'} 
                                                onChange={e => setEditingProfile({...editingProfile, okiMediaType: e.target.value as any})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                <option value="PLAIN">PLAIN</option>
                                                <option value="LABELS">LABELS</option>
                                                <option value="BOND">BOND</option>
                                                <option value="RECYCLED">RECYCLED</option>
                                                <option value="CARDSTOCK">CARDSTOCK</option>
                                                <option value="ROUGH">ROUGH</option>
                                              </select>
                                            </div>

                                            {/* Paper Size */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Paper Size</label>
                                              <select
                                                value={editingProfile.okiPaperSize || 'A4'}
                                                onChange={e => {
                                                  const size = e.target.value as keyof typeof OKI_PAPER_SIZES;
                                                  const preset = OKI_PAPER_SIZES[size];
                                                  if (preset && size !== 'CUSTOM' && size !== 'FLEXI') {
                                                    setEditingProfile({
                                                      ...editingProfile,
                                                      okiPaperSize: size,
                                                      okiCustomPaperWidth: preset.width,
                                                      okiCustomPaperLength: preset.length
                                                    });
                                                  } else {
                                                    setEditingProfile({...editingProfile, okiPaperSize: size});
                                                  }
                                                }}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                {Object.entries(OKI_PAPER_SIZES).map(([key, { label }]) => (
                                                  <option key={key} value={key}>{label}</option>
                                                ))}
                                              </select>
                                            </div>

                                            {/* Paper Dimensions (shown for all except FLEXI) */}
                                            {editingProfile.okiPaperSize !== 'FLEXI' && (
                                              <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Width (mm)</label>
                                                  <NumericInput
                                                    value={editingProfile.okiCustomPaperWidth || OKI_PAPER_SIZES[editingProfile.okiPaperSize || 'A4']?.width || 210}
                                                    onChange={v => setEditingProfile({...editingProfile, okiCustomPaperWidth: v, okiPaperSize: 'CUSTOM'})}
                                                    className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-[10px] font-bold text-slate-500 mb-1 block">Length (mm)</label>
                                                  <NumericInput
                                                    value={editingProfile.okiCustomPaperLength || OKI_PAPER_SIZES[editingProfile.okiPaperSize || 'A4']?.length || 297}
                                                    onChange={v => setEditingProfile({...editingProfile, okiCustomPaperLength: v, okiPaperSize: 'CUSTOM'})}
                                                    className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                                  />
                                                </div>
                                              </div>
                                            )}

                                            {/* Density */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Print Density</label>
                                              <select
                                                value={editingProfile.okiDensity || 3}
                                                onChange={e => setEditingProfile({...editingProfile, okiDensity: Number(e.target.value) as 1|2|3|4|5})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                <option value={1}>1 - Lightest</option>
                                                <option value={2}>2 - Light</option>
                                                <option value={3}>3 - Normal</option>
                                                <option value={4}>4 - Dark</option>
                                                <option value={5}>5 - Darkest</option>
                                              </select>
                                            </div>

                                            {/* Resolution */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Resolution</label>
                                              <select 
                                                value={editingProfile.okiResolution || 1200} 
                                                onChange={e => setEditingProfile({...editingProfile, okiResolution: Number(e.target.value) as 600 | 1200})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300'}`}
                                              >
                                                <option value={600}>600 DPI</option>
                                                <option value={1200}>1200 DPI (Best for artwork)</option>
                                              </select>
                                            </div>

                                            {/* Orientation */}
                                            <div>
                                              <label className="text-[10px] font-bold text-slate-500 mb-1 block">Orientation</label>
                                              <div className="grid grid-cols-2 gap-2">
                                                <button
                                                  onClick={() => setEditingProfile({...editingProfile, okiOrientation: 'PORTRAIT'})}
                                                  className={`px-2 py-1.5 text-[10px] rounded border ${editingProfile.okiOrientation === 'PORTRAIT' || !editingProfile.okiOrientation ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-800 border-slate-700 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                                >
                                                  PORTRAIT
                                                </button>
                                                <button
                                                  onClick={() => setEditingProfile({...editingProfile, okiOrientation: 'LANDSCAPE'})}
                                                  className={`px-2 py-1.5 text-[10px] rounded border ${editingProfile.okiOrientation === 'LANDSCAPE' ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-800 border-slate-700 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                                >
                                                  LANDSCAPE
                                                </button>
                                              </div>
                                            </div>

                                          </div>
                                        )}

                                        <button
                                          onClick={() => connectToAndroidPrinter(editingProfile)}
                                          disabled={isConnectingAndroid}
                                          className={`w-full p-2 rounded border flex items-center justify-center gap-2 text-xs font-bold transition-all ${darkMode ? 'bg-slate-800 border-slate-700 text-white hover:bg-slate-700' : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'}`}
                                        >
                                          {isConnectingAndroid ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Usb className="w-4 h-4" />}
                                          {androidConnectionStatus || 'Test Connection'}
                                        </button>
                                      </div>
                                    )}

                                    {/* Printer Command Settings - Mobile Only (TSPL/thermal printers only, not OKI) */}
                                    {PrinterBridge.isMobile && (editingProfile as any).driverType !== 'oki_usb' && (
                                      <div className={`p-3 rounded border space-y-3 ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="text-[10px] font-bold text-slate-500 block">Printer Command Settings</label>

                                        {/* Media Size Mode */}
                                        <div>
                                          <label className="text-[9px] font-bold text-slate-500 block mb-1">Media Size Mode</label>
                                          <select
                                            value={editingProfile.mediaSize || 'FIXED'}
                                            onChange={e => setEditingProfile({...editingProfile, mediaSize: e.target.value as 'FLEXIBLE' | 'FIXED'})}
                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                          >
                                            <option value="FIXED">Fixed (use page size settings below)</option>
                                            <option value="FLEXIBLE">Flexi (dispense only artwork length)</option>
                                          </select>
                                          <p className="text-[8px] text-slate-400 mt-1">
                                            {editingProfile.mediaSize === 'FLEXIBLE'
                                              ? 'Printer will only dispense the length needed for the artwork - ideal for roll media.'
                                              : 'Printer will use the fixed page dimensions specified below.'}
                                          </p>
                                        </div>

                                        {editingProfile.driverType === 'godex' && (
                                            <div className="p-2 my-2 bg-orange-500/20 rounded border-2 border-orange-500 space-y-2">
                                                <label className="text-[10px] font-bold text-orange-600 block">GODEX PRINT MODE</label>
                                                <select
                                                    value={editingProfile.godexPdfMode || 'ezpl_hex'}
                                                    onChange={e => setEditingProfile({...editingProfile, godexPdfMode: e.target.value as any})}
                                                    className={`w-full p-2 text-sm font-bold border-2 rounded ${darkMode ? 'bg-slate-800 border-orange-500 text-white' : 'bg-white border-orange-500 text-slate-800'}`}
                                                >
                                                    <option value="ezpl_hex">EZPL (Native Godex)</option>
                                                    <option value="gepl">GEPL (EPL2 Emulation)</option>
                                                    <option value="sdk">SDK Direct</option>
                                                    <option value="legacy_bitmap">Legacy Bitmap</option>
                                                </select>
                                                <p className="text-[8px] text-orange-500">Select print language mode for your Godex printer</p>

                                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-orange-300/30">
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 block mb-1">Print Speed</label>
                                                        <NumericInput
                                                            value={editingProfile.godexSpeed || 0}
                                                            onChange={v => setEditingProfile({...editingProfile, godexSpeed: v})}
                                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                            placeholder="Default"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 block mb-1">Darkness (0-19)</label>
                                                        <NumericInput
                                                            value={editingProfile.godexDarkness ?? -1}
                                                            onChange={v => setEditingProfile({...editingProfile, godexDarkness: v})}
                                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                            placeholder="Default"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {editingProfile.driverType === 'godex' && (
                                          <div className={`p-2 rounded border space-y-2 ${darkMode ? 'bg-slate-950 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                            <label className="text-[9px] font-bold text-slate-500 block">Raw Command (Godex)</label>
                                            <textarea
                                              value={godexRawCommand}
                                              onChange={(e) => setGodexRawCommand(e.target.value)}
                                              className={`w-full p-1.5 text-[10px] font-mono border rounded outline-none ${darkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-800'}`}
                                              rows={3}
                                            />
                                            <button
                                              onClick={sendGodexRawCommand}
                                              disabled={godexRawSending}
                                              className={`w-full p-1.5 rounded text-[10px] font-bold ${godexRawSending ? 'bg-blue-600/60 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                                            >
                                              {godexRawSending ? 'Sending…' : 'Send Raw Command'}
                                            </button>
                                            {godexRawResult && (
                                              <div className={`text-[10px] font-mono whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                {godexRawResult}
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* Citizen Thermal Printer Settings */}
                                        {editingProfile.driverType === 'citizen' && (
                                            <div className="p-2 my-2 bg-green-500/20 rounded border-2 border-green-500 space-y-2">
                                                <label className="text-[10px] font-bold text-green-600 block">CITIZEN PRINTER SETTINGS</label>

                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 block mb-1">Print Speed (IPS)</label>
                                                        <select
                                                            value={editingProfile.citizenSpeed || 0}
                                                            onChange={e => setEditingProfile({...editingProfile, citizenSpeed: Number(e.target.value)})}
                                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                        >
                                                            <option value={0}>Default</option>
                                                            <option value={1}>1 IPS (Slow)</option>
                                                            <option value={2}>2 IPS</option>
                                                            <option value={3}>3 IPS</option>
                                                            <option value={4}>4 IPS</option>
                                                            <option value={5}>5 IPS</option>
                                                            <option value={6}>6 IPS (Fast)</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 block mb-1">Darkness (-5 to +5)</label>
                                                        <NumericInput
                                                            min={-5}
                                                            max={5}
                                                            value={editingProfile.citizenDarkness || 0}
                                                            onChange={v => setEditingProfile({...editingProfile, citizenDarkness: v})}
                                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="text-[9px] font-bold text-slate-500 block mb-1">Sensor Type</label>
                                                    <select
                                                        value={editingProfile.citizenSensorType || 'gap'}
                                                        onChange={e => setEditingProfile({...editingProfile, citizenSensorType: e.target.value as 'gap' | 'blackmark' | 'continuous'})}
                                                        className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                    >
                                                        <option value="gap">Gap (See-through sensor for label gaps)</option>
                                                        <option value="blackmark">Black Mark (Reflective sensor)</option>
                                                        <option value="continuous">Continuous (No sensor / receipt paper)</option>
                                                    </select>
                                                </div>

                                                {/* Continuous Media Settings - only shown when continuous sensor is selected */}
                                                {editingProfile.citizenSensorType === 'continuous' && (
                                                    <div className="p-2 bg-yellow-500/20 rounded border border-yellow-500 space-y-2">
                                                        <label className="text-[9px] font-bold text-yellow-600 block">CONTINUOUS MEDIA SETTINGS</label>
                                                        <div>
                                                            <label className="text-[9px] font-bold text-slate-500 block mb-1">Roll Width (mm)</label>
                                                            <NumericInput
                                                                min={10}
                                                                max={300}
                                                                value={editingProfile.citizenRollWidth || 80}
                                                                onChange={v => setEditingProfile({...editingProfile, citizenRollWidth: v})}
                                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                            />
                                                            <p className="text-[8px] text-slate-400 mt-1">Width of the continuous roll/receipt paper</p>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div>
                                                                <label className="text-[9px] font-bold text-slate-500 block mb-1">Feed Before (mm)</label>
                                                                <NumericInput
                                                                    min={0}
                                                                    max={50}
                                                                    value={editingProfile.citizenFeedBefore || 0}
                                                                    onChange={v => setEditingProfile({...editingProfile, citizenFeedBefore: v})}
                                                                    className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[9px] font-bold text-slate-500 block mb-1">Feed After (mm)</label>
                                                                <NumericInput
                                                                    min={0}
                                                                    max={50}
                                                                    value={editingProfile.citizenFeedAfter || 0}
                                                                    onChange={v => setEditingProfile({...editingProfile, citizenFeedAfter: v})}
                                                                    className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                                />
                                                            </div>
                                                        </div>
                                                        <p className="text-[8px] text-yellow-600">Feed before adds blank space before artwork. Feed after adds space after (useful for cutter clearance).</p>
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={editingProfile.citizenAutoCut ?? true}
                                                        onChange={e => setEditingProfile({...editingProfile, citizenAutoCut: e.target.checked})}
                                                        className="w-4 h-4 accent-green-600"
                                                    />
                                                    <label className="text-[10px] font-bold text-slate-500">
                                                        Auto-Cut after print (requires cutter)
                                                    </label>
                                                </div>

                                                <div>
                                                    <label className="text-[9px] font-bold text-slate-500 block mb-1">Print Method</label>
                                                    <select
                                                        value={editingProfile.citizenPrintMethod || 'direct'}
                                                        onChange={e => setEditingProfile({...editingProfile, citizenPrintMethod: e.target.value as 'direct' | 'transfer'})}
                                                        className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                                    >
                                                        <option value="direct">Direct Thermal (no ribbon)</option>
                                                        <option value="transfer">Thermal Transfer (uses ribbon)</option>
                                                    </select>
                                                    <p className="text-[8px] text-slate-400 mt-1">Direct Thermal: heat-sensitive paper. Thermal Transfer: uses ribbon for permanent prints.</p>
                                                </div>

                                                <p className="text-[8px] text-green-600">Settings are applied during printing. Debug files saved to Documents folder.</p>
                                            </div>
                                        )}

                                        {/* Page Size - Hidden in FLEXIBLE mode and for Citizen continuous media */}
                                        {editingProfile.mediaSize !== 'FLEXIBLE' && !(editingProfile.driverType === 'citizen' && editingProfile.citizenSensorType === 'continuous') && (
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="text-[9px] font-bold text-slate-500 block mb-1">Page Width (mm)</label>
                                            <NumericInput
                                              min={10}
                                              max={1000}
                                              value={editingProfile.pageWidth || 109}
                                              onChange={v => setEditingProfile({...editingProfile, pageWidth: v})}
                                              className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                            />
                                          </div>
                                          <div>
                                            <label className="text-[9px] font-bold text-slate-500 block mb-1">Page Height (mm)</label>
                                            <NumericInput
                                              min={10}
                                              max={1000}
                                              value={editingProfile.pageHeight || 520}
                                              onChange={v => setEditingProfile({...editingProfile, pageHeight: v})}
                                              className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                            />
                                          </div>
                                        </div>
                                        )}
                                        <p className="text-[8px] text-slate-400">Note: Printer uses 200 DPI (1mm = 8 dots). In Fixed mode, these are exact PDF dimensions. In Flexible mode, page size matches artwork.</p>

                                        {/* Paper Type - Hidden, always continuous */}
                                        {/* <div>
                                          <label className="text-[9px] font-bold text-slate-500 block mb-1">Paper Type</label>
                                          <select 
                                            value={editingProfile.paperType || 'continuous'} 
                                            onChange={e => setEditingProfile({...editingProfile, paperType: e.target.value as any})}
                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                          >
                                            <option value="continuous">Continuous Paper</option>
                                            <option value="label">Label (Gap Detection)</option>
                                            <option value="black_label">Black Label</option>
                                          </select>
                                        </div> */}
                                        
                                        {/* TSPL Specific: Gap Detection - only for generic TSPL thermal printers */}
                                        {editingProfile.driverType !== 'oki_usb' && editingProfile.driverType !== 'citizen' && editingProfile.driverType !== 'meena_lite' && editingProfile.printerLanguage === 'TSPL' && (
                                          <div>
                                            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={editingProfile.gapEnabled || false}
                                                onChange={e => setEditingProfile({...editingProfile, gapEnabled: e.target.checked})}
                                                className="w-4 h-4 accent-blue-600"
                                              />
                                              <span>Enable Gap Detection (TSPL)</span>
                                            </label>
                                            <p className="text-[8px] text-slate-400 mt-1">Enable gap sensor detection for label paper.</p>
                                          </div>
                                        )}

                                        {/* Compression - for all thermal (not OKI/Citizen) */}
                                        {editingProfile.driverType !== 'oki_usb' && editingProfile.driverType !== 'citizen' && (
                                          <div>
                                            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={editingProfile.compress || false}
                                                onChange={e => setEditingProfile({...editingProfile, compress: e.target.checked})}
                                                className="w-4 h-4 accent-blue-600"
                                              />
                                              <span>Compress Image Data</span>
                                            </label>
                                            <p className="text-[8px] text-slate-400 mt-1">Compress bitmap data before sending (faster transfer, recommended for Meena Lite).</p>
                                          </div>
                                        )}

                                        {/* Cut After Print - not for OKI, Citizen, or Meena Lite (no cutter) */}
                                        {editingProfile.driverType !== 'oki_usb' && editingProfile.driverType !== 'citizen' && editingProfile.driverType !== 'meena_lite' && (
                                          <div>
                                            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={editingProfile.cutAfterPrint || false}
                                                onChange={e => setEditingProfile({...editingProfile, cutAfterPrint: e.target.checked})}
                                                className="w-4 h-4 accent-blue-600"
                                              />
                                              <span>Auto-Cut After Print</span>
                                            </label>
                                            <p className="text-[8px] text-slate-400 mt-1">Automatically cut paper after printing (requires cutter).</p>
                                          </div>
                                        )}

                                        {/* Feed After Print - for all thermal (not OKI/Citizen) */}
                                        {editingProfile.driverType !== 'oki_usb' && editingProfile.driverType !== 'citizen' && (
                                          <div>
                                            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={editingProfile.feedEnabled || false}
                                                onChange={e => setEditingProfile({...editingProfile, feedEnabled: e.target.checked})}
                                                className="w-4 h-4 accent-blue-600"
                                              />
                                              <span>Feed After Print</span>
                                            </label>
                                            <p className="text-[8px] text-slate-400 mt-1">Advance paper after print to prevent edge getting stuck in roller.</p>
                                            {editingProfile.feedEnabled && (
                                              <div className="mt-2">
                                                <label className="text-[10px] text-slate-400 mb-1 block">
                                                  Feed Amount: {editingProfile.feedAmount || 5}mm
                                                </label>
                                                <input
                                                  type="range"
                                                  min="1"
                                                  max="20"
                                                  step="1"
                                                  value={editingProfile.feedAmount || 5}
                                                  onChange={e => setEditingProfile({...editingProfile, feedAmount: Number(e.target.value)})}
                                                  className="w-full"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Sheet Size Settings - Desktop Only (non-mobile, non-OKI system printers) */}
                                    {!PrinterBridge.isMobile && editingProfile.connectionType !== 'usb_oki' && (
                                      <div className={`p-3 rounded border space-y-3 ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="text-[10px] font-bold text-slate-500 block">Sheet Size Settings</label>

                                        {/* Media Size Mode */}
                                        <div>
                                          <label className="text-[9px] font-bold text-slate-500 block mb-1">Sheet Size Mode</label>
                                          <select
                                            value={editingProfile.mediaSize || 'FLEXIBLE'}
                                            onChange={e => setEditingProfile({...editingProfile, mediaSize: e.target.value as 'FLEXIBLE' | 'FIXED'})}
                                            className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                          >
                                            <option value="FLEXIBLE">Flexible (PDF matches artwork size)</option>
                                            <option value="FIXED">Fixed (force specific sheet size)</option>
                                          </select>
                                          <p className="text-[8px] text-slate-400 mt-1">
                                            {editingProfile.mediaSize === 'FIXED'
                                              ? 'PDF will be created at the sheet size below. Artwork will be centred on the page — larger artwork will be cropped, smaller artwork will have whitespace around it. This enables guidelines/cut lines.'
                                              : 'PDF will match the artwork size exactly. Guidelines will not appear as there is no surrounding whitespace.'}
                                          </p>
                                        </div>

                                        {/* Page Dimensions - only when FIXED */}
                                        {editingProfile.mediaSize === 'FIXED' && (
                                          <div className="grid grid-cols-2 gap-2">
                                            <div>
                                              <label className="text-[9px] font-bold text-slate-500 block mb-1">Sheet Width (mm)</label>
                                              <NumericInput
                                                min={10}
                                                max={1000}
                                                value={editingProfile.pageWidth || 109}
                                                onChange={v => setEditingProfile({...editingProfile, pageWidth: v})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                              />
                                            </div>
                                            <div>
                                              <label className="text-[9px] font-bold text-slate-500 block mb-1">Sheet Height (mm)</label>
                                              <NumericInput
                                                min={10}
                                                max={1000}
                                                value={editingProfile.pageHeight || 520}
                                                onChange={v => setEditingProfile({...editingProfile, pageHeight: v})}
                                                className={`w-full p-1.5 text-xs border rounded ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                                              />
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* PDF File Print Section - Mobile Only (TSP/TSPL printers, not OKI) */}
                                    {PrinterBridge.isMobile && editingProfile.connectionType !== 'usb_oki' && (
                                      <div className={`mt-3 p-3 rounded border ${darkMode ? 'bg-slate-900/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                        <label className="text-[10px] font-bold text-slate-500 mb-2 block">Test Print: Select PDF File</label>
                                        <p className="text-[9px] text-slate-400 mb-2">Choose a PDF file to send directly to the printer for testing.</p>
                                        <label className={`flex flex-col items-center justify-center w-full p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${darkMode ? 'border-slate-600 hover:border-slate-500 hover:bg-slate-800' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-100'}`}>
                                          <input 
                                            type="file" 
                                            accept="application/pdf"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) {
                                                handlePdfFilePrint(file, editingProfile.id);
                                              }
                                              // Reset input so same file can be selected again
                                              e.target.value = '';
                                            }}
                                            disabled={isPrinting}
                                            className="hidden"
                                          />
                                          {isPrinting ? (
                                            <div className="flex flex-col items-center gap-2">
                                              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                                              <span className="text-xs font-semibold">Printing PDF...</span>
                                            </div>
                                          ) : (
                                            <div className="flex flex-col items-center gap-2">
                                              <FileText className="w-5 h-5 text-slate-400" />
                                              <span className="text-xs font-semibold">Choose PDF File</span>
                                              <span className="text-[9px] text-slate-500">or tap to browse</span>
                                            </div>
                                          )}
                                        </label>
                                        <p className="text-[9px] text-slate-400 mt-2">Note: Uses default size (100mm x 150mm). Printer uses 200 DPI (1mm = 8 dots).</p>
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <label className={`flex items-center gap-1 text-xs font-bold cursor-pointer p-1 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}><input type="checkbox" checked={editingProfile.mirror} onChange={e => setEditingProfile({...editingProfile, mirror: e.target.checked})}/> Mirror</label>
                                        <label className={`flex items-center gap-1 text-xs font-bold cursor-pointer p-1 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}><input type="checkbox" checked={editingProfile.rotate} onChange={e => setEditingProfile({...editingProfile, rotate: e.target.checked})}/> Rotate 90°</label>
                                        </div>
                                    
                                    {/* Visual Position Offset Control */}
                                    <div className={`p-3 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="text-[10px] font-bold text-slate-500 mb-2 block">Print Position Adjustment</label>
                                        {(() => {
                                            // Convert internal X/Y to visual H/V based on rotation
                                            const isRotated = editingProfile.rotate;
                                            const visualH = isRotated ? (editingProfile.offsetY || 0) : (editingProfile.offsetX || 0);
                                            const visualV = isRotated ? -(editingProfile.offsetX || 0) : (editingProfile.offsetY || 0);
                                            
                                            // Update function that converts visual to internal
                                            const updateOffset = (deltaH: number, deltaV: number) => {
                                                const newVisualH = visualH + deltaH;
                                                const newVisualV = visualV + deltaV;
                                                if (isRotated) {
                                                    setEditingProfile({
                                                        ...editingProfile,
                                                        offsetX: -newVisualV,
                                                        offsetY: newVisualH
                                                    });
                                                } else {
                                                    setEditingProfile({
                                                        ...editingProfile,
                                                        offsetX: newVisualH,
                                                        offsetY: newVisualV
                                                    });
                                                }
                                            };
                                            
                                            const setVisualOffset = (h: number, v: number) => {
                                                if (isRotated) {
                                                    setEditingProfile({
                                                        ...editingProfile,
                                                        offsetX: -v,
                                                        offsetY: h
                                                    });
                                                } else {
                                                    setEditingProfile({
                                                        ...editingProfile,
                                                        offsetX: h,
                                                        offsetY: v
                                                    });
                                                }
                                            };
                                            
                                            // Scale factor for visual movement (same for both axes)
                                            const visualScale = 1.5; // % per mm
                                            
                                            return (
                                                <div className="flex flex-col gap-2">
                                                    {/* Visual preview - page with artwork inside */}
                                                    <div className="flex items-center gap-2">
                                                        <button 
                                                            onClick={() => updateOffset(-0.5, 0)}
                                                            className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`}
                                                            title="Shift Left 0.5mm"
                                                        >
                                                            <ChevronLeft className="w-5 h-5" />
                                                        </button>
                                                        
                                                        {/* Preview rectangle - landscape aspect ratio */}
                                                        <div 
                                                            className={`flex-1 h-12 rounded border-2 relative ${darkMode ? 'border-slate-500 bg-slate-800' : 'border-slate-400 bg-slate-100'}`}
                                                            style={{ minWidth: '160px', aspectRatio: '4/1' }}
                                                        >
                                                            {/* Inner artwork rectangle */}
                                                            <div 
                                                                className={`absolute rounded ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`}
                                                                style={{
                                                                    width: '70%',
                                                                    height: '60%',
                                                                    top: `calc(50% - 30% + ${Math.max(-25, Math.min(25, visualV * visualScale))}%)`,
                                                                    left: `calc(50% - 35% + ${Math.max(-20, Math.min(20, visualH * visualScale))}%)`,
                                                                    transition: 'all 0.15s ease-out'
                                                                }}
                                                            />
                                                            {/* Center crosshair */}
                                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                                                                <div className={`w-px h-full ${darkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                                        </div>
                                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                                                                <div className={`h-px w-full ${darkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                                                            </div>
                                                        </div>
                                                        
                                                        <button 
                                                            onClick={() => updateOffset(0.5, 0)}
                                                            className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`}
                                                            title="Shift Right 0.5mm"
                                                        >
                                                            <ChevronRight className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Up/Down buttons and editable value display */}
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button 
                                                            onClick={() => updateOffset(0, -0.5)}
                                                            className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`}
                                                            title="Shift Up 0.5mm"
                                                        >
                                                            <ChevronUp className="w-5 h-5" />
                                                        </button>
                                                        
                                                        {/* Editable offset inputs */}
                                                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                                            <span className="text-slate-500">H:</span>
                                                            <div className="relative">
                                                                <NumericInput
                                                                    value={visualH}
                                                                    onChange={v => setVisualOffset(v, visualV)}
                                                                    className={`w-12 px-1 ${isMobile ? 'pr-4' : ''} py-0.5 text-xs font-mono rounded border text-center ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                                />
                                                                <div className="absolute right-0 top-0 bottom-0 flex flex-col">
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => setVisualOffset(visualH + 0.5, visualV)}
                                                                            className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                        >
                                                                            +
                                                                        </button>
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => setVisualOffset(visualH - 0.5, visualV)}
                                                                            className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                        >
                                                                            -
                                                                        </button>
                                                                    </div>
                                                            </div>
                                                            <span className="text-slate-500">V:</span>
                                                            <div className="relative">
                                                                <NumericInput
                                                                    value={visualV}
                                                                    onChange={v => setVisualOffset(visualH, v)}
                                                                    className={`w-12 px-1 ${isMobile ? 'pr-4' : ''} py-0.5 text-xs font-mono rounded border text-center ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                                />
                                                                <div className="absolute right-0 top-0 bottom-0 flex flex-col">
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => setVisualOffset(visualH, visualV + 0.5)}
                                                                            className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                        >
                                                                            +
                                                                        </button>
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => setVisualOffset(visualH, visualV - 0.5)}
                                                                            className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                        >
                                                                            -
                                                                        </button>
                                                                    </div>
                                                            </div>
                                                            <span className="text-slate-400 text-[10px]">mm</span>
                                                        </div>
                                                        
                                                        <button 
                                                            onClick={() => updateOffset(0, 0.5)}
                                                            className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`}
                                                            title="Shift Down 0.5mm"
                                                        >
                                                            <ChevronDown className="w-5 h-5" />
                                                        </button>
                                                        
                                                        <button 
                                                            onClick={() => setVisualOffset(0, 0)}
                                                            className={`px-2 py-1 rounded text-[10px] font-bold ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`}
                                                            title="Reset to center"
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    
                                    <div className={`p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="flex items-center gap-2 mb-1">
                                            <input
                                                type="checkbox"
                                                checked={editingProfile.lengthCalibrationEnabled || false}
                                                onChange={e => setEditingProfile({...editingProfile, lengthCalibrationEnabled: e.target.checked})}
                                            />
                                            <span className="text-xs font-bold">Length Calibration (Stretch)</span>
                                        </label>
                                        <p className="text-[9px] text-slate-400 mb-2">Enable when the printed length doesn't match the expected size. Stretches or shrinks the output.</p>
                                        {editingProfile.lengthCalibrationEnabled && (
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-500 flex justify-between">
                                                    <span>Stretch Amount</span>
                                                    <span>{editingProfile.lengthCalibration || 100}%</span>
                                                </label>
                                                <input type="range" min="90" max="110" step="0.5" value={editingProfile.lengthCalibration || 100} onChange={e => setEditingProfile({...editingProfile, lengthCalibration: Number(e.target.value)})} className="w-full" />
                                            </div>
                                        )}
                                    </div>

                                    {/* DPI - hidden for OKI (has own setting) and Meena Lite (preset to 200) */}
                                    {editingProfile.connectionType !== 'usb_oki' && editingProfile.driverType !== 'oki_usb' && editingProfile.driverType !== 'meena_lite' && (
                                        <div className={`p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                            <label className="text-[10px] font-bold text-slate-500 mb-1 block">DPI (Print Quality)</label>
                                            <select value={editingProfile.dpi || 300} onChange={e => setEditingProfile({...editingProfile, dpi: Number(e.target.value)})} className={`w-full p-1 text-sm border rounded ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                                                <option value={200}>200 DPI</option>
                                                <option value={300}>300 DPI</option>
                                                <option value={600}>600 DPI</option>
                                                <option value={1200}>1200 DPI</option>
                                            </select>
                                            <p className="text-[9px] text-slate-400 mt-1">Higher DPI = better quality but larger file size</p>
                                        </div>
                                    )}

                                    {/* Crop Marks Section */}
                                    <div className={`p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="flex items-center gap-2 mb-1">
                                            <input type="checkbox" checked={editingProfile.enableCropMarks} onChange={e => setEditingProfile({...editingProfile, enableCropMarks: e.target.checked})} />
                                            <span className="text-xs font-bold flex items-center gap-1"><Crop className="w-3 h-3"/> Enable Crop Marks</span>
                                        </label>
                                        <p className="text-[9px] text-slate-400 mb-2">Adds corner marks showing where to cut. Only draws when media is larger than artwork.</p>
                                        {editingProfile.enableCropMarks && (
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-500 flex justify-between">
                                                    <span>Bleed (mm)</span>
                                                    <span>{editingProfile.cropMarksOffset || 0}mm</span>
                                                </label>
                                                <input type="range" min="0" max="20" step="1" value={editingProfile.cropMarksOffset || 0} onChange={e => setEditingProfile({...editingProfile, cropMarksOffset: Number(e.target.value)})} className="w-full" />
                                                <p className="text-[8px] text-slate-400 mt-1">In Flexible mode, adds whitespace around artwork. In Fixed mode, controls where marks draw (if space allows).</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Alignment Guidelines Section (Short Plate Feature) */}
                                    <div className={`p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-600' : 'bg-white border-slate-300'}`}>
                                        <label className="flex items-center gap-2 mb-1">
                                            <input type="checkbox" checked={editingProfile.enableSideGuidelines || false} onChange={e => setEditingProfile({...editingProfile, enableSideGuidelines: e.target.checked})} />
                                            <span className="text-xs font-bold">Alignment Guidelines</span>
                                        </label>
                                        <p className="text-[9px] text-slate-400 mb-2">Draws dashed lines at artwork edges. Use for aligning acrylic blocks when printing small plates on large media.</p>
                                        {editingProfile.enableSideGuidelines && (
                                            <div className="space-y-2">
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-500 mb-1 block">Position</label>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => setEditingProfile({...editingProfile, sideGuidelinesPosition: 'side'})}
                                                            className={`flex-1 text-[10px] py-1 px-2 rounded ${editingProfile.sideGuidelinesPosition === 'side' || !editingProfile.sideGuidelinesPosition ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                                                        >
                                                            Side
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingProfile({...editingProfile, sideGuidelinesPosition: 'top'})}
                                                            className={`flex-1 text-[10px] py-1 px-2 rounded ${editingProfile.sideGuidelinesPosition === 'top' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                                                        >
                                                            Top
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingProfile({...editingProfile, sideGuidelinesPosition: 'both'})}
                                                            className={`flex-1 text-[10px] py-1 px-2 rounded ${editingProfile.sideGuidelinesPosition === 'both' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                                                        >
                                                            Both
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-500 flex justify-between">
                                                        <span>Guidelines Offset (Outwards)</span>
                                                        <span>{editingProfile.sideGuidelinesOffset || 0}mm</span>
                                                    </label>
                                                    <input type="range" min="0" max="5" step="0.5" value={editingProfile.sideGuidelinesOffset || 0} onChange={e => setEditingProfile({...editingProfile, sideGuidelinesOffset: Number(e.target.value)})} className="w-full" />
                                                    <p className="text-[8px] text-slate-400 mt-1">0mm = exact edge. Increase to move lines further into the whitespace.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 justify-end">
                                        <button onClick={() => { setEditingProfile(null); if(p.name === 'New Printer') deleteProfile(p.id); }} className="text-xs font-bold text-slate-500 px-2">Cancel</button>
                                        <button onClick={() => { saveProfile(editingProfile); }} className="text-xs font-bold bg-green-600 text-white px-3 py-1 rounded">Save</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-between items-center">
                                    <div><div className="font-bold text-sm">{p.name}</div><div className="text-xs text-slate-500">{p.systemPrinterName}</div></div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditingProfile(p)} className="p-1 hover:bg-white/10 rounded"><Settings className="w-4 h-4 text-slate-500"/></button>
                                        <button onClick={() => deleteProfile(p.id)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4 text-red-500"/></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                     {!editingProfile && (
                        <button
                            onClick={createNewProfile}
                            className={`w-full py-2 border-2 border-dashed rounded-lg font-bold text-sm transition-colors ${darkMode ? 'border-slate-700 text-slate-500 hover:border-blue-500 hover:text-blue-400' : 'border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-500'}`}
                        >
                            + Add Printer Profile
                        </button>
                    )}
                </div>
                <hr className={`my-4 ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}/>
                <h3 className="font-bold text-sm mb-3">Workflow Configuration</h3>
                <div className="space-y-4">
                    {!shouldHidePro && <div className={`p-3 border rounded-lg transition-colors ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-slate-50 border-slate-200 hover:bg-white'}`}>
                        <label className="flex items-center justify-between cursor-pointer mb-1">
                            <span className="font-bold text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500"/> Quick Print <ProBadge /></span>
                            <input type="checkbox" className="toggle-checkbox" checked={printSettings.quickPrintEnabled} onChange={e => updatePrintSettings({...printSettings, quickPrintEnabled: e.target.checked})} />
                        </label>
                        <p className="text-[10px] text-slate-500 leading-tight">If enabled, pressing <strong>ENTER</strong> in the registration box will immediately send the plate to the default printer.</p>
                    </div>}
                    {!shouldHidePro && <div className={`p-3 border rounded-lg transition-colors ${darkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-50'}`}>
                        <label className="flex items-center justify-between cursor-pointer">
                            <span className="font-bold text-sm">Dual Printer Mode <ProBadge /></span>
                            <input 
                                type="checkbox" 
                                className="toggle-checkbox" 
                                checked={printSettings.dualMode} 
                                onChange={e => {
                                    if (!isPro && e.target.checked) {
                                        setProModalErrors(['Dual Printer Mode']);
                                        setShowProModal(true);
                                        return;
                                    }
                                    updatePrintSettings({...printSettings, dualMode: e.target.checked});
                                }} 
                            />
                        </label>
                    </div>}
                    {printSettings.dualMode ? (
                        <div className={`grid grid-cols-2 gap-3 p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">White Plate Printer</label>
                                <select value={printSettings.dualWhiteProfileId} onChange={e => updatePrintSettings({...printSettings, dualWhiteProfileId: e.target.value})} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`}>
                                    <option value="">Select Profile...</option>
                                    {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 block mb-1">Yellow Plate Printer</label>
                                <select value={printSettings.dualYellowProfileId} onChange={e => updatePrintSettings({...printSettings, dualYellowProfileId: e.target.value})} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`}>
                                    <option value="">Select Profile...</option>
                                    {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Default Printer Profile</label>
                            <select value={printSettings.singleProfileId} onChange={e => updatePrintSettings({...printSettings, singleProfileId: e.target.value})} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300'}`}>
                                <option value="">None (show dialog each time)</option>
                                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <p className="text-[9px] text-slate-400 mt-1">Select a default to send jobs directly when clicking Print. Leave as 'None' to choose a profile each time.</p>
                        </div>
                    )}

                    {/* PDF EXPORT SETTINGS SECTION - Expandable */}
                    {!shouldHidePro && <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        <button 
                            onClick={() => setShowPdfSettingsExpanded(!showPdfSettingsExpanded)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700 hover:bg-slate-750' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                        >
                            <span className="font-bold flex items-center gap-2">
                                <Download className="w-5 h-5 text-green-500"/> PDF Export Settings
                            </span>
                            {showPdfSettingsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                        
                        {showPdfSettingsExpanded && (
                        <div className="space-y-4 mt-4">
                            {/* Show PDF Button Toggle */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <label className="flex items-center justify-between cursor-pointer">
                                    <span className="text-xs font-bold text-slate-500">Show PDF Export Button</span>
                                    <input type="checkbox" checked={pdfExportSettings.showButton} onChange={e => setPdfExportSettings({...pdfExportSettings, showButton: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                                </label>
                                <p className="text-[10px] text-slate-400 mt-1">Hide to make print buttons wider if you don't use PDF export</p>
                </div>

                            {/* Save Print PDFs Toggle - Mobile Only */}
                            {Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && (
                                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <span className="text-xs font-bold text-slate-500">Save Print PDFs</span>
                                        <input type="checkbox" checked={pdfExportSettings.savePrintPdfs} onChange={e => setPdfExportSettings({...pdfExportSettings, savePrintPdfs: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                                    </label>
                                    <p className="text-[10px] text-slate-400 mt-1">When enabled, saves a copy of each print to Documents/RegIQ folder</p>
                                </div>
                            )}

                            {/* DPI - Dropdown */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">DPI (Resolution)</label>
                                <select 
                                    value={pdfExportSettings.dpi} 
                                    onChange={e => setPdfExportSettings({...pdfExportSettings, dpi: Number(e.target.value)})} 
                                    className={`w-full p-2 text-sm border rounded ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                >
                                    <option value={200}>200 DPI</option>
                                    <option value={300}>300 DPI (Recommended)</option>
                                    <option value={600}>600 DPI</option>
                                    <option value={1200}>1200 DPI</option>
                                </select>
                            </div>

                            {/* Transformations */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={pdfExportSettings.mirror} onChange={e => setPdfExportSettings({...pdfExportSettings, mirror: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                                        Mirror
                                    </label>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={pdfExportSettings.rotate} onChange={e => setPdfExportSettings({...pdfExportSettings, rotate: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                                        Rotate 90°
                                    </label>
                                </div>
                            </div>

                            {/* Visual Position Adjustment for PDF */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <label className="text-xs font-bold text-slate-500 mb-2 block">Print Position Adjustment</label>
                                {(() => {
                                    const isRotated = pdfExportSettings.rotate;
                                    const visualH = isRotated ? pdfExportSettings.offsetY : pdfExportSettings.offsetX;
                                    const visualV = isRotated ? -pdfExportSettings.offsetX : pdfExportSettings.offsetY;
                                    
                                    const updatePdfOffset = (deltaH: number, deltaV: number) => {
                                        const newVisualH = visualH + deltaH;
                                        const newVisualV = visualV + deltaV;
                                        if (isRotated) {
                                            setPdfExportSettings({...pdfExportSettings, offsetX: -newVisualV, offsetY: newVisualH});
                                        } else {
                                            setPdfExportSettings({...pdfExportSettings, offsetX: newVisualH, offsetY: newVisualV});
                                        }
                                    };
                                    
                                    const setPdfVisualOffset = (h: number, v: number) => {
                                        if (isRotated) {
                                            setPdfExportSettings({...pdfExportSettings, offsetX: -v, offsetY: h});
                                        } else {
                                            setPdfExportSettings({...pdfExportSettings, offsetX: h, offsetY: v});
                                        }
                                    };
                                    
                                    const visualScale = 1.5;
                                    
                                    return (
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => updatePdfOffset(-0.5, 0)} className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`} title="Shift Left 0.5mm">
                                                    <ChevronLeft className="w-5 h-5" />
                                                </button>
                                                <div className={`flex-1 h-12 rounded border-2 relative ${darkMode ? 'border-slate-500 bg-slate-900' : 'border-slate-400 bg-slate-100'}`} style={{ minWidth: '160px', aspectRatio: '4/1' }}>
                                                    <div className={`absolute rounded ${darkMode ? 'bg-green-600' : 'bg-green-500'}`} style={{
                                                        width: '70%', height: '60%',
                                                        top: `calc(50% - 30% + ${Math.max(-25, Math.min(25, visualV * visualScale))}%)`,
                                                        left: `calc(50% - 35% + ${Math.max(-20, Math.min(20, visualH * visualScale))}%)`,
                                                        transition: 'all 0.15s ease-out'
                                                    }}/>
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                                                        <div className={`w-px h-full ${darkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                                                    </div>
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                                                        <div className={`h-px w-full ${darkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                                                    </div>
                                                </div>
                                                <button onClick={() => updatePdfOffset(0.5, 0)} className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`} title="Shift Right 0.5mm">
                                                    <ChevronRight className="w-5 h-5" />
                                                </button>
                                            </div>
                                            <div className="flex items-center justify-center gap-2">
                                                <button onClick={() => updatePdfOffset(0, -0.5)} className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`} title="Shift Up 0.5mm">
                                                    <ChevronUp className="w-5 h-5" />
                                                </button>
                                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${darkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
                                                    <span className="text-slate-500">H:</span>
                                                    <div className="relative">
                                                        <NumericInput
                                                            value={visualH}
                                                            onChange={v => setPdfVisualOffset(v, visualV)}
                                                            className={`w-12 px-1 ${isMobile ? 'pr-4' : ''} py-0.5 text-xs font-mono rounded border text-center ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                        />
                                                        <div className="absolute right-0 top-0 bottom-0 flex flex-col">
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setPdfVisualOffset(visualH + 0.5, visualV)}
                                                                    className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                >
                                                                    +
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setPdfVisualOffset(visualH - 0.5, visualV)}
                                                                    className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                >
                                                                    -
                                                                </button>
                                                            </div>
                                                    </div>
                                                    <span className="text-slate-500">V:</span>
                                                    <div className="relative">
                                                        <NumericInput
                                                            value={visualV}
                                                            onChange={v => setPdfVisualOffset(visualH, v)}
                                                            className={`w-12 px-1 ${isMobile ? 'pr-4' : ''} py-0.5 text-xs font-mono rounded border text-center ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                        />
                                                        <div className="absolute right-0 top-0 bottom-0 flex flex-col">
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setPdfVisualOffset(visualH, visualV + 0.5)}
                                                                    className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                >
                                                                    +
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => setPdfVisualOffset(visualH, visualV - 0.5)}
                                                                    className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                                >
                                                                    -
                                                                </button>
                                                            </div>
                                                    </div>
                                                    <span className="text-slate-400 text-[10px]">mm</span>
                                                </div>
                                                <button onClick={() => updatePdfOffset(0, 0.5)} className={`w-8 h-8 flex items-center justify-center rounded ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-200 hover:bg-slate-300'}`} title="Shift Down 0.5mm">
                                                    <ChevronDown className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => setPdfVisualOffset(0, 0)} className={`px-2 py-1 rounded text-[10px] font-bold ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`} title="Reset to center">
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Length Calibration */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                                    <span>Length Calibration (Stretch)</span>
                                    <span>{pdfExportSettings.lengthCalibration}%</span>
                                </label>
                                <input type="range" min="90" max="110" step="0.5" value={pdfExportSettings.lengthCalibration} onChange={e => setPdfExportSettings({...pdfExportSettings, lengthCalibration: Number(e.target.value)})} className="w-full"/>
                            </div>

                            {/* Crop Marks */}
                            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-xs font-bold text-slate-500">Crop Marks</label>
                                    <input type="checkbox" checked={pdfExportSettings.enableCropMarks} onChange={e => setPdfExportSettings({...pdfExportSettings, enableCropMarks: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                                </div>
                                {pdfExportSettings.enableCropMarks && (
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-[10px] text-slate-400 flex justify-between">
                                                <span>Offset (mm)</span><span>{pdfExportSettings.cropMarksOffset}mm</span>
                                            </label>
                                            <input type="range" min="2" max="20" step="1" value={pdfExportSettings.cropMarksOffset} onChange={e => setPdfExportSettings({...pdfExportSettings, cropMarksOffset: Number(e.target.value)})} className="w-full"/>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] text-slate-400">Show in Preview</label>
                                            <input type="checkbox" checked={pdfExportSettings.showCropLinesPreview} onChange={e => setPdfExportSettings({...pdfExportSettings, showCropLinesPreview: e.target.checked})} className="w-3 h-3 accent-blue-600"/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>}
                </div>

                {/* Hide Pro Features Toggle */}
                <hr className={`my-4 ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}/>
                <div className={`p-3 border rounded-lg transition-colors ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                    <label className="flex items-center justify-between cursor-pointer">
                        <span className="font-bold text-sm">Hide Premium Features</span>
                        <input type="checkbox" className="toggle-checkbox" checked={hideProFeatures} onChange={e => {
                            setHideProFeatures(e.target.checked);
                            localStorage.setItem('ps_hide_pro_features', JSON.stringify(e.target.checked));
                        }} />
                    </label>
                    <p className="text-[10px] text-slate-500 leading-tight mt-1">Hide professional-only features from the interface. Simplifies the UI for basic usage.</p>
                </div>

                {/* App Version at the very bottom */}
                <div className="mt-8 text-center text-xs text-slate-400 select-none">
                    App Version: {APP_VERSION}
                </div>
            </div>
        </div>
      )}

      {!shouldHidePro && showBulkModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] print-hidden p-4">
            <div className={`p-4 md:p-6 rounded-xl w-full max-w-[900px] h-[90vh] max-h-[90vh] flex flex-col ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-white'}`}>
              <h3 className="text-lg md:text-xl font-bold mb-4 flex items-center gap-2"><List className="w-4 h-4 md:w-5 md:h-5"/> Bulk Manager <ProBadge /></h3>
              
              {/* Collapsible CSV Input */}
              {showBulkCsvInput && (
                <div className="space-y-3 mb-4">
                  <p className="text-[10px] md:text-xs text-slate-500">CSV Format: <strong>REG, TYPE (F/R/P), BORDER (Y/N), FLAG (Y/N)</strong>.</p>
                  <textarea className={`w-full p-2 md:p-3 border rounded font-mono text-[10px] md:text-xs h-20 md:h-24 ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50'}`} placeholder="Example:&#10;GB55REG, P, Y, N&#10;MY P14TE, F, Y, Y" value={bulkCsvInput} onChange={e => setBulkCsvInput(e.target.value)}/>
                  <button onClick={parseBulkCSV} className={`w-full py-2 rounded text-xs md:text-sm font-bold ${darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 hover:bg-slate-300'}`}>Parse CSV Data</button>
              </div>
              )}
              
              {!showBulkCsvInput && bulkItems.length > 0 && (
                <button onClick={() => setShowBulkCsvInput(true)} className={`mb-3 text-xs flex items-center gap-1 ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                  <ChevronDown className="w-3 h-3"/> Add More Items
                </button>
              )}

              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={toggleAllBulk} className={`text-[10px] md:text-xs font-bold flex items-center gap-1 ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    <CheckSquare className="w-3 h-3 md:w-4 md:h-4"/> Select All
                  </button>
                  {selectedBulkCount > 1 && (
                    <button onClick={deleteSelectedBulk} className="text-[10px] md:text-xs font-bold flex items-center gap-1 text-red-500 hover:text-red-600">
                      <Trash2 className="w-3 h-3 md:w-4 md:h-4"/> Delete ({selectedBulkCount})
                    </button>
                  )}
          </div>
                <div className="flex gap-1 md:gap-2 flex-wrap">
                  <button onClick={() => printSelectedBulk('WHITE')} className={`px-2 md:px-3 py-1 border rounded text-[10px] md:text-xs font-bold ${darkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white border-slate-300 hover:bg-slate-50'}`}>Print White</button>
                  <button onClick={() => printSelectedBulk('YELLOW')} className="px-2 md:px-3 py-1 bg-yellow-100 border border-yellow-400 text-yellow-900 rounded text-[10px] md:text-xs font-bold hover:bg-yellow-200">Print Yellow</button>
                  <button onClick={() => printSelectedBulk('PAIR')} className="px-2 md:px-3 py-1 bg-blue-600 text-white rounded text-[10px] md:text-xs font-bold hover:bg-blue-700">Print Pair</button>
                </div>
              </div>

              <div className={`space-y-2 flex-1 overflow-y-auto border p-2 rounded ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                {bulkItems.map((item, idx) => {
                  const isPrinted = (item.status?.printedWhite && (item.type === 'F' || item.type === 'P')) || 
                                   (item.status?.printedYellow && (item.type === 'R' || item.type === 'P')) ||
                                   (item.status?.printedWhite && item.status?.printedYellow && item.type === 'P');
                  return (
                    <div 
                      key={item.id} 
                      className={`flex items-center gap-2 text-sm border-b pb-2 p-2 rounded transition-colors ${
                        isPrinted 
                          ? (darkMode ? 'bg-green-900/30 border-green-700/50' : 'bg-green-50 border-green-200') 
                          : (darkMode ? 'bg-slate-900 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-100 hover:bg-slate-50')
                      }`}
                    >
                      <button onClick={() => toggleBulkSelection(idx)} className="text-slate-400 hover:text-blue-600">{item.selected ? <CheckSquare className="w-5 h-5 text-blue-600"/> : <Square className="w-5 h-5"/>}</button>
                      
                      <button onClick={() => openRNPSModal(item.registration)} title="Add RNPS Records">
                          <ShieldCheck className="w-4 h-4 text-green-500 hover:text-green-600" />
                      </button>
                      
                      <span className="text-[10px] md:text-xs font-mono text-slate-400 w-3 md:w-4">{idx+1}.</span>
                      <input value={item.registration} readOnly className={`w-20 md:w-28 p-1 border rounded font-mono font-bold uppercase text-[10px] md:text-xs max-w-[80px] ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-50'}`} />
                      <button onClick={() => cycleBulkType(idx)} className={`px-1 md:px-2 py-1 text-[9px] md:text-[10px] font-bold rounded w-12 md:w-14 text-center select-none ${item.type === 'P' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : item.type === 'F' ? (darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-800 hover:bg-slate-200') : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}>{item.type === 'P' ? 'PAIR' : item.type === 'F' ? 'FRONT' : 'REAR'}</button>
                      <div className="flex gap-1">
                        <button onClick={() => toggleBulkBorder(idx)} title="Border" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded border transition-colors ${item.border ? 'bg-green-600 border-green-600 text-white' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'bg-white text-slate-300 hover:border-slate-400')}`}><Maximize className="w-3 h-3 md:w-4 md:h-4"/></button>
                        <button onClick={() => toggleBulkFlag(idx)} title="Flag" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded border transition-colors ${item.flagLayout ? 'bg-green-600 border-green-600 text-white' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'bg-white text-slate-300 hover:border-slate-400')}`}><Flag className="w-3 h-3 md:w-4 md:h-4"/></button>
                      </div>
                      
                      {/* Per-item print buttons */}
                      <div className="flex gap-1 ml-auto">
                        {printSettings.dualMode ? (
                          <>
                            <button onClick={() => printSingleBulkItem(idx, item, 'WHITE')} title="Print White" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded border text-xs ${item.status?.printedWhite ? 'bg-green-600 border-green-600 text-white' : (darkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600' : 'bg-white border-slate-300 hover:bg-slate-50')}`}>
                              <Printer className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                            <button onClick={() => printSingleBulkItem(idx, item, 'YELLOW')} title="Print Yellow" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded border ${item.status?.printedYellow ? 'bg-green-600 border-green-600 text-white' : 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200'}`}>
                              <Printer className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          </>
                        ) : (
                          <button 
                            onClick={() => printSingleBulkItem(idx, item, item.type === 'P' ? 'DOUBLE' : 'SINGLE')} 
                            title={item.type === 'P' ? 'Print Pair (x2)' : 'Print (x1)'}
                            className={`px-1 md:px-2 h-7 md:h-8 flex items-center justify-center gap-1 rounded border text-[10px] md:text-xs font-bold ${isPrinted ? 'bg-green-600 border-green-600 text-white' : (darkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600' : 'bg-white border-slate-300 hover:bg-slate-50')}`}
                          >
                            {item.type === 'P' ? <><Layers className="w-3 h-3 md:w-4 md:h-4"/>x2</> : <><Printer className="w-3 h-3 md:w-4 md:h-4"/>x1</>}
                          </button>
                        )}
                  </div>
                      
                      <button onClick={() => setBulkItems(bulkItems.filter((_, i) => i !== idx))} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 className="w-3 h-3 md:w-4 md:h-4"/></button>
          </div>
                  );
                })}
              </div>

              {/* Progress Status - PDF Export */}
              {bulkExportProgress && (
                <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold">Generating PDF...</span>
                    <span className="text-sm font-mono">{bulkExportProgress.current} / {bulkExportProgress.total}</span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div 
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${(bulkExportProgress.current / bulkExportProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">{bulkExportProgress.total - bulkExportProgress.current} pages remaining</span>
                    <button 
                      onClick={cancelBulkExport}
                      className="px-3 py-1 text-xs font-bold text-red-500 hover:bg-red-500/10 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Progress Status - Print Jobs */}
              {bulkPrintProgress && (
                <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold">Printing...</span>
                    <span className="text-sm font-mono">{bulkPrintProgress.current} / {bulkPrintProgress.total}</span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div 
                      className="h-full bg-green-600 transition-all duration-300"
                      style={{ width: `${(bulkPrintProgress.current / bulkPrintProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">{bulkPrintProgress.total - bulkPrintProgress.current} jobs remaining</span>
                    <button 
                      onClick={cancelBulkPrint}
                      className="px-3 py-1 text-xs font-bold text-red-500 hover:bg-red-500/10 rounded transition-colors"
                    >
                      Cancel (ESC)
                    </button>
                  </div>
                </div>
              )}

              {/* Bottom actions */}
              <div className={`flex gap-2 mt-4 pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                <button onClick={() => setShowBulkModal(false)} disabled={exporting || bulkPrintProgress !== null} className={`flex-1 py-2 md:py-3 border rounded text-xs md:text-sm font-semibold transition-colors ${darkMode ? 'border-slate-600 hover:bg-slate-800 text-slate-300' : 'text-slate-600 hover:bg-slate-50'} disabled:opacity-50`}>Close</button>
                <button onClick={() => setShowBulkSettings(!showBulkSettings)} disabled={exporting || bulkPrintProgress !== null} className={`px-3 md:px-4 py-2 md:py-3 border rounded text-xs md:text-sm font-semibold flex items-center gap-1 md:gap-2 transition-colors ${darkMode ? 'border-slate-600 hover:bg-slate-800 text-slate-300' : 'text-slate-600 hover:bg-slate-50'} disabled:opacity-50`}>
                  <Settings className="w-3 h-3 md:w-4 md:h-4"/> Settings
                </button>
                <div className="flex relative" ref={bulkExportDropdownRef}>
                  <button 
                    onClick={() => handleBulkExport('all')} 
                    disabled={exporting || bulkItems.length === 0 || bulkPrintProgress !== null || selectedBulkCount === 0} 
                    className="flex-1 py-2 md:py-3 bg-blue-600 text-white rounded-l font-bold flex justify-center gap-1 md:gap-2 hover:bg-blue-700 disabled:opacity-50 text-xs md:text-sm"
                  >
                    {exporting ? <><Loader2 className="animate-spin w-4 h-4 md:w-5 md:h-5"/> Exporting...</> : <><Download className="w-4 h-4 md:w-5 md:h-5"/> Export PDF</>}
                  </button>
                  <button 
                    onClick={() => setBulkExportDropdownOpen(!bulkExportDropdownOpen)}
                    disabled={exporting || bulkItems.length === 0 || bulkPrintProgress !== null || selectedBulkCount === 0}
                    className={`px-2 md:px-3 py-2 md:py-3 bg-blue-700 text-white rounded-r border-l border-blue-500 hover:bg-blue-800 disabled:opacity-50 ${darkMode ? 'border-blue-600' : ''}`}
                  >
                    <ChevronDown className={`w-3 h-3 md:w-4 md:h-4 transition-transform ${bulkExportDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {bulkExportDropdownOpen && (
                    <div className={`absolute bottom-full right-0 mb-2 w-48 rounded-lg shadow-xl overflow-hidden border z-50 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                      <button 
                        onClick={() => handleBulkExport('all')}
                        className={`w-full text-left p-3 text-sm hover:bg-opacity-50 transition-colors ${darkMode ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-800 hover:bg-slate-50'}`}
                      >
                        Export All Selected
                      </button>
                      <button 
                        onClick={() => handleBulkExport('whites')}
                        className={`w-full text-left p-3 text-sm hover:bg-opacity-50 transition-colors border-t ${darkMode ? 'text-slate-200 hover:bg-slate-800 border-slate-800' : 'text-slate-800 hover:bg-slate-50 border-slate-100'}`}
                      >
                        Export Whites Only
                      </button>
                      <button 
                        onClick={() => handleBulkExport('yellows')}
                        className={`w-full text-left p-3 text-sm hover:bg-opacity-50 transition-colors border-t ${darkMode ? 'text-slate-200 hover:bg-slate-800 border-slate-800' : 'text-slate-800 hover:bg-slate-50 border-slate-100'}`}
                      >
                        Export Yellows Only
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-[9px] md:text-[10px] text-slate-400 text-center mt-2">Uses PDF settings from Printer Settings modal</p>

              {/* Settings Panel */}
              {showBulkSettings && !exporting && bulkPrintProgress === null && (
                <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <h4 className="text-sm font-bold mb-3">Bulk Print Settings</h4>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block">Delay Between Print Jobs</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="range" 
                        min="0" max="30" step="1" 
                        value={bulkPrintDelay} 
                        onChange={e => setBulkPrintDelay(Number(e.target.value))} 
                        className="flex-1"
                      />
                      <span className="text-sm font-mono w-12">{bulkPrintDelay}s</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Delay before processing the next print job. Only applies to print operations, not PDF export.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
      )}

      {showSizeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] backdrop-blur-sm print-hidden">
              <div className={`p-6 rounded-xl w-[500px] shadow-2xl overflow-y-auto max-h-[90vh] ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-white'}`}>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">Manage Sizes</h3>
                  
                  {existingCustomSizes.length > 0 && (
                      <div className="mb-6">
                          <h4 className="text-xs font-bold text-slate-500 mb-2 uppercase">Your Custom Sizes</h4>
                          <div className={`border rounded-lg divide-y ${darkMode ? 'border-slate-700 divide-slate-700' : 'border-slate-200 divide-slate-100'}`}>
                              {existingCustomSizes.map(([key, dim]) => (
                                  <div key={key} className={`flex justify-between items-center p-2 text-sm hover:bg-white/5 cursor-pointer`} onClick={() => editCustomSize(key, dim)}>
                                      <span className="flex items-center gap-2"><Edit2 className="w-3 h-3 text-slate-500"/> {dim.label} <span className="text-xs text-slate-500 ml-1">({dim.width}x{dim.height})</span></span>
                                      <button onClick={(e) => { e.stopPropagation(); deleteCustomSize(key); }} className="text-red-500 hover:bg-red-500/10 p-1 rounded transition-colors"><Trash2 className="w-4 h-4"/></button>
                                  </div>
                              ))}
                          </div>
                      </div>
                  )}

                  <hr className={`my-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} />

                  <h4 className="text-sm font-bold mb-3">Add / Edit Size</h4>
                  <div className="space-y-3">
                      <input value={newSizeName} onChange={e => setNewSizeName(e.target.value)} placeholder="Size Name (e.g. My Hex)" className={`w-full border p-2 rounded text-sm ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}/>
                      <div className="flex gap-2">
                          <div className="flex-1">
                              <label className="text-[10px] text-slate-500 font-bold">Width (mm)</label>
                              <div className="relative">
                                  <NumericInput
                                      value={newSizeW}
                                      onChange={v => setNewSizeW(v)}
                                      className={`w-full border p-2 pr-8 rounded text-sm ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}
                                  />
                                  <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                          <button 
                                              type="button"
                                              onClick={() => {
                                                const newVal = Math.min(1000, newSizeW + 1);
                                                setNewSizeW(newVal);
                                              }}
                                              className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                          >
                                              +
                                          </button>
                                          <button 
                                              type="button"
                                              onClick={() => {
                                                const newVal = Math.max(10, newSizeW - 1);
                                                setNewSizeW(newVal);
                                              }}
                                              className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                          >
                                              -
                                          </button>
                                      </div>
                              </div>
                          </div>
                          <div className="flex-1">
                              <label className="text-[10px] text-slate-500 font-bold">Height (mm)</label>
                              <div className="relative">
                                  <NumericInput
                                      value={newSizeH}
                                      onChange={v => setNewSizeH(v)}
                                      className={`w-full border p-2 pr-8 rounded text-sm ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}
                                  />
                                  <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                          <button 
                                              type="button"
                                              onClick={() => {
                                                const newVal = Math.min(1000, newSizeH + 1);
                                                setNewSizeH(newVal);
                                              }}
                                              className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                          >
                                              +
                                          </button>
                                          <button 
                                              type="button"
                                              onClick={() => {
                                                const newVal = Math.max(10, newSizeH - 1);
                                                setNewSizeH(newVal);
                                              }}
                                              className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                          >
                                              -
                                          </button>
                                      </div>
                              </div>
                          </div>
                      </div>
                      
                      {!shouldHidePro && <button
                        onClick={() => {
                            if (isPro) {
                                setShowShapeInputs(!showShapeInputs);
                            }
                        }}
                        disabled={!isPro}
                        className={`w-full py-2 flex items-center justify-center gap-2 text-sm font-bold rounded border transition-colors ${
                            isPro
                                ? (darkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-100')
                                : 'opacity-50 cursor-not-allowed bg-slate-100/5'
                        }`}
                      >
                        {isPro ? (
                            <><Code className="w-4 h-4"/> {showShapeInputs ? 'Hide' : 'Custom Shape SVG'} <ProBadge/></>
                        ) : (
                            <><Lock className="w-4 h-4"/> Custom Shape SVG (Professional Only) <ProBadge/></>
                        )}
                      </button>}

                      {!shouldHidePro && showShapeInputs && isPro && (
                        <div className={`p-3 rounded border space-y-3 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            <div>
                                <label className="text-[10px] text-blue-400 font-bold mb-1 block">Paste {'<svg>...</svg>'} Code (Auto-Fill)</label>
                                <textarea 
                                    value={newSizeRawSVG}
                                    onChange={e => { setNewSizeRawSVG(e.target.value); parseSVGCode(e.target.value); }}
                                    placeholder="Paste full SVG code here..."
                                    className={`w-full border p-2 rounded text-xs font-mono h-16 ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}
                                />
                            </div>
                            
                            <div>
                                <label className="text-[10px] text-slate-500 font-bold mb-1 flex justify-between">Path Data (d)</label>
                                <input 
                                    value={newSizePath} 
                                    onChange={e => setNewSizePath(e.target.value)} 
                                    className={`w-full border p-2 rounded text-xs font-mono ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}
                                />
                            </div>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <label className="text-[10px] text-slate-500 font-bold mb-1">ViewBox</label>
                                    <input 
                                        value={newSizeViewBox} 
                                        onChange={e => setNewSizeViewBox(e.target.value)} 
                                        className={`w-full border p-2 rounded text-xs font-mono ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] text-slate-500 font-bold mb-1">Transform</label>
                                    <input 
                                        value={newSizeTransform} 
                                        onChange={e => setNewSizeTransform(e.target.value)} 
                                        className={`w-full border p-2 rounded text-xs font-mono ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white'}`}
                                    />
                                </div>
                            </div>
                        </div>
                      )}

                      <div className="pt-4 flex gap-2">
                          <button onClick={() => setShowSizeModal(false)} className={`flex-1 p-2 rounded text-sm font-medium ${darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}>Close</button>
                          <button onClick={addCustomSize} disabled={!newSizeName || !newSizeW || !newSizeH} className="flex-1 bg-green-600 text-white p-2 rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50">Save Size</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* RNPS MODAL */}
      {!shouldHidePro && showRNPSModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] print-hidden p-2">
              <div className={`p-4 rounded-xl w-full max-w-2xl ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-white'}`}> 
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold flex items-center gap-2"><Shield className="w-6 h-6"/> RNPS Record Keeping <ProBadge /></h3>
                      <button onClick={() => setShowRNPSModal(false)}><X className="w-5 h-5 text-slate-400"/></button>
                  </div>

                  {/* Desktop/Web: QR Code and Folder Selection - moved up */}
                  {!Capacitor.isNativePlatform() && (
                      <div className={`mb-4 p-3 rounded-lg border flex items-center gap-6 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex flex-col items-center">
                              <h4 className="text-base font-extrabold text-green-600 dark:text-green-400 mb-2 uppercase tracking-wide">Upload from Phone</h4>
                              {rnpsQrCode && (
                                  <div className="bg-white p-2 rounded-lg inline-block mb-2">
                                      <img src={rnpsQrCode} alt="Upload QR Code" className="w-28 h-28"/>
                                  </div>
                              )}
                              <p className="text-xs text-slate-700 dark:text-slate-200 text-center font-semibold max-w-[10rem]">Scan this QR code with your phone to upload images directly.</p>
                          </div>
                          {/* Folder selection only on Electron */}
                          {/Electron/.test(navigator.userAgent) && (window as any).require && (
                              <div className="flex-1 space-y-2">
                                  <div>
                                      <label className="text-xs block text-slate-500 font-bold mb-1">Save Location</label>
                                      <div className="flex gap-2">
                                          <input
                                              type="text"
                                              value={rnpsDefaultPath || 'Not set'}
                                              readOnly
                                              className={`flex-1 p-2 border rounded text-xs ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                          />
                                          <button
                                              onClick={selectRNPSFolder}
                                              className={`px-3 py-2 rounded text-xs font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
                                          >
                                              Browse
                                          </button>
                                      </div>
                                  </div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                          type="checkbox"
                                          checked={autoSaveRnps}
                                          onChange={e => toggleAutoSaveRnps(e.target.checked)}
                                          className="w-4 h-4"
                                      />
                                      <span className="text-xs text-slate-600 dark:text-slate-400">Auto-save uploaded images</span>
                                  </label>
                              </div>
                          )}
                      </div>
                  )}

                  {/* Forms - Customer Details - now half width */}
                  <div className="space-y-4 mb-4">
                      <div className="space-y-3">
                          <h4 className="text-sm font-bold text-slate-500 uppercase">Customer Details</h4>
                          <div className="grid grid-cols-2 gap-2">
                              <input placeholder="Customer Name" value={rnpsRecord.customerName} onChange={e => setRnpsRecord({...rnpsRecord, customerName: e.target.value})} className={`p-2 border rounded text-sm ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}/>
                              <textarea placeholder="Address" value={rnpsRecord.customerAddress} onChange={e => setRnpsRecord({...rnpsRecord, customerAddress: e.target.value})} className={`p-2 border rounded text-sm h-20 ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}/>
                          </div>

                      </div>
                  </div>

                  {/* Image Fields - Side by Side */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* ID Document Column */}
                      <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-500 block">ID Document</label>
                          <select value={rnpsRecord.idType} onChange={e => setRnpsRecord({...rnpsRecord, idType: e.target.value})} className={`w-full p-2 border rounded text-xs mb-2 ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}>
                              <option value="">Select...</option>
                              <option>Driving Licence</option>
                              <option>Passport</option>
                              <option>Utility Bill</option>
                              <option>Bank Statement</option>
                          </select>
                          <input placeholder="Reference" value={rnpsRecord.idReference} onChange={e => setRnpsRecord({...rnpsRecord, idReference: e.target.value})} className={`w-full p-2 border rounded text-xs mb-2 ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}/>
                          <div className={`border-2 border-dashed rounded-lg p-2 h-32 flex flex-col items-center justify-center relative mb-2 ${rnpsRecord.idImage ? 'border-green-500 bg-green-500/10' : (darkMode ? 'border-slate-700' : 'border-slate-200')}`}>
                              {rnpsRecord.idImage ? (
                                  <img src={rnpsRecord.idImage} className="h-full w-full object-contain" alt="Proof of ID"/>
                              ) : (
                                  <div className="text-center text-slate-400">
                                      <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50"/>
                                      <span className="text-[10px]">No image</span>
                                  </div>
                              )}
                          </div>
                          <div className="flex gap-2">
                              <button onClick={() => capturePhoto('id')} className={`flex-1 flex items-center justify-center py-2 px-2 rounded-lg ${darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`} title="Take Photo">
                                  <Camera className="w-5 h-5"/>
                              </button>
                              <button onClick={() => browseImage('id')} className={`flex-1 flex items-center justify-center py-2 px-2 rounded-lg ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`} title="Browse">
                                  <FolderOpen className="w-5 h-5"/>
                              </button>
                          </div>
                          <input type="file" id="id-upload" className="hidden" accept="image/*" onChange={e => handleFileSelect(e, 'id')}/>
                      </div>

                      {/* Entitlement Column */}
                      <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-500 block">Entitlement</label>
                          <select value={rnpsRecord.entitlementType} onChange={e => setRnpsRecord({...rnpsRecord, entitlementType: e.target.value})} className={`w-full p-2 border rounded text-xs mb-2 ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}>
                              <option value="">Select...</option>
                              <option>V5C (Logbook)</option>
                              <option>V750 (Certificate)</option>
                              <option>V778 (Retention)</option>
                              <option>V11 (Tax Reminder)</option>
                          </select>
                          <input placeholder="Reference" value={rnpsRecord.entitlementReference} onChange={e => setRnpsRecord({...rnpsRecord, entitlementReference: e.target.value})} className={`w-full p-2 border rounded text-xs mb-2 ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`}/>
                          <div className={`border-2 border-dashed rounded-lg p-2 h-32 flex flex-col items-center justify-center relative mb-2 ${rnpsRecord.entitlementImage ? 'border-green-500 bg-green-500/10' : (darkMode ? 'border-slate-700' : 'border-slate-200')}`}>
                              {rnpsRecord.entitlementImage ? (
                                  <img src={rnpsRecord.entitlementImage} className="h-full w-full object-contain" alt="Proof of Entitlement"/>
                              ) : (
                                  <div className="text-center text-slate-400">
                                      <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50"/>
                                      <span className="text-[10px]">No image</span>
                                  </div>
                              )}
                          </div>
                          <div className="flex gap-2">
                              <button onClick={() => capturePhoto('entitlement')} className={`flex-1 flex items-center justify-center py-2 px-2 rounded-lg ${darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`} title="Take Photo">
                                  <Camera className="w-5 h-5"/>
                              </button>
                              <button onClick={() => browseImage('entitlement')} className={`flex-1 flex items-center justify-center py-2 px-2 rounded-lg ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`} title="Browse">
                                  <FolderOpen className="w-5 h-5"/>
                              </button>
                          </div>
                          <input type="file" id="ent-upload" className="hidden" accept="image/*" onChange={e => handleFileSelect(e, 'entitlement')}/>
                      </div>
                  </div>

                  {/* Android: Save Location Info */}
                  {Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && (
                      <div className={`mb-4 p-3 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="text-xs text-slate-500 mb-2">Files are saved to: <span className="font-mono font-bold">Documents/RegIQ/</span></div>
                      </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <button 
                          onClick={saveCompliancePacket} 
                          className={`flex-1 ${!isPro ? 'bg-slate-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2`}
                      >
                          <Save className="w-5 h-5"/> Save ZIP
                      </button>
                      {Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && (
                          <button 
                              onClick={async () => {
                                  try {
                                      // Use Android Intent to open the folder
                                      const { Browser } = await import('@capacitor/core');
                                      // On Android, we can't directly open a folder, but we can use a file manager intent
                                      // For now, show a message with instructions
                                      showToast('Open Files app → Documents → RegIQ folder');
                                      
                                      // Try to open with file manager if available
                                      try {
                                          const { App } = await import('@capacitor/core');
                                          // This will open the file manager, user can navigate to Documents/RegIQ
                                          await App.openUrl({ url: 'content://com.android.externalstorage.documents/document/primary%3ADocuments%2FRegIQ' });
                                      } catch (intentError) {
                                          // Fallback: just show the toast
                                          console.log('Could not open file manager directly');
                                      }
                                  } catch (e: any) {
                                      showToast('Open Files app → Documents → RegIQ folder');
                                  }
                              }}
                              className={`px-4 py-3 rounded-lg font-medium flex items-center gap-2 ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
                          >
                              <Folder className="w-5 h-5"/> Open Folder
                          </button>
                      )}
                  </div>
              </div>
          </div>
      )}

      <div id="plate-export-standard" style={{ position: 'fixed', left: '-20000px', top: 0, zIndex: -1, width: `${currentDims.width}px`, height: `${currentDims.height + (config.lippedPlate ? config.lipHeight : 0)}px` }}>
          <PlateView config={config} dimensions={currentDims} forceTransparent={true} unit="px" />
          {/* Demo mode watermark - displays on export PDF */}
          {demoMode && (
              <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: `${Math.max(16, currentDims.width / 12)}px`,
                  fontWeight: 'bold',
                  color: 'rgba(200, 0, 0, 0.2)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  fontFamily: 'Arial, sans-serif',
                  zIndex: 1000,
                  mixBlendMode: 'multiply',
                  letterSpacing: '0.1em'
              }}>
                  DEMO - RegIQ
              </div>
          )}
      </div>

      <div id="plate-export-print" style={{ position: 'fixed', left: '-20000px', top: 0, zIndex: -1, width: `${currentDims.width}px`, height: `${currentDims.height + (config.lippedPlate ? config.lipHeight : 0)}px` }}>
          <PlateView config={{...config, type: PlateType.FRONT}} dimensions={currentDims} forceTransparent={true} unit="px" />
          {/* Demo mode watermark - displays on print preview */}
          {demoMode && (
              <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: `${Math.max(16, currentDims.width / 12)}px`,
                  fontWeight: 'bold',
                  color: 'rgba(200, 0, 0, 0.2)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  fontFamily: 'Arial, sans-serif',
                  zIndex: 1000,
                  mixBlendMode: 'multiply',
                  letterSpacing: '0.1em'
              }}>
                  DEMO - RegIQ
              </div>
          )}
      </div>

      <div id="print-output-container">
           {printJobConfig ? (() => {
              // Get offsets
              const offsetX = Number(printJobConfig.profile.offsetX) || 0;
              const offsetY = Number(printJobConfig.profile.offsetY) || 0;
              
              // Calculate page dimensions - MUST match executePrintJob exactly
              const lipHeight = config.lippedPlate ? config.lipHeight : 0;
              const calibrationScale = printJobConfig.profile.lengthCalibrationEnabled ? (printJobConfig.profile.lengthCalibration || 100) / 100 : 1;
              const cropOffset = printJobConfig.profile.enableCropMarks ? (printJobConfig.profile.cropMarksOffset || 0) : 0;
              const rawWidth = (printJobConfig.profile.rotate ? currentDims.height : currentDims.width) + (cropOffset * 2);
              const rawHeight = (printJobConfig.profile.rotate ? currentDims.width : currentDims.height) + lipHeight + (cropOffset * 2);
              const pageWidth = printJobConfig.profile.rotate ? rawWidth : rawWidth * calibrationScale;
              const pageHeight = printJobConfig.profile.rotate ? rawHeight * calibrationScale : rawHeight;

              return (
              <div 
                id="print-content-wrapper"
                style={{ 
                    position: 'relative', 
                    // Container dimensions now match EXACTLY what Electron sends to the printer
                    width: `${pageWidth}mm`, 
                    height: `${pageHeight}mm`,
                    overflow: 'visible',
                    clipPath: 'none',
                    clip: 'auto',
                    background: '#ffffff',
                    backgroundColor: '#ffffff'
                }}
              >
                 {/* CALIBRATION SCALE APPLIED HERE */}
                 {printJobConfig.profile.enableCropMarks && (
                     <svg className="absolute pointer-events-none" style={{
                         zIndex: 50,
                         // Crop marks fill the entire page
                         top: 0,
                         left: 0,
                     width: '100%',
                     height: '100%',
                         // Apply offsets via transform
                         transform: `translate(${offsetX}mm, ${offsetY}mm)`,
                         overflow: 'visible'
                     }}>
                             <line x1="0" y1="0" x2="10" y2="0" stroke="black" strokeWidth="0.5" />
                             <line x1="0" y1="0" x2="0" y2="10" stroke="black" strokeWidth="0.5" />
                             <line x1="100%" y1="0" x2="calc(100% - 10px)" y2="0" stroke="black" strokeWidth="0.5" />
                             <line x1="100%" y1="0" x2="100%" y2="10" stroke="black" strokeWidth="0.5" />
                             <line x1="0" y1="100%" x2="10" y2="100%" stroke="black" strokeWidth="0.5" />
                             <line x1="0" y1="100%" x2="0" y2="calc(100% - 10px)" stroke="black" strokeWidth="0.5" />
                             <line x1="100%" y1="100%" x2="calc(100% - 10px)" y2="100%" stroke="black" strokeWidth="0.5" />
                             <line x1="100%" y1="100%" x2="100%" y2="calc(100% - 10px)" stroke="black" strokeWidth="0.5" />
                         </svg>
                     )}
                     <div style={{ 
                         position: 'absolute',
                     // Position at dead center of fixed container
                         top: '50%',
                         left: '50%',
                         transformOrigin: 'center center',
                         width: `${currentDims.width}mm`,
                     height: `${currentDims.height + (config.lippedPlate && config.lipImage ? config.lipHeight : 0)}mm`,
                     // Center first, then apply offsets BEFORE rotation so X+ always moves right on screen
                     // This is what users expect for "Printer Offsets"
                     transform: (() => {
                         const center = 'translate(-50%, -50%)';
                         const translate = `translate(${offsetX}mm, ${offsetY}mm)`;
                         const rotation = printJobConfig.profile.rotate ? 'rotate(90deg)' : '';
                         const mirror = printJobConfig.profile.mirror ? 'scaleX(-1)' : '';
                         // Order: center, then translate (screen coordinates), then rotate, then mirror
                         return [center, translate, rotation, mirror].filter(Boolean).join(' ') || 'none';
                     })(),
                     overflow: 'visible',
                     // Ensure this container doesn't clip content
                     willChange: 'transform',
                     // Pure white background to match the page
                     background: '#ffffff'
                     }}>
                         <div style={{
                             width: '100%',
                             height: '100%',
                         transformOrigin: 'center center',
                         overflow: 'visible',
                         position: 'relative',
                         background: '#ffffff'
                     }}>
                         <div style={{
                             width: '100%',
                             height: '100%',
                             transform: `scaleX(${(printJobConfig.profile.lengthCalibration || 100) / 100})`,
                             transformOrigin: 'center center',
                             overflow: 'visible',
                             background: '#ffffff'
                         }}>
                             <PlateView config={{...config, type: printJobConfig.plateType}} dimensions={currentDims} unit="mm" forceTransparent={true} />
                         </div>
                     </div>
                 </div>
                 {demoMode && (
                     <div style={{
                         position: 'absolute',
                         top: '50%',
                         left: '50%',
                         transform: 'translate(-50%, -50%)',
                         fontSize: `${Math.max(6, currentDims.width / 14)}mm`,
                         fontWeight: 'bold',
                         color: 'rgba(200, 0, 0, 0.25)',
                         whiteSpace: 'nowrap',
                         pointerEvents: 'none',
                         fontFamily: 'Arial, sans-serif',
                         zIndex: 1000,
                         letterSpacing: '0.1em'
                     }}>
                         DEMO - RegIQ
                     </div>
                 )}
              </div>
              );
           })() : ( <div style={{ position: 'relative', width: `${currentDims.width}mm`, height: `${currentDims.height}mm`, background: '#ffffff' }}> <PlateView config={config} dimensions={currentDims} unit="mm" forceTransparent={true} /> {demoMode && (<div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: `${Math.max(6, currentDims.width / 14)}mm`, fontWeight: 'bold', color: 'rgba(200, 0, 0, 0.25)', whiteSpace: 'nowrap', pointerEvents: 'none', fontFamily: 'Arial, sans-serif', zIndex: 1000, letterSpacing: '0.1em' }}>DEMO - RegIQ</div>)} </div> )}
      </div>

      <aside className={`border-r flex flex-col ${isMobile ? 'flex-1 min-h-0 z-50' : 'h-full z-20 shadow-lg'} transition-colors duration-200 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', width: isMobile ? '100%' : '28rem', height: isMobile ? '50%' : '100%' }}>
        {isMobile && (
          <div className={`${mobileCompact ? 'p-1.5' : 'p-2.5'} md:p-4 border-b flex justify-between items-center sticky top-0 z-30 transition-all flex-shrink-0 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-100'}`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            <div className="flex items-center gap-2 md:gap-2 flex-1 min-w-0 overflow-visible">
                <div className="h-[60px] md:h-16 w-auto flex-shrink-0 overflow-visible" style={{ transform: 'scale(1.25)', transformOrigin: 'left center' }}>
                    <Logo darkMode={darkMode} className="h-full w-auto"/>
                </div>
                <div className="hidden sm:flex flex-col leading-tight flex-shrink-0">
                    {currentUser?.license && (() => {
                        const daysRemaining = Math.ceil((currentUser.license.expiryDate - Date.now()) / (1000 * 60 * 60 * 24));
                        const isExpiringSoon = daysRemaining <= 30;
                        return (
                            <>
                                <div className={`text-[9px] md:text-[10px] font-mono ${isExpiringSoon ? 'text-red-500 font-bold' : 'text-slate-500'}`}>
                                    {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                                </div>
                                <span className="text-[9px] md:text-[10px] text-slate-500 font-mono">
                                    {currentUser.license.tier}
                                </span>
                            </>
                        );
                    })()}
                    {!currentUser?.license && (
                        <span className="text-[9px] md:text-[10px] text-slate-500 font-mono">
                            UNREGISTERED
                        </span>
                    )}
                </div>
            </div>
            <div className="flex gap-1 items-center">
                {!shouldHidePro && <button onClick={() => setShowPrintHistory(true)} title="Print History" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><History className="w-5 h-5"/></button>}
                <button onClick={() => setDarkMode(!darkMode)} title="Toggle Theme" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-yellow-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}>{darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}</button>
                <button onClick={() => setShowPrinterSettings(true)} title="Printer Settings" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><Settings className="w-5 h-5"/></button>
                {!shouldHidePro && <button onClick={() => setShowBulkModal(true)} title="Bulk Import" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><List className="w-5 h-5"/></button>}
                <button onClick={() => setShowTemplateModal(true)} title="Templates" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-200'}`}><FolderOpen className="w-5 h-5"/></button>
                <button onClick={handleLogout} title="Deactivate License (Logout)" className={`p-2 rounded transition-colors ${darkMode ? 'text-slate-400 hover:text-red-500 hover:bg-slate-800' : 'text-slate-600 hover:text-red-500 hover:bg-slate-200'}`}><LogOut className="w-5 h-5"/></button>
            </div>
          </div>
        )}
        <div className={`px-3 md:px-4 ${mobileCompact ? 'pt-1 pb-1' : 'pt-2.5 pb-2.5'} md:pt-2 md:pb-2 flex gap-1.5 ${isMobile ? 'sticky top-[73px] md:top-[81px]' : ''} z-20 transition-all ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
            {['main', 'style', 'footer'].map(t => (<button key={t} onClick={() => setActiveTab(t as any)} className={`flex-1 ${mobileCompact ? 'py-1.5' : 'py-2.5'} md:py-1.5 text-[10px] md:text-xs font-bold rounded shadow-sm transition-all ${activeTab === t ? (darkMode ? 'bg-slate-700 text-blue-400 ring-1 ring-blue-900' : 'bg-white text-blue-600 ring-1 ring-blue-100') : (darkMode ? 'bg-slate-900 text-slate-500 hover:bg-slate-800' : 'bg-slate-200 text-slate-500 hover:bg-slate-300')}`}>{t.toUpperCase()}</button>))}
        </div>
          <div className={`${mobileCompact ? 'p-2 space-y-2' : 'p-4 space-y-4'} md:p-6 md:space-y-6 flex-1 overflow-y-auto min-h-0 transition-all ${darkMode ? 'bg-slate-900' : 'bg-white'}`} style={{ 
            WebkitOverflowScrolling: 'touch', 
            overscrollBehavior: 'contain', 
            overflowX: 'hidden', 
            paddingBottom: isMobile && hideOriginalPreview 
              ? `${Math.max(200, (currentDims.height * previewScale) + 60)}px` 
              : isMobile 
                ? '200px' 
                : '100px' 
          }}>
            {activeTab === 'main' && (
                <>
                    <section className={isMobile ? 'hidden' : ''}>
                        <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">
                            Registration
                        </label>
                        {/* Registration input with space reduction support */}
                        <div className="relative">
                            <textarea 
                                value={config.registration} 
                                onChange={e => setConfig({...config, registration: config.forceUppercase ? e.target.value.toUpperCase() : e.target.value})} 
                                onKeyDown={handleRegKeyDown} 
                                onClick={(e) => e.currentTarget.select()} 
                                className={`w-full ${isMobile ? 'p-2 text-2xl' : 'p-4 text-4xl'} font-mono uppercase border-2 rounded-xl text-center focus:border-blue-500 focus:ring-0 outline-none font-bold tracking-wider ${darkMode ? 'bg-slate-950 border-slate-700 text-slate-100' : 'bg-slate-50 border-slate-200 text-slate-800'} ${config.isShowPlate && config.showPlateOverrides.reduceSpace ? 'text-transparent' : ''}`} 
                                rows={1}
                            />
                            {/* Visual overlay showing reduced spaces when enabled */}
                            {config.isShowPlate && config.showPlateOverrides.reduceSpace && (
                                <div 
                                    className={`absolute inset-0 ${isMobile ? 'p-2 text-2xl' : 'p-4 text-4xl'} font-mono uppercase text-center font-bold tracking-wider pointer-events-none flex items-center justify-center whitespace-pre-wrap`}
                                    style={{
                                        color: darkMode ? '#e2e8f0' : '#1e293b',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {config.registration.split('').map((char, i) => {
                                        if (char === ' ' || char === '\u00A0') {
                                            return <span key={i} style={{ fontSize: '25%', display: 'inline-block' }}>{char}</span>;
                                        }
                                        return <span key={i}>{char === '\n' ? <br key={i}/> : char}</span>;
                                    })}
                                </div>
                            )}
                        </div>
                    </section>


                    {config.registration.includes('\n') && (<div className={`border p-3 rounded-lg mb-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}><label className="text-xs font-bold text-slate-500 mb-1 flex justify-between">Multi-line Spacing <span>{config.mainTextLineHeight}x</span></label><input type="range" min="0.5" max="2.0" step="0.05" value={config.mainTextLineHeight} onChange={e => setConfig({...config, mainTextLineHeight: parseFloat(e.target.value)})} className="w-full"/></div>)}

                    {/* ALIGNMENT CONTROLS FOR MULTILINE */}
                    {config.registration.includes('\n') && config.flagLayout && (
                        <div className={`border p-2 rounded-lg mb-2 flex gap-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                            <label className="text-xs font-bold text-slate-500 flex items-center mr-auto">Text Align</label>
                            <button onClick={() => setConfig({...config, mainTextAlignment: 'center'})} className={`p-1.5 rounded ${config.mainTextAlignment === 'center' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-white/10'}`}><AlignCenter className="w-4 h-4"/></button>
                            <button onClick={() => setConfig({...config, mainTextAlignment: 'left'})} className={`p-1.5 rounded ${config.mainTextAlignment === 'left' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-white/10'}`}><AlignLeft className="w-4 h-4"/></button>
                            <button onClick={() => setConfig({...config, mainTextAlignment: 'right'})} className={`p-1.5 rounded ${config.mainTextAlignment === 'right' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-white/10'}`}><AlignRight className="w-4 h-4"/></button>
                        </div>
                    )}

                    <div className="space-y-2">
                        <input value={config.slogan} onChange={e => setConfig({...config, slogan: e.target.value})} placeholder="Optional slogan text" className={`w-full p-2 md:p-2.5 border rounded text-sm text-center font-bold ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50'}`} />
                        {config.slogan.length > 0 && (<label className={`flex items-center gap-2 text-xs font-bold p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 text-slate-600'}`}><input type="checkbox" checked={config.supplierInCorner} onChange={e => setConfig({...config, supplierInCorner: e.target.checked})} className="rounded text-blue-600"/>Move legal text to corner</label>)}
                    </div>

                    <div className="hidden md:grid grid-cols-2 gap-4"><button onClick={() => setConfig({...config, type: PlateType.FRONT})} className={`p-3 rounded-lg border-2 font-bold text-sm transition-all ${config.type === PlateType.FRONT ? 'bg-blue-50/10 border-blue-500 text-blue-500' : (darkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-100 hover:border-slate-300')}`}>Front (White)</button><button onClick={() => setConfig({...config, type: PlateType.REAR})} className={`p-3 rounded-lg border-2 font-bold text-sm transition-all ${config.type === PlateType.REAR ? 'bg-yellow-500/10 border-yellow-500 text-yellow-500' : (darkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-100 hover:border-slate-300')}`}>Rear (Yellow)</button></div>
                    <div className={isMobile ? 'flex items-center gap-2' : ''}><label className={`text-xs font-bold text-slate-500 ${isMobile ? '' : 'mb-1 block'}`}>Size</label><div className="flex gap-2 flex-1"><select value={config.plateSize} onChange={e => setConfig({...config, plateSize: e.target.value})} className={`flex-1 p-2 md:p-2.5 border rounded-lg text-xs md:text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50'}`}>{Object.entries(dimensions).map(([key, val]) => <option key={key} value={key}>{val.label}</option>)}</select><button onClick={() => setShowSizeModal(true)} className={`p-2 md:p-2.5 border rounded-lg hover:bg-slate-100 text-slate-600 ${darkMode ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : ''}`} title="Add Custom Size"><Plus className="w-4 h-4 md:w-5 md:h-5"/></button></div></div>
                    
                    <div className={`grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-0`}>
                        <div className={`flex justify-between items-center p-2 md:p-3 border rounded-lg ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                            <label className={`text-xs md:text-sm font-bold flex items-center gap-2 ${darkMode ? 'text-slate-200' : ''}`}>
                                <Maximize className="w-3 h-3 md:w-4 md:h-4 text-slate-600"/> Border
                            </label>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={config.border} onChange={e => setConfig({...config, border: e.target.checked})} className="w-4 h-4 md:w-5 md:h-5 accent-blue-600"/>
                                <button type="button" onClick={() => setShowBorderSettingsModal(true)} className={`p-1 rounded ${darkMode ? 'text-slate-400 hover:bg-slate-900' : 'text-slate-600 hover:bg-slate-200'}`} title="Border Settings">
                                    <Settings className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className={`p-3 border rounded-lg space-y-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                            <div className="flex justify-between items-center">
                                <label className={`text-xs md:text-sm font-bold flex items-center gap-2 ${darkMode ? 'text-slate-200' : ''}`}>
                                    <Flag className="w-3 h-3 md:w-4 md:h-4 text-blue-600"/> Badge / Flag
                                </label>
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" checked={config.badge.enabled} onChange={e => handleBadgeToggle(e.target.checked)} className="w-4 h-4 md:w-5 md:h-5 accent-blue-600" />
                                    {!shouldHidePro && <button type="button" onClick={() => isMobile ? setShowBadgeSettingsModal(true) : setShowBadgeSettingsInline(v => !v)} className={`p-1 rounded ${darkMode ? 'text-slate-400 hover:bg-slate-900' : 'text-slate-600 hover:bg-slate-200'}`} title="Badge Settings">
                                        <Settings className="w-4 h-4" />
                                    </button>}
                                </div>
                            </div>

                            {config.badge.enabled && !isMobile && showBadgeSettingsInline && (
                                <div className="space-y-3 pt-2 border-t border-slate-200/10">
                                    {/* Show Graphic checkbox */}
                                    <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                                        <label className="text-xs font-bold text-slate-500">Show Graphic</label>
                                        <input type="checkbox" checked={config.showFlagGraphic} onChange={e => setConfig({...config, showFlagGraphic: e.target.checked})} className="toggle-checkbox" />
                                    </div>

                                    {/* Graphic settings - greyed unless Show Graphic ticked */}
                                    <div className={`p-3 rounded border space-y-3 transition-opacity ${config.showFlagGraphic ? '' : 'opacity-40 pointer-events-none'} ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                        {/* Flag icons - single row, no labels */}
                                        <div className="flex gap-1.5">
                                            {['UK', 'SCO', 'ENG', 'CYM', 'EU', 'CUSTOM'].map(ft => (
                                                <button
                                                    key={ft}
                                                    onClick={async () => {
                                                        if (ft === 'CUSTOM') {
                                                            setConfig({...config, badge: {...config.badge, type: ft as FlagType}});
                                                            return;
                                                        }
                                                        try {
                                                            const response = await fetch(`/flags/${ft}.svg`);
                                                            const blob = await response.blob();
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                const dataUrl = reader.result as string;
                                                                setConfig({...config, badge: {...config.badge, type: ft as FlagType, customSvgUrl: dataUrl}});
                                                            };
                                                            reader.readAsDataURL(blob);
                                                        } catch (error) {
                                                            setConfig({...config, badge: {...config.badge, type: ft as FlagType}});
                                                        }
                                                    }}
                                                    className={`flex-1 h-14 flex items-center justify-center rounded border transition-all ${config.badge.type === ft ? 'ring-2 ring-blue-500 border-blue-600' : (darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-300')}`}
                                                >
                                                    {ft === 'CUSTOM' ? (
                                                        <span className="text-[9px] font-bold">Custom</span>
                                                    ) : (
                                                        <img src={`/flags/${ft}.svg`} alt={ft} className="h-10 w-auto" style={{ objectFit: 'contain' }} />
                                                    )}
                                                </button>
                                            ))}
                                        </div>

                                        {config.badge.type === 'CUSTOM' && (
                                            <div className={`relative border-dashed border-2 rounded-lg p-2 text-center cursor-pointer transition-colors ${darkMode ? 'bg-slate-800 border-slate-600 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'}`}>
                                                <span className="text-xs text-slate-500">Upload custom badge</span>
                                                <input type="file" accept="image/*" onChange={handleBadgeUpload} className="absolute inset-0 opacity-0 cursor-pointer"/>
                                            </div>
                                        )}

                                        <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                                            <label className="text-xs font-bold text-slate-500">Vertical Orientation</label>
                                            <input type="checkbox" checked={config.badge.isVertical} onChange={e => setConfig({...config, badge: {...config.badge, isVertical: e.target.checked}})} className="toggle-checkbox" />
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Graphic Size</span><span className="text-slate-400">{(config.badge.graphicScale || 1).toFixed(1)}</span></label>
                                            <input type="range" min="0.5" max="2.5" step="0.1" value={config.badge.graphicScale || 1} onChange={e => setConfig({...config, badge: {...config.badge, graphicScale: parseFloat(e.target.value)}})} className="w-full"/>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Graphic Vertical</span><span className="text-slate-400">{config.badge.graphicVOffset || 0}</span></label>
                                            <input type="range" min="-20" max="20" step="1" value={config.badge.graphicVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, graphicVOffset: parseFloat(e.target.value)}})} className="w-full"/>
                                        </div>
                                    </div>

                                    {/* Identifier Text checkbox */}
                                    <div className={`flex justify-between items-center p-2 rounded border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                                        <label className="text-xs font-bold text-slate-500">Show Identifier Text</label>
                                        <input type="checkbox" checked={config.badge.showIdentifier} onChange={e => setConfig({...config, badge: {...config.badge, showIdentifier: e.target.checked}})} className="toggle-checkbox" />
                                    </div>

                                    {config.badge.showIdentifier && (
                                        <div className={`p-3 rounded border space-y-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                                            <div className="flex gap-2">
                                                <input type="text" value={config.badge.identifierText} onChange={e => setConfig({...config, badge: {...config.badge, identifierText: e.target.value.toUpperCase()}})} className={`flex-1 p-1.5 border rounded text-xs uppercase ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}`} placeholder="UK"/>
                                                <input type="color" value={config.badge.identifierTextColor || '#ffffff'} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextColor: e.target.value}})} className="w-8 h-8 rounded border p-0 overflow-hidden"/>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Text Size</span><span className="text-slate-400">{config.badge.identifierTextSize || 14}</span></label>
                                                <input type="range" min="8" max="50" step="1" value={config.badge.identifierTextSize || 14} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextSize: parseInt(e.target.value)}})} className="w-full"/>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 mb-1 flex justify-between"><span>Text Vertical</span><span className="text-slate-400">{config.badge.identifierTextVOffset || 0}</span></label>
                                                <input type="range" min="-20" max="20" step="1" value={config.badge.identifierTextVOffset || 0} onChange={e => setConfig({...config, badge: {...config.badge, identifierTextVOffset: parseInt(e.target.value)}})} className="w-full"/>
                                            </div>
                                        </div>
                                    )}

                                    {/* Background colour */}
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 mb-1 block">Background</label>
                                        <div className="flex gap-2">
                                            {['BLUE', 'GREEN', 'BLACK', 'NONE'].map((c: any) => (
                                                <button key={c} onClick={() => setConfig({...config, badge: {...config.badge, backgroundColor: c}})} className={`flex-1 h-6 rounded border ${config.badge.backgroundColor === c ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`} style={{ backgroundColor: c === 'NONE' ? 'transparent' : c.toLowerCase(), backgroundImage: c === 'NONE' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)' : 'none', backgroundSize: '10px 10px' }} title={c}/>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {!shouldHidePro && <div className="space-y-2"><div className={`flex justify-between items-center p-3 rounded-lg border ${darkMode ? 'bg-red-900/20 border-red-900/30' : 'bg-red-50 border-red-100'}`}><label className={`text-sm font-bold flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}><EyeOff className="w-4 h-4"/> Show Plate Mode <ProBadge /></label><input type="checkbox" checked={config.isShowPlate} onChange={e => setConfig({...config, isShowPlate: e.target.checked})} className="w-5 h-5 accent-red-600"/></div>
                        {config.isShowPlate && (<div className={`pl-4 py-2 space-y-2 text-xs border-l-2 ${darkMode ? 'bg-red-900/10 border-red-900/30 text-slate-300' : 'bg-red-50 border-red-200 text-slate-900'}`}><label className="flex items-center gap-2"><input type="checkbox" checked={config.showPlateOverrides.removeDealer} onChange={e => setConfig({...config, showPlateOverrides: {...config.showPlateOverrides, removeDealer: e.target.checked}})}/> Remove Company Name</label><label className="flex items-center gap-2"><input type="checkbox" checked={config.showPlateOverrides.removeBSAU} onChange={e => setConfig({...config, showPlateOverrides: {...config.showPlateOverrides, removeBSAU: e.target.checked}})}/> Remove BSAU</label><label className="flex items-center gap-2"><input type="checkbox" checked={config.showPlateOverrides.reduceSpace} onChange={e => setConfig({...config, showPlateOverrides: {...config.showPlateOverrides, reduceSpace: e.target.checked}})}/> Reduce Space (1/4 Width)</label></div>)}
                    </div>}
                </>
            )}

            {activeTab === 'style' && (
                <>
                    <FontSelect label="Font Family" fonts={ALL_FONTS} selectedFont={config.mainFontFamily} onChange={v => {
                        const defaults = FONT_DEFAULTS[v] || {};
                        setConfig({
                            ...config, 
                            mainFontFamily: v,
                            mainTextVerticalOffset: defaults.vOffset !== undefined ? defaults.vOffset : 0,
                            mainFontSize: defaults.fontSize !== undefined ? defaults.fontSize : 99
                        });
                    }} darkMode={darkMode} />
                    {!shouldHidePro && <div className={`border rounded-lg overflow-hidden transition-all ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                        <button onClick={() => setShowTextEffects(!showTextEffects)} className={`w-full flex items-center justify-between p-3 text-sm font-bold ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100'}`}><span>Text Styling <ProBadge /></span><div className="flex items-center gap-2"><span className="text-xs font-normal text-slate-500">{config.textEffect.replace('_', ' ')}</span>{showTextEffects ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}</div></button>
                        {showTextEffects && (
                            <div className={`p-3 border-t space-y-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                {/* Bold/Italic toggles */}
                                <div className="flex gap-2">
                                    <button 
                                        type="button"
                                        onClick={() => setConfig({...config, mainTextBold: !config.mainTextBold})}
                                        className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm font-bold rounded border ${config.mainTextBold ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                    >
                                        <span className="font-bold">B</span> Bold
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setConfig({...config, mainTextItalic: !config.mainTextItalic})}
                                        className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm font-bold rounded border ${config.mainTextItalic ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                    >
                                        <span className="italic">I</span> Italic
                                    </button>
                                </div>
                                {/* Text effects grid */}
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 mb-1 block">Text Effect</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {Object.values(TextEffect).map(effect => (
                                            <button key={effect} onClick={() => setConfig({...config, textEffect: effect})} className={`text-xs py-2 rounded font-bold transition-colors ${config.textEffect === effect ? 'bg-blue-600 text-white' : (darkMode ? 'bg-slate-900 text-slate-400 hover:bg-slate-700' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200')}`}>{effect.replace('_', ' ')}</button>
                                        ))}
                                    </div>
                                </div>
                                {/* Disclaimer */}
                                <p className={`text-[10px] p-2 rounded ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                                    ⚠️ Text effects are not road legal. For show plates and sign making only.
                                </p>
                            </div>
                        )}
                    </div>}
                    <div className={`flex items-center gap-2 p-2 border rounded text-xs ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}><label className="flex-1 flex items-center gap-2 font-bold text-slate-500 cursor-pointer"><input type="checkbox" checked={config.forceUppercase} onChange={e => setConfig({...config, forceUppercase: e.target.checked})} className="toggle-checkbox"/>{config.forceUppercase ? <Lock className="w-3 h-3"/> : <Unlock className="w-3 h-3"/>} Force Caps</label>{!shouldHidePro && <><div className="w-[1px] h-4 bg-slate-500/20"></div><label className="flex-1 flex items-center gap-2 font-bold text-slate-500 cursor-pointer"><input type="checkbox" checked={config.enableKerning} onChange={e => setConfig({...config, enableKerning: e.target.checked})} className="toggle-checkbox"/><AlignEndHorizontal className="w-3 h-3"/> Kerning <ProBadge /></label></>}</div>
                    {!shouldHidePro && <div className="flex items-center gap-2 mb-4"><label className={`flex-1 cursor-pointer border border-dashed rounded p-2 text-xs text-center transition-colors ${darkMode ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}><Upload className="w-3 h-3 inline mr-1 mb-0.5" /> Upload Custom .TTF <ProBadge /><input type="file" accept=".ttf" onChange={handleFontUpload} className="hidden" /></label></div>}
                    {!shouldHidePro && <div className={`p-3 rounded border space-y-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-center text-xs font-bold text-slate-500"><span>Text Size <ProBadge /> <span className="font-normal text-slate-400 text-[9px]">(99 = legal 79x50mm)</span></span><button onClick={handleAutoFit} className="flex items-center gap-1 text-blue-500 hover:text-blue-600" title="Auto-calculate optimal size"><Wand2 className="w-3 h-3" /> Auto-Fit <ProBadge /></button></div>
                        <div className="flex gap-2">
                            <div className="flex-1 relative">
                                <NumericInput
                                    value={config.mainFontSize}
                                    onChange={v => setConfig({...config, mainFontSize: v})}
                                    className={`w-full p-2 pr-8 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                />
                                <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.min(200, config.mainFontSize + 1);
                                              setConfig({...config, mainFontSize: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            +
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.max(10, config.mainFontSize - 1);
                                              setConfig({...config, mainFontSize: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            -
                                        </button>
                                    </div>
                            </div>
                        
                        {/* Letter Spacing Input with Label */}
                        <div className="flex-1 relative">
                            <label className="absolute -top-4 left-0 text-[10px] text-slate-500 font-bold">Letter Space <ProBadge /></label>
                            <div className="relative">
                                <NumericInput
                                    value={config.letterSpacing}
                                    onChange={v => setConfig({...config, letterSpacing: v})}
                                    className={`w-full p-2 pr-8 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                />
                                <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.min(50, config.letterSpacing + 1);
                                              setConfig({...config, letterSpacing: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            +
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.max(-50, config.letterSpacing - 1);
                                              setConfig({...config, letterSpacing: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            -
                                        </button>
                                    </div>
                            </div>
                        </div>
                        </div>
                        <div className="flex justify-between text-xs font-bold text-slate-500"><span>Vertical Pos</span><span>Height Scale</span></div>
                        <div className="flex gap-2">
                            <div className="flex-1 relative">
                                <NumericInput
                                    value={config.mainTextVerticalOffset}
                                    onChange={v => setConfig({...config, mainTextVerticalOffset: v})}
                                    className={`w-full p-2 pr-8 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                />
                                <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                        <button 
                                            type="button"
                                            onClick={() => setConfig({...config, mainTextVerticalOffset: config.mainTextVerticalOffset + 0.5})}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            +
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setConfig({...config, mainTextVerticalOffset: config.mainTextVerticalOffset - 0.5})}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            -
                                        </button>
                                    </div>
                            </div>
                            <div className="flex-1 relative">
                                <NumericInput
                                    value={config.mainTextHeightScale}
                                    onChange={v => setConfig({...config, mainTextHeightScale: v})}
                                    className={`w-full p-2 pr-8 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                />
                                <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.min(2, config.mainTextHeightScale + 0.1);
                                              setConfig({...config, mainTextHeightScale: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            +
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => {
                                              const newVal = Math.max(0.1, config.mainTextHeightScale - 0.1);
                                              setConfig({...config, mainTextHeightScale: newVal});
                                            }}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            -
                                        </button>
                                    </div>
                            </div>
                        </div>
                    </div>}
                    {null}
                </>
            )}

            {activeTab === 'footer' && (
                <>
                    {/* SLOGAN SECTION */}
                    <div className={`p-4 border rounded-xl space-y-3 mb-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                        <label className="text-sm font-bold flex items-center gap-2">Slogan Styling</label>
                        <FontSelect label="Slogan Font" fonts={ALL_FONTS} selectedFont={config.sloganConfig.fontFamily} onChange={v => updateSloganConfig('fontFamily', v)} darkMode={darkMode} />
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] text-slate-400">Size (mm)</label>
                                <div className="relative">
                                    <NumericInput
                                        value={config.sloganConfig.fontSize}
                                        onChange={v => updateSloganConfig('fontSize', v)}
                                        className={`w-full p-1 pr-6 border rounded text-xs ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                    />
                                    <div className="absolute right-0.5 top-0 bottom-0 flex flex-col">
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                  const newVal = Math.min(50, config.sloganConfig.fontSize + 0.5);
                                                  updateSloganConfig('fontSize', newVal);
                                                }}
                                                className={`flex-1 px-1 text-[10px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                            >
                                                +
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                  const newVal = Math.max(1, config.sloganConfig.fontSize - 0.5);
                                                  updateSloganConfig('fontSize', newVal);
                                                }}
                                                className={`flex-1 px-1 text-[10px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                            >
                                                -
                                            </button>
                                        </div>
                                </div>
                            </div>
                            <div><label className="text-[10px] text-slate-400">Color</label><input type="color" value={config.sloganConfig.color} onChange={e => updateSloganConfig('color', e.target.value)} className="h-6 w-full border rounded cursor-pointer"/></div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => updateSloganConfig('bold', !config.sloganConfig.bold)}
                                className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.sloganConfig.bold ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                            >
                                <span className="font-bold w-4 text-center">B</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => updateSloganConfig('italic', !config.sloganConfig.italic)}
                                className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.sloganConfig.italic ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                            >
                                <span className="italic w-4 text-center">I</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => updateSloganConfig('outline', !config.sloganConfig.outline)}
                                className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.sloganConfig.outline ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                title="Outline effect"
                            >
                                <span className="w-4 text-center" style={{ WebkitTextStroke: '0.5px currentColor' }}>O</span>
                            </button>
                        </div>
                        <div className="flex gap-1 pt-2">
                            <button onClick={() => setConfig({...config, sloganLocation: 'ABOVE_BORDER'})} className={`flex-1 py-1 text-[10px] border rounded ${config.sloganLocation === 'ABOVE_BORDER' ? 'bg-slate-900 border-blue-500 text-blue-400 font-bold' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'text-slate-500')}`}>Above Border</button>
                            <button onClick={() => setConfig({...config, sloganLocation: 'ON_BORDER'})} className={`flex-1 py-1 text-[10px] border rounded ${config.sloganLocation === 'ON_BORDER' ? 'bg-slate-900 border-blue-500 text-blue-400 font-bold' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'text-slate-500')}`}>On Border</button>
                            <button onClick={() => setConfig({...config, sloganLocation: 'CUSTOM'})} className={`flex-1 py-1 text-[10px] border rounded ${config.sloganLocation === 'CUSTOM' ? 'bg-slate-900 border-blue-500 text-blue-400 font-bold' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'text-slate-500')}`}>Custom</button>
                        </div>
                        {config.sloganLocation === 'CUSTOM' && (
                            <div className="pt-2 border-t border-slate-500/20">
                                <label className={`text-[10px] font-bold block mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Vertical Offset: <span className="font-normal">{config.sloganConfig.yOffset || 0}mm</span>
                                </label>
                                <input type="range" min="-50" max="50" step="1" value={config.sloganConfig.yOffset || 0} onChange={e => updateSloganConfig('yOffset', Number(e.target.value))} className="w-full"/>
                            </div>
                        )}
                    </div>

                    {/* DEALER IDENTITY SECTION */}
                    <div className={`p-4 border rounded-xl space-y-3 mb-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-center"><span className={`text-sm font-bold ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>Dealer Identity</span>{!shouldHidePro && <div className={`flex rounded-lg p-1 border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}><button onClick={() => setConfig({...config, useDealerLogo: false})} className={`px-3 py-1 text-xs rounded ${!config.useDealerLogo ? 'bg-slate-700 text-white' : 'text-slate-500'}`}>Text</button><button onClick={() => setConfig({...config, useDealerLogo: true})} className={`px-3 py-1 text-xs rounded ${config.useDealerLogo ? 'bg-slate-700 text-white' : 'text-slate-500'}`}>Logo <ProBadge /></button></div>}</div>
                        {!shouldHidePro && config.useDealerLogo ? (
                            <div className="space-y-2">
                                {config.dealerLogo && <img src={config.dealerLogo} alt="Logo" className="h-8 object-contain mx-auto border bg-white p-1 rounded"/>}
                                <div className={`relative border-dashed border-2 rounded-lg p-3 text-center cursor-pointer transition-colors ${darkMode ? 'bg-slate-900 border-slate-700 hover:bg-slate-800' : 'bg-white hover:bg-slate-50'}`}><span className="text-xs text-slate-500">Click to upload logo image</span><input type="file" accept="image/*" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer"/></div>
                                <div><label className="text-xs text-slate-500 mb-1 flex justify-between">Logo Height (mm) <span className="text-slate-400">{config.dealerLogoHeight || 8}mm</span></label><input type="range" min="4" max="25" step="1" value={config.dealerLogoHeight || 8} onChange={e => setConfig({...config, dealerLogoHeight: Number(e.target.value)})} className="w-full"/></div>
                            </div>
                        ) : (
                           <div className="space-y-4">
                               <div>
                                   <label className="text-xs font-bold text-slate-500 mb-1">Company Name</label>
                                   <input type="text" placeholder="DEALER NAME" value={config.dealerText.text} onChange={e => updateDealer('text', e.target.value)} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}/>
                                   <div className="flex gap-2 mt-1">
                                       <FontSelect fonts={ALL_FONTS} selectedFont={config.dealerText.fontFamily} onChange={v => updateDealer('fontFamily', v)} darkMode={darkMode} />
                                       <div className="relative">
                                           <NumericInput
                                               className={`w-16 p-2 pr-6 border rounded text-xs ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                                               value={config.dealerText.fontSize}
                                               onChange={v => updateDealer('fontSize', v)}
                                           />
                                           <div className="absolute right-0.5 top-0 bottom-0 flex flex-col">
                                                   <button
                                                       type="button"
                                                       onClick={() => {
                                                         const newVal = Math.min(20, config.dealerText.fontSize + 0.5);
                                                         updateDealer('fontSize', newVal);
                                                       }}
                                                       className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                   >
                                                       +
                                                   </button>
                                                   <button 
                                                       type="button"
                                                       onClick={() => {
                                                         const newVal = Math.max(1, config.dealerText.fontSize - 0.5);
                                                         updateDealer('fontSize', newVal);
                                                       }}
                                                       className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                   >
                                                       -
                                                   </button>
                                               </div>
                                       </div>
                                       <input type="color" className="w-10 h-9 border rounded" value={config.dealerText.color} onChange={e => updateDealer('color', e.target.value)}/>
                                   </div>
                                   <div className="flex gap-2 mt-1">
                                       <button
                                           type="button"
                                           onClick={() => updateDealer('bold', !config.dealerText.bold)}
                                           className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.dealerText.bold ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                       >
                                           <span className="font-bold w-4 text-center">B</span>
                                       </button>
                                       <button
                                           type="button"
                                           onClick={() => updateDealer('italic', !config.dealerText.italic)}
                                           className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.dealerText.italic ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                       >
                                           <span className="italic w-4 text-center">I</span>
                                       </button>
                                       <button
                                           type="button"
                                           onClick={() => updateDealer('outline', !config.dealerText.outline)}
                                           className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.dealerText.outline ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                           title="Outline effect"
                                       >
                                           <span className="w-4 text-center" style={{ WebkitTextStroke: '0.5px currentColor' }}>O</span>
                                       </button>
                                   </div>
                               </div>
                               <hr className={`${darkMode ? 'border-slate-700' : 'border-slate-200'}`}/>
                               <div>
                                   <label className="text-xs font-bold text-slate-500 mb-1">Postcode</label>
                                   <input type="text" placeholder="POSTCODE" value={config.dealerPostcode} onChange={e => setConfig({...config, dealerPostcode: e.target.value})} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`}/>
                                   <div className="flex gap-2 mt-1">
                                       <FontSelect fonts={ALL_FONTS} selectedFont={config.dealerPostcodeConfig.fontFamily || config.dealerText.fontFamily} onChange={v => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, fontFamily: v}})} darkMode={darkMode} />
                                       <div className="relative">
                                           <NumericInput className={`w-16 p-2 pr-6 border rounded text-xs ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : ''}`} value={config.dealerPostcodeConfig.fontSize} onChange={v => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, fontSize: v}})}/>
                                           <div className="absolute right-0.5 top-0 bottom-0 flex flex-col">
                                                   <button type="button" onClick={() => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, fontSize: Math.min(20, config.dealerPostcodeConfig.fontSize + 0.5)}})} className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}>+</button>
                                                   <button type="button" onClick={() => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, fontSize: Math.max(1, config.dealerPostcodeConfig.fontSize - 0.5)}})} className={`flex-1 px-0.5 text-[9px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}>-</button>
                                               </div>
                                       </div>
                                       <input type="color" className="w-10 h-9 border rounded" value={config.dealerPostcodeConfig.color} onChange={e => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, color: e.target.value}})}/>
                                   </div>
                                   <div className="flex gap-2 mt-1">
                                       <button
                                           type="button"
                                           onClick={() => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, bold: !config.dealerPostcodeConfig.bold}})}
                                           className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.dealerPostcodeConfig.bold ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                       >
                                           <span className="font-bold w-4 text-center">B</span>
                                       </button>
                                       <button
                                           type="button"
                                           onClick={() => setConfig({...config, dealerPostcodeConfig: {...config.dealerPostcodeConfig, italic: !config.dealerPostcodeConfig.italic}})}
                                           className={`flex-1 flex items-center justify-center py-1.5 text-xs rounded border ${config.dealerPostcodeConfig.italic ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600')}`}
                                       >
                                           <span className="italic w-4 text-center">I</span>
                                       </button>
                                   </div>
                               </div>
                               <div><label className="text-xs text-slate-500">Separator Width (mm)</label><input type="range" min="2" max="30" step="1" value={config.dealerSeparator} onChange={e => setConfig({...config, dealerSeparator: Number(e.target.value)})} className="w-full"/></div>
                           </div>
                        )}
                        <div className="flex gap-1 pt-2">
                            <button onClick={() => setConfig({...config, dealerLocation: 'ABOVE_BORDER'})} className={`flex-1 py-1 text-[10px] border rounded ${config.dealerLocation === 'ABOVE_BORDER' ? 'bg-slate-900 border-blue-500 text-blue-400 font-bold' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'text-slate-500')}`}>Above Border</button>
                            <button onClick={() => setConfig({...config, dealerLocation: 'ON_BORDER'})} className={`flex-1 py-1 text-[10px] border rounded ${config.dealerLocation === 'ON_BORDER' ? 'bg-slate-900 border-blue-500 text-blue-400 font-bold' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-500' : 'text-slate-500')}`}>On Border</button>
                        </div>
                        
                        <div className="flex items-center justify-between pt-2 border-t border-slate-500/20 mt-2">
                            <label className="text-xs font-bold text-slate-500">Corner Placement</label>
                            <input 
                                type="checkbox" 
                                checked={config.supplierInCorner} 
                                onChange={e => setConfig({...config, supplierInCorner: e.target.checked})} 
                                className="w-4 h-4 accent-blue-600"
                            />
                        </div>

                        <div className="mt-2">
                            <label className="text-xs text-slate-500 mb-1">Dealer Height Adjustment (mm)</label>
                            <div className="relative">
                                <NumericInput
                                    value={config.dealerVerticalOffset}
                                    onChange={v => setConfig({...config, dealerVerticalOffset: v})}
                                    className={`w-full p-2 pr-8 border rounded text-xs ${darkMode ? 'bg-slate-900 border-slate-700 text-white' : ''}`}
                                />
                                <div className="absolute right-1 top-0.5 bottom-0.5 flex flex-col">
                                        <button
                                            type="button"
                                            onClick={() => setConfig({...config, dealerVerticalOffset: config.dealerVerticalOffset + 0.5})}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            +
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setConfig({...config, dealerVerticalOffset: config.dealerVerticalOffset - 0.5})}
                                            className={`flex-1 px-1.5 text-xs ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            -
                                        </button>
                                    </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* BSAU SECTION - Using CSS Grid approach */}
                    <div className={`grid border rounded-xl mb-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`} style={{ gridTemplateRows: config.bsauText.show ? 'auto 1fr' : 'auto 0fr', transition: 'grid-template-rows 0.3s ease' }}>
                        <div className="flex justify-between items-center p-3 min-h-0">
                            <label className="text-sm font-bold">BSAU 145e Mark</label>
                            <input type="checkbox" checked={config.bsauText.show} onChange={e => updateBSAU('show', e.target.checked)} className="w-4 h-4 accent-blue-600"/>
                        </div>
                        <div className="overflow-hidden min-h-0">
                            <div className="px-3 pb-3 space-y-3">
                                <input type="text" value={config.bsauText.text} onChange={e => updateBSAU('text', e.target.value)} className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`} placeholder="BS AU 145e"/>
                                <div className="mb-2">
                                    <label className={`text-[10px] block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Font</label>
                                    <FontSelect fonts={ALL_FONTS} selectedFont={config.bsauText.fontFamily} onChange={v => updateBSAU('fontFamily', v)} darkMode={darkMode} />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className={`text-[10px] block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Size (mm)</label>
                                        <div className="relative">
                                            <NumericInput
                                                className={`w-full p-2 pr-6 border rounded text-xs ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                                                value={config.bsauText.fontSize}
                                                onChange={v => updateBSAU('fontSize', v)}
                                            />
                                            <div className="absolute right-0.5 top-0 bottom-0 flex flex-col">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                          const newVal = Math.min(20, config.bsauText.fontSize + 0.5);
                                                          updateBSAU('fontSize', newVal);
                                                        }}
                                                        className={`flex-1 px-1 text-[10px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                    >
                                                        +
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                          const newVal = Math.max(1, config.bsauText.fontSize - 0.5);
                                                          updateBSAU('fontSize', newVal);
                                                        }}
                                                        className={`flex-1 px-1 text-[10px] ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                                    >
                                                        -
                                                    </button>
                                                </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={`text-[10px] block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Color</label>
                                        <input type="color" className="w-full h-9 border rounded cursor-pointer" value={config.bsauText.color} onChange={e => updateBSAU('color', e.target.value)}/>
                                    </div>
                                    <div>
                                        <label className={`text-[10px] block mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Location</label>
                                        <select value={config.bsauLocation} onChange={e => setConfig({...config, bsauLocation: e.target.value as any})} className={`w-full p-2 text-xs border rounded ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                                            <option value="ON_BORDER">On Border</option>
                                            <option value="ABOVE_BORDER">Above</option>
                                            <option value="BELOW_BORDER">Below</option>
                                            <option value="CUSTOM">Custom</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => updateBSAU('bold', !config.bsauText.bold)} className={`flex-1 py-1.5 text-xs font-bold rounded border transition-colors ${config.bsauText.bold ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400 hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50')}`}>
                                        <span className="font-bold">B</span> Bold
                                    </button>
                                    <button onClick={() => updateBSAU('italic', !config.bsauText.italic)} className={`flex-1 py-1.5 text-xs font-bold rounded border transition-colors ${config.bsauText.italic ? 'bg-blue-600 text-white border-blue-600' : (darkMode ? 'bg-slate-900 border-slate-600 text-slate-400 hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50')}`}>
                                        <span className="italic">I</span> Italic
                                    </button>
                                </div>
                                <div className="pt-2 border-t border-slate-500/20 space-y-2">
                                    <div>
                                        <label className={`text-[10px] font-bold block mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Horizontal Offset: <span className="font-normal">{config.bsauText.xOffset || 0}mm</span>
                                        </label>
                                        <input type="range" min="0" max="150" step="1" value={config.bsauText.xOffset || 0} onChange={e => updateBSAU('xOffset', Number(e.target.value))} className="w-full"/>
                                    </div>
                                    {config.bsauLocation === 'CUSTOM' && (
                                        <div>
                                            <label className={`text-[10px] font-bold block mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                Vertical Offset: <span className="font-normal">{config.bsauText.yOffset || 0}mm</span>
                                            </label>
                                            <input type="range" min="-50" max="50" step="1" value={config.bsauText.yOffset || 0} onChange={e => updateBSAU('yOffset', Number(e.target.value))} className="w-full"/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* LIPPED PLATE SECTION - moved to bottom */}
                    {!shouldHidePro && <div className={`p-4 border rounded-xl space-y-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-center">
                            <label className="text-sm font-bold flex items-center gap-2">Lipped Plate Attachment <ProBadge /></label>
                            <input type="checkbox" checked={config.lippedPlate} onChange={e => setConfig({...config, lippedPlate: e.target.checked})} className="w-4 h-4 accent-blue-600"/>
                        </div>
                        {config.lippedPlate && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    {/* Lip Height */}
                                    <div>
                                        <label className="text-xs text-slate-500 mb-1 flex justify-between">Height (Max 40mm) <span className="text-slate-400">{config.lipHeight}mm</span></label>
                                        <input type="range" min="5" max="40" step="1" value={config.lipHeight} onChange={e => setConfig({...config, lipHeight: Number(e.target.value)})} className="w-full"/>
                                    </div>
                                    {/* Lip Width */}
                                    <div>
                                        <label className="text-xs text-slate-500 mb-1 flex justify-between">Width <span className="text-slate-400">{config.lipWidth || currentDims.width}mm</span></label>
                                        <input type="range" min="50" max={currentDims.width} step="1" value={config.lipWidth || currentDims.width} onChange={e => setConfig({...config, lipWidth: Number(e.target.value)})} className="w-full"/>
                                    </div>
                                </div>

                                {/* Background Image or Color */}
                                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <label className="text-xs font-bold text-slate-500 mb-2 block">Background</label>
                                    {!config.lipImage && (
                                        <div className="mb-2">
                                            <label className="text-[10px] text-slate-500 mb-1 block">Color (defaults to plate color)</label>
                                            <input type="color" value={config.lipBackgroundColor || (config.type === PlateType.FRONT ? '#ffffff' : '#facc15')} onChange={e => setConfig({...config, lipBackgroundColor: e.target.value})} className="w-full h-8 rounded cursor-pointer border"/>
                                        </div>
                                    )}
                                    <div className={`relative border-dashed border-2 rounded-lg p-2 text-center cursor-pointer transition-colors ${darkMode ? 'border-slate-600 hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
                                        <span className="text-xs text-slate-500">{config.lipImage ? 'Change Image' : 'Upload Background Image'}</span>
                                        <input type="file" accept="image/*" onChange={handleLipUpload} className="absolute inset-0 opacity-0 cursor-pointer"/>
                                    </div>
                                    {config.lipImage && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <img src={config.lipImage} alt="Lip BG" className="h-6 object-contain flex-1"/>
                                            <button onClick={() => setConfig({...config, lipImage: ''})} className="text-red-500 hover:text-red-400 text-xs">Remove</button>
                                        </div>
                                    )}
                                </div>

                                {/* LIP TEXT BOXES */}
                                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-xs font-bold text-slate-500">Lip Text Boxes ({(config.lipTexts || []).length})</label>
                                        <button onClick={() => {
                                            const newText: LipTextItem = {
                                                id: Date.now().toString(),
                                                enabled: true,
                                                text: 'New Text',
                                                fontFamily: 'Arial',
                                                fontSize: 4,
                                                color: '#000000',
                                                bold: false,
                                                italic: false,
                                                xOffset: 0,
                                                yOffset: 0
                                            };
                                            setConfig({...config, lipTexts: [...(config.lipTexts || []), newText]});
                                        }} className={`px-2 py-1 rounded text-[10px] font-bold ${darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                                            + Add Text
                                        </button>
                                    </div>
                                    {(config.lipTexts || []).length > 0 && (
                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                            {(config.lipTexts || []).map((textItem, idx) => (
                                                <div key={textItem.id} className={`p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <input type="checkbox" checked={textItem.enabled} onChange={e => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, enabled: e.target.checked} : t)})} className="w-3 h-3 accent-blue-600"/>
                                                        <span className="text-xs text-slate-500 flex-1">Text {idx + 1}</span>
                                                        <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).filter(t => t.id !== textItem.id)})} className="text-red-500 hover:text-red-400 text-xs">✕</button>
                                                    </div>
                                                    <input type="text" value={textItem.text} onChange={e => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, text: e.target.value} : t)})} placeholder="Enter text..." className={`w-full p-1.5 mb-2 border rounded text-xs ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}/>
                                                    <FontSelect fonts={ALL_FONTS} selectedFont={textItem.fontFamily} onChange={v => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, fontFamily: v} : t)})} darkMode={darkMode} />
                                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                                        <div>
                                                            <label className="text-[10px] text-slate-500 mb-1 block">Color</label>
                                                            <input type="color" value={textItem.color} onChange={e => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, color: e.target.value} : t)})} className="w-full h-7 rounded cursor-pointer"/>
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] text-slate-500 mb-1 block">Size: {textItem.fontSize}mm</label>
                                                            <div className="flex gap-1">
                                                                <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, fontSize: Math.max(1, t.fontSize - 0.5)} : t)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                    <Minus className="w-3 h-3 mx-auto" />
                                                                </button>
                                                                <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, fontSize: Math.min(20, t.fontSize + 0.5)} : t)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                    <Plus className="w-3 h-3 mx-auto" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 mt-2">
                                                        <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, bold: !t.bold} : t)})} className={`flex-1 p-1 rounded text-xs font-bold border ${textItem.bold ? 'bg-blue-600 text-white border-blue-600' : darkMode ? 'bg-slate-700 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600'}`}>B</button>
                                                        <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, italic: !t.italic} : t)})} className={`flex-1 p-1 rounded text-xs italic border ${textItem.italic ? 'bg-blue-600 text-white border-blue-600' : darkMode ? 'bg-slate-700 border-slate-600 text-slate-400' : 'bg-white border-slate-300 text-slate-600'}`}>I</button>
                                                    </div>
                                                    <div className="space-y-2 mt-2">
                                                        <label className="text-[10px] font-bold text-slate-500 block">Position</label>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            <div className="col-span-3">
                                                                <label className="text-[9px] text-slate-400 mb-1 block text-center">Move Up/Down</label>
                                                                <div className="flex gap-1">
                                                                    <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, yOffset: t.yOffset - 0.5} : t)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Up">
                                                                        <ChevronUp className="w-4 h-4 mx-auto" />
                                                                    </button>
                                                                    <div className={`flex-1 px-2 py-1.5 rounded border text-center text-[10px] font-mono flex items-center justify-center ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-slate-50 border-slate-300 text-slate-600'}`}>
                                                                        {textItem.yOffset.toFixed(1)}
                                                                    </div>
                                                                    <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, yOffset: t.yOffset + 0.5} : t)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Down">
                                                                        <ChevronDown className="w-4 h-4 mx-auto" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="col-span-3">
                                                                <label className="text-[9px] text-slate-400 mb-1 block text-center">Move Left/Right</label>
                                                                <div className="flex gap-1">
                                                                    <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, xOffset: t.xOffset - 0.5} : t)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Left">
                                                                        <ChevronLeft className="w-4 h-4 mx-auto" />
                                                                    </button>
                                                                    <div className={`flex-1 px-2 py-1.5 rounded border text-center text-[10px] font-mono flex items-center justify-center ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-slate-50 border-slate-300 text-slate-600'}`}>
                                                                        {textItem.xOffset.toFixed(1)}
                                                                    </div>
                                                                    <button onClick={() => setConfig({...config, lipTexts: (config.lipTexts || []).map(t => t.id === textItem.id ? {...t, xOffset: t.xOffset + 0.5} : t)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Right">
                                                                        <ChevronRight className="w-4 h-4 mx-auto" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* LIP IMAGES */}
                                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-xs font-bold text-slate-500">Lip Images ({config.lipImages.length})</label>
                                        <label className={`cursor-pointer px-2 py-1 rounded text-[10px] font-bold ${darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                                            + Add
                                            <input type="file" accept="image/*" onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onload = (ev) => {
                                                        const newImage = {
                                                            id: Date.now().toString(),
                                                            image: ev.target?.result as string,
                                                            width: 10,
                                                            height: 10,
                                                            xOffset: 0,
                                                            yOffset: 0
                                                        };
                                                        setConfig({...config, lipImages: [...config.lipImages, newImage]});
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                                e.target.value = '';
                                            }} className="hidden"/>
                                        </label>
                                    </div>
                                    {config.lipImages.length > 0 && (
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {config.lipImages.map((img, idx) => (
                                                <div key={img.id} className={`p-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <img src={img.image} alt={`Lip ${idx + 1}`} className="w-8 h-8 object-contain rounded"/>
                                                        <span className="text-xs text-slate-500 flex-1">Image {idx + 1}</span>
                                                        <button onClick={() => setConfig({...config, lipImages: config.lipImages.filter(i => i.id !== img.id)})} className="text-red-500 hover:text-red-400 text-xs">✕</button>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div>
                                                                <label className="text-[10px] text-slate-500 mb-1 block">Width: {img.width}mm</label>
                                                                <div className="flex gap-1">
                                                                    <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, width: Math.max(1, i.width - 1)} : i)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                        <Minus className="w-3 h-3 mx-auto" />
                                                                    </button>
                                                                    <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, width: Math.min(100, i.width + 1)} : i)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                        <Plus className="w-3 h-3 mx-auto" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-slate-500 mb-1 block">Height: {img.height}mm</label>
                                                                <div className="flex gap-1">
                                                                    <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, height: Math.max(1, i.height - 1)} : i)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                        <Minus className="w-3 h-3 mx-auto" />
                                                                    </button>
                                                                    <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, height: Math.min(40, i.height + 1)} : i)})} className={`flex-1 px-2 py-1 rounded text-xs border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                                                        <Plus className="w-3 h-3 mx-auto" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <label className="text-[10px] font-bold text-slate-500 block">Position</label>
                                                            <div className="grid grid-cols-3 gap-2">
                                                                <div className="col-span-3">
                                                                    <label className="text-[9px] text-slate-400 mb-1 block text-center">Move Up/Down</label>
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, yOffset: i.yOffset - 0.5} : i)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Up">
                                                                            <ChevronUp className="w-4 h-4 mx-auto" />
                                                                        </button>
                                                                        <div className={`flex-1 px-2 py-1.5 rounded border text-center text-[10px] font-mono flex items-center justify-center ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-slate-50 border-slate-300 text-slate-600'}`}>
                                                                            {img.yOffset.toFixed(1)}
                                                                        </div>
                                                                        <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, yOffset: i.yOffset + 0.5} : i)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Down">
                                                                            <ChevronDown className="w-4 h-4 mx-auto" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="col-span-3">
                                                                    <label className="text-[9px] text-slate-400 mb-1 block text-center">Move Left/Right</label>
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, xOffset: i.xOffset - 0.5} : i)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Left">
                                                                            <ChevronLeft className="w-4 h-4 mx-auto" />
                                                                        </button>
                                                                        <div className={`flex-1 px-2 py-1.5 rounded border text-center text-[10px] font-mono flex items-center justify-center ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-400' : 'bg-slate-50 border-slate-300 text-slate-600'}`}>
                                                                            {img.xOffset.toFixed(1)}
                                                                        </div>
                                                                        <button onClick={() => setConfig({...config, lipImages: config.lipImages.map(i => i.id === img.id ? {...i, xOffset: i.xOffset + 0.5} : i)})} className={`flex-1 px-2 py-1.5 rounded border ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} title="Move Right">
                                                                            <ChevronRight className="w-4 h-4 mx-auto" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>}
                </>
            )}
        </div>

        <div className={`p-2 md:p-4 border-t gap-1 md:gap-2 flex-shrink-0 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'}`} style={{ display: isMobile ? 'none' : 'flex' }}>
            {/* RNPS Compliance Button */}
            {!shouldHidePro && <button onClick={() => openRNPSModal()} className="w-10 h-10 md:w-12 md:h-12 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center shadow-lg transition-all" title="RNPS Compliance Records"><Shield className="w-5 h-5 md:w-6 md:h-6"/></button>}
            {printSettings.dualMode ? (
                <>
                    <button onClick={() => handlePrintAction('WHITE')} className={`flex-1 h-10 md:h-12 rounded-lg font-bold text-[10px] md:text-xs flex flex-col items-center justify-center gap-0.5 md:gap-1 transition-all border ${darkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}><Printer className="w-3 h-3 md:w-4 md:h-4" />White</button>
                    <button onClick={() => handlePrintAction('YELLOW')} className={`flex-1 h-10 md:h-12 rounded-lg font-bold text-[10px] md:text-xs flex flex-col items-center justify-center gap-0.5 md:gap-1 transition-all border ${darkMode ? 'bg-yellow-900/20 border-yellow-700 text-yellow-500 hover:bg-yellow-900/30' : 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100'}`}><Printer className="w-3 h-3 md:w-4 md:h-4" />Yellow</button>
                    <button onClick={() => handlePrintAction('PAIR')} className={`flex-1 h-10 md:h-12 rounded-lg font-bold text-[10px] md:text-xs flex flex-col items-center justify-center gap-0.5 md:gap-1 transition-all border ${darkMode ? 'bg-blue-900/20 border-blue-700 text-blue-400 hover:bg-blue-900/30' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}><Layers className="w-3 h-3 md:w-4 md:h-4" />Pair</button>
                    {!shouldHidePro && pdfExportSettings.showButton && (
                    <button onClick={handleExport} disabled={exporting} className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center transition-all ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`} title="Export PDF">{exporting ? <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" /> : <Download className="w-4 h-4 md:w-5 md:h-5" />}</button>
                    )}
                </>
            ) : (
                <>
                    {/* PRINT x1 BUTTON */}
                    <div className={`${pdfExportSettings.showButton ? 'flex-[2]' : 'flex-[3]'} flex relative`} ref={printDropdownRef}>
                        <button 
                            onClick={() => handlePrintAction('SINGLE')} 
                            className={`flex-grow h-10 md:h-12 rounded-lg md:rounded-l-lg md:rounded-r-none font-semibold text-xs md:text-sm flex items-center justify-center gap-1 md:gap-2 transition-all border md:border-y md:border-l md:border-r-0 ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}
                        >
                            <Printer className="w-4 h-4 md:w-5 md:h-5" /><span className="hidden md:inline">Print </span>x1
                        </button>
                        <button 
                            onClick={() => setPrintDropdownOpen(!printDropdownOpen)}
                            className={`hidden md:flex w-10 h-10 md:h-12 items-center justify-center rounded-r-lg border transition-all ${darkMode ? 'bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-400' : 'bg-white hover:bg-slate-50 border-slate-300 text-slate-600'}`}
                        >
                            {printDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {printDropdownOpen && (
                            <div className={`absolute bottom-full left-0 mb-2 w-full rounded-lg shadow-xl overflow-hidden border z-50 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                <div className={`text-[10px] font-bold p-2 uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Print to Specific Profile</div>
                                {profiles.length === 0 && <div className="p-2 text-xs italic text-slate-500 text-center">No profiles found</div>}
                                {profiles.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => {
                                            // Check pro features before printing
                                            if (!isPro) {
                                                const violations = checkProFeatures(config);
                                                if (violations.length > 0) {
                                                    setProModalErrors(violations);
                                                    setShowProModal(true);
                                                    setPrintDropdownOpen(false);
                                                    return;
                                                }
                                            }
                                            executePrintJob(p.id);
                                            setPrintDropdownOpen(false);
                                        }}
                                        className={`w-full text-left p-3 text-sm font-medium hover:bg-opacity-50 transition-colors flex justify-between items-center ${darkMode ? 'text-slate-200 hover:bg-slate-800 border-t border-slate-800' : 'text-slate-800 hover:bg-slate-50 border-t border-slate-100'}`}
                                    >
                                        <span>{p.name}</span>
                                        <span className="text-[10px] opacity-50">{p.systemPrinterName.substring(0, 15)}...</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* PRINT x2 BUTTON */}
                    <button onClick={() => handlePrintAction('DOUBLE')} className={`${pdfExportSettings.showButton ? 'flex-1' : 'flex-[2]'} h-10 md:h-12 rounded-lg font-semibold text-xs md:text-sm flex items-center justify-center gap-1 md:gap-2 transition-all border ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}><Layers className="w-4 h-4 md:w-5 md:h-5" />x2</button>
                    
                    {/* PDF EXPORT BUTTON - Conditional */}
                    {!shouldHidePro && pdfExportSettings.showButton && (
                        <button onClick={handleExport} disabled={exporting} className={`w-10 h-10 md:w-12 md:h-12 rounded-lg font-semibold flex items-center justify-center shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed ${darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`} title="Export PDF">{exporting ? <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" /> : <Download className="w-4 h-4 md:w-5 md:h-5" />}</button>
                    )}
                </>
            )}
        </div>
      </aside>

      <main 
        ref={mainPreviewRef}
        onClick={(e) => {
          // On mobile, clicking the main area (not the plate) resets zoom if zoomed
          if (isMobile && manualZoom > 1.0 && e.target === e.currentTarget) {
            setManualZoom(1);
            setPanPosition({ x: 0, y: 0 });
            setIsSuperZoom(false);
          }
        }}
        className={`${isMobile && hideOriginalPreview ? 'flex-none h-0 overflow-visible' : isMobile ? 'flex-1 overflow-hidden' : 'flex-1 overflow-hidden'} flex flex-col items-center ${isMobile && !hideOriginalPreview ? 'justify-center p-2 pb-0' : isMobile ? '' : 'justify-center p-8'} relative transition-all ${darkMode ? 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-slate-950' : 'bg-slate-200'} ${isPanning ? 'cursor-grabbing' : manualZoom > 1.2 ? 'cursor-grab' : ''}`}
        style={isMobile && hideOriginalPreview ? { height: 0, flex: 'none', padding: 0 } : isMobile ? { paddingBottom: '0' } : undefined}
        onWheel={handleWheelZoom}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={isMobile ? undefined : handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Tap-to-type input is rendered as an overlay on the floating plate below — not here */}
        
        {/* Duplicate Registration Input Box - Directly on top of plate preview (Desktop) - DISABLED */}
        {false && !isMobile && (
          <div 
            className="absolute left-1/2 transform -translate-x-1/2 z-30"
            style={{ 
              bottom: `${(currentDims.height * previewScale * manualZoom) + 8}px`,
              width: `${Math.max(280, currentDims.width * previewScale * manualZoom)}px`,
              maxWidth: '90vw'
            }}
          >
            <div className="relative w-full h-full">
              <textarea 
                value={config.registration} 
                onChange={e => setConfig({...config, registration: config.forceUppercase ? e.target.value.toUpperCase() : e.target.value})} 
                onKeyDown={handleRegKeyDown} 
                onClick={(e) => e.currentTarget.select()} 
                className={`w-full h-full p-4 text-4xl font-mono uppercase border-0 rounded-xl text-center focus:ring-0 outline-none font-bold tracking-wider bg-transparent text-transparent ${config.isShowPlate && config.showPlateOverrides.reduceSpace ? 'text-transparent' : ''}`} 
                rows={1}
                style={{ resize: 'none' }}
              />
              {/* Visual overlay showing reduced spaces when enabled */}
              {config.isShowPlate && config.showPlateOverrides.reduceSpace && (
                <div 
                  className="absolute inset-0 p-4 text-4xl font-mono uppercase text-center font-bold tracking-wider pointer-events-none flex items-center justify-center whitespace-pre-wrap"
                  style={{
                    color: darkMode ? '#e2e8f0' : '#1e293b',
                    fontFamily: 'monospace'
                  }}
                >
                  {config.registration.split('').map((char, i) => {
                    if (char === ' ' || char === '\u00A0') {
                      return <span key={i} style={{ fontSize: '25%', display: 'inline-block' }}>{char}</span>;
                    }
                    return <span key={i}>{char === '\n' ? <br key={i}/> : char}</span>;
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Plate preview container - flexible height */}
        <div 
            className={`relative flex flex-col items-center ${isMobile ? 'justify-end pb-24' : 'justify-center'} ${isMobile ? 'flex-1' : ''} ${isMobile && hideOriginalPreview ? 'hidden' : ''}`}
            // style={isMobile && mobileCompact ? { maxHeight: '50vh' } : {}} // Disabled to ensure plate sticks to bottom
        >
            <div 
              id="plate-preview-container"
              ref={previewContainerRef} 
              onClick={(e) => {
                // NUCLEAR MODAL CHECK: Check what's visually on top
                const x = e.clientX;
                const y = e.clientY;
                const elements = document.elementsFromPoint(x, y);
                const topElement = elements[0];
                
                // If the top element is NOT the preview container or its children, then something else (modal) is covering it
                if (topElement && previewContainerRef.current && !previewContainerRef.current.contains(topElement) && topElement !== previewContainerRef.current) {
                    e.stopPropagation();
                    return;
                }

                // On mobile, tap plate to type only if not zoomed (touch handler handles zoomed state)
                if (isMobile && manualZoom <= 1.0 && !isPanning) {
                  e.stopPropagation();
                  setShowMobileRegistrationInput(true);
                }
              }}
              onDoubleClick={(e) => {
                // Prevent double tap zoom on mobile
                if (isMobile) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className={`${isMobile ? 'cursor-pointer' : 'shadow-2xl rounded-xl overflow-hidden'} relative ${isMobile && showMobileRegistrationInput ? 'ring-4 ring-blue-500 ring-opacity-75' : ''}`}
              style={{ 
                transform: `scale(${previewScale * manualZoom}) translate(${panPosition.x / Math.max(0.1, previewScale * manualZoom)}px, ${panPosition.y / Math.max(0.1, previewScale * manualZoom)}px)`,
                transformOrigin: isMobile ? 'center center' : 'center center',
                transition: isPanning ? 'none' : 'transform 0.15s ease-out',
                touchAction: 'none'
              }}
            >
                <PlateView id="plate-preview-main" config={isMobile ? {...config, type: PlateType.REAR} : config} dimensions={currentDims} showCropLines={pdfExportSettings.enableCropMarks && pdfExportSettings.showCropLinesPreview} cropLinesOffset={pdfExportSettings.cropMarksOffset} />
            </div>
            {/* Typing indicator with blinking cursor */}
            {isMobile && showMobileRegistrationInput && !isAnyModalOpen && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-1">
                  <span className="text-white font-mono text-sm">Typing...</span>
                  <span className="text-blue-400 font-bold text-xl cursor-blink">|</span>
                </div>
              </div>
            )}
            {/* Mobile zoom hint - show when not zoomed */}
            {isMobile && manualZoom <= 1.0 && (
              <div className="absolute bottom-2 left-2 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-white/80 text-[10px] shadow-lg">
                Pinch to zoom • Drag to pan
              </div>
            )}
        </div>
        
        {/* Mobile print buttons - Fixed at bottom (original) */}
        {isMobile && !hideOriginalPreview && (
          <div 
            className={`flex-shrink-0 w-full flex justify-center pb-safe pt-2 ${darkMode ? 'bg-slate-950' : 'bg-slate-100'}`}
          >
            <div 
              className={`flex gap-1 ${mobileCompact ? 'p-1' : 'p-1.5'} rounded-lg transition-all shadow-lg ${darkMode ? 'bg-slate-900/95 border border-slate-700' : 'bg-white/95 border border-slate-200'}`}
              style={{ 
                  width: `${Math.max(280, currentDims.width * previewScale)}px`,
                  maxWidth: '95vw'
              }}
            >
            {/* RNPS Compliance Button */}
            {!shouldHidePro && <button onClick={() => openRNPSModal()} className={`${mobileCompact ? 'w-8 h-8' : 'w-10 h-10'} bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center shadow-md transition-all`} title="RNPS Compliance Records"><Shield className={`${mobileCompact ? 'w-4 h-4' : 'w-5 h-5'}`}/></button>}
            {printSettings.dualMode ? (
              <>
                <button onClick={() => handlePrintAction('WHITE')} className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}><Printer className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />White</button>
                <button onClick={() => handlePrintAction('YELLOW')} className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-yellow-900/20 border-yellow-700 text-yellow-500 hover:bg-yellow-900/30' : 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100'}`}><Printer className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />Yellow</button>
                <button onClick={() => handlePrintAction('PAIR')} className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-blue-900/20 border-blue-700 text-blue-400 hover:bg-blue-900/30' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}><Layers className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />Pair</button>
              </>
            ) : (
              <>
                {/* Print x1 with split button for profile selection */}
                <div className="flex-1 flex">
                  <button onClick={() => handlePrintAction('SINGLE')} className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-l-lg font-semibold ${mobileCompact ? 'text-[10px]' : 'text-xs'} flex items-center justify-center gap-1 transition-all border-y border-l ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}><Printer className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />x1</button>
                  <button onClick={() => { setPendingPrintAction('SINGLE'); setShowPrinterProfileSelect(true); }} className={`${mobileCompact ? 'w-6 h-8' : 'w-7 h-10'} rounded-r-lg flex items-center justify-center transition-all border ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-400 border-slate-600' : 'bg-slate-200 hover:bg-slate-300 text-slate-500 border-slate-300'}`} title="Select printer profile"><ChevronDown className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} /></button>
                </div>
                <button onClick={() => handlePrintAction('DOUBLE')} className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-semibold ${mobileCompact ? 'text-[10px]' : 'text-xs'} flex items-center justify-center gap-1 transition-all border ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}><Layers className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />x2</button>
              </>
            )}
            {!shouldHidePro && pdfExportSettings.showButton && (
              <button onClick={handleExport} disabled={exporting} className={`${mobileCompact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center transition-all ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`} title="Export PDF">{exporting ? <Loader2 className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'} animate-spin`} /> : <Download className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />}</button>
            )}
            </div>
          </div>
        )}
        
        {/* Zoom buttons - hidden on mobile */}
        <div 
            className={`hidden md:flex absolute bottom-8 right-8 gap-2 backdrop-blur border shadow-lg rounded-full p-2 ${darkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-white/90 border-slate-200'}`}
            onDoubleClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <button onClick={() => setManualZoom(z => Math.max(0.3, z - 0.1))} className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ZoomOut className="w-5 h-5"/></button>
            <div className={`w-12 flex items-center justify-center text-xs font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{Math.round(manualZoom * 100)}%</div>
            <button onClick={() => setManualZoom(z => Math.min(5, z + 0.1))} className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ZoomIn className="w-5 h-5"/></button>
            <button onClick={() => { setManualZoom(1.0); setPanPosition({ x: 0, y: 0 }); setIsSuperZoom(false); }} className={`p-2 rounded-full border-l ml-1 ${darkMode ? 'hover:bg-slate-800 text-slate-500 border-slate-700' : 'hover:bg-slate-100 text-slate-400 border-slate-200'}`} title="Reset zoom"><RefreshCw className="w-4 h-4"/></button>
        </div>
        {/* Zoom hint - hidden on mobile */}
        {manualZoom === 1 && (
          <div className={`hidden md:block absolute bottom-8 left-8 text-[10px] px-3 py-1 rounded-full backdrop-blur ${darkMode ? 'bg-slate-900/70 text-slate-500' : 'bg-white/70 text-slate-400'}`}>
            Scroll to zoom • Double-click to super zoom
          </div>
        )}


        {/* NEW: Floating Plate Preview for Mobile - Above print buttons */}
        {isMobile && hideOriginalPreview && (
          <>
            {/* Single floating plate preview.
                key=registration forces full remount on text change → fixes iOS GPU tile split-text bug. */}
            <PlateView
              key={`${config.registration}|${config.textEffect}|${config.textColor}|${config.mainFontFamily}|${config.border}|${config.badge?.enabled}|${config.flagLayout}`}
              id="plate-preview-floating"
              config={{...config, type: PlateType.REAR}}
              dimensions={currentDims}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              position="fixed"
              bottom={`${keyboardHeight + (mobileCompact ? 32 : 40)}px`}
              left="50%"
              transform={`translate(-50%, -14px) scale(${previewScale * manualZoom}) translate(${panPosition.x / Math.max(0.1, previewScale * manualZoom)}px, ${panPosition.y / Math.max(0.1, previewScale * manualZoom)}px)`}
              transformOrigin="center bottom"
              transition={isPanning ? 'none' : 'transform 0.15s ease-out'}
              touchAction="none"
              zIndex={99}
              pointerEvents="auto"
            />

            {/* Transparent overlay input — sits over the plate so a tap directly focuses it.
                iOS opens the keyboard on a real user tap of an <input>, no programmatic .focus() needed. */}
            {mobileTypingMode === 'tap' && !isAnyModalOpen && manualZoom <= 1.0 && (
              <textarea
                ref={hiddenRegInputRef}
                value={config.registration}
                onChange={e => setConfig({...config, registration: config.forceUppercase ? e.target.value.toUpperCase() : e.target.value})}
                onFocus={() => {
                  setShowMobileRegistrationInput(true);
                  const t = hiddenRegInputRef.current;
                  if (t) setTimeout(() => t.setSelectionRange(0, t.value.length), 50);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (e.shiftKey) {
                      // Shift+Enter: allow newline, don't prevent default
                      return;
                    } else if (printSettings.quickPrintEnabled) {
                      // Enter alone: trigger print if enabled
                      e.preventDefault();
                      handlePrintAction('SINGLE');
                    } else {
                      // Enter alone with quickPrintEnabled=false: allow newline
                      return;
                    }
                  }
                }}
                onBlur={() => setShowMobileRegistrationInput(false)}
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                style={{
                  position: 'fixed',
                  bottom: `${keyboardHeight + (mobileCompact ? 32 : 40)}px`,
                  left: '50%',
                  transform: `translate(-50%, -14px) scale(${previewScale})`,
                  transformOrigin: 'center bottom',
                  width: `${currentDims.width}px`,
                  height: `${currentDims.height}px`,
                  opacity: 0,
                  zIndex: 101,
                  fontSize: '16px',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  caretColor: 'transparent',
                  resize: 'none',
                  overflow: 'hidden'
                }}
              />
            )}

            {/* Typing indicator */}
            {showMobileRegistrationInput && !isAnyModalOpen && (
              <div style={{
                position: 'fixed',
                bottom: `${keyboardHeight + (mobileCompact ? 60 : 70)}px`,
                left: '50%',
                transform: 'translate(-50%, -100%)',
                zIndex: 90,
                pointerEvents: 'none',
                marginBottom: '10px'
              }}>
                <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-1">
                  <span className="text-white font-mono text-sm">Typing...</span>
                  <span className="text-blue-400 font-bold text-xl cursor-blink">|</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* NEW: Floating Print Button Bar for Mobile - Full screen width, stuck to bottom */}
        {isMobile && hideOriginalPreview && (
          <div
            className={`fixed left-0 right-0 z-[100] flex gap-1.5 shadow-2xl px-2 py-1.5 ${darkMode ? 'bg-slate-900/95 border-t border-slate-700 backdrop-blur-md' : 'bg-white/95 border-t border-slate-200 backdrop-blur-md'}`}
            style={{
              bottom: `${keyboardHeight}px`,
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {/* RNPS Compliance Button */}
            {!shouldHidePro && <button
              onClick={() => openRNPSModal()}
              className={`${mobileCompact ? 'w-8 h-8' : 'w-10 h-10'} bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center shadow-md transition-all`}
              title="RNPS Compliance Records"
            >
              <Shield className={`${mobileCompact ? 'w-4 h-4' : 'w-5 h-5'}`}/>
            </button>}
            {printSettings.dualMode ? (
              <>
                <button 
                  onClick={() => handlePrintAction('WHITE')} 
                  className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                >
                  <Printer className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
                  <span>White</span>
                </button>
                <button 
                  onClick={() => handlePrintAction('YELLOW')} 
                  className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-yellow-900/20 border-yellow-700 text-yellow-500 hover:bg-yellow-900/30' : 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100'}`}
                >
                  <Printer className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
                  <span>Yellow</span>
                </button>
                <button 
                  onClick={() => handlePrintAction('PAIR')} 
                  className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-bold ${mobileCompact ? 'text-[9px]' : 'text-[11px]'} flex flex-col items-center justify-center gap-0 transition-all border ${darkMode ? 'bg-blue-900/20 border-blue-700 text-blue-400 hover:bg-blue-900/30' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}
                >
                  <Layers className={`${mobileCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
                  <span>Pair</span>
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => handlePrintAction('SINGLE')} 
                  className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-semibold ${mobileCompact ? 'text-[10px]' : 'text-xs'} flex items-center justify-center gap-1 transition-all border ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}
                >
                  <Printer className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                  <span>x1</span>
                </button>
                <button 
                  onClick={() => handlePrintAction('DOUBLE')} 
                  className={`flex-1 ${mobileCompact ? 'h-8' : 'h-10'} rounded-lg font-semibold ${mobileCompact ? 'text-[10px]' : 'text-xs'} flex items-center justify-center gap-1 transition-all border ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300'}`}
                >
                  <Layers className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                  <span>x2</span>
                </button>
              </>
            )}
            {!shouldHidePro && pdfExportSettings.showButton && (
              <button
                onClick={handleExport}
                disabled={exporting}
                className={`${mobileCompact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center transition-all ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`}
                title="Export PDF"
              >
                {exporting ? <Loader2 className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'} animate-spin`} /> : <Download className={`${mobileCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />}
              </button>
            )}
          </div>
        )}
      </main>

      {/* Global Click Blocker when Modals are open - Prevents clicking through to plate preview */}
      {isAnyModalOpen && (
        <div 
          className="fixed inset-0 z-[90]"
          style={{ touchAction: 'none' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
      </div>
    </div>
  );
};

export default App;
